/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import * as d3 from "d3";
import Landing from "./Landing";
import LiveSessionPanel from "./liveSession/LiveSessionPanel";
import CoachTerminal from "./coach/CoachTerminal";
import { useAuth } from "./auth/useAuth";
import AccountMenu from "./auth/AccountMenu";

// ═══════════════════════════════════════════════════════════════
//  PCG TRADING PLATFORM v1 + NESTED CYCLE STORE
//  Cycles persist across timeframe switches via localStorage
//  Levels: Global (1H) / Macro (5m) / Micro (1m) / Nano (15s)
// ═══════════════════════════════════════════════════════════════

// ── CYCLE STORE ────────────────────────────────────────────────
const CYCLE_STEPS = ["SC","AR","ST","B1","B2","B3","C","DSOW","LPS","DSOS","BU"];

const CYCLE_LEVELS = [
  { key:"global", label:"Global", tfHint:"1H/4H", col:"#f5a623", thickness:2.5, opacity:0.90, dotR:5 },
  { key:"macro",  label:"Macro",  tfHint:"15m/5m",col:"#00d4ff", thickness:1.8, opacity:0.75, dotR:4 },
  { key:"micro",  label:"Micro",  tfHint:"1m",    col:"#00e5a0", thickness:1.2, opacity:0.62, dotR:3.5 },
  { key:"nano",   label:"Nano",   tfHint:"15s",   col:"#c084fc", thickness:0.8, opacity:0.50, dotR:3 },
];

const LEVEL_MAP = Object.fromEntries(CYCLE_LEVELS.map(l=>[l.key,l]));

const STEP_COLORS = {
  SC:"#ff40ff", AR:"#00bcd4", ST:"#4488ff",
  B1:"#f5a623", B2:"#f5a623", B3:"#f5a623",
  C:"#ff40ff",  DSOW:"#00cc88", LPS:"#00e090",
  DSOS:"#80ff40", BU:"#00d4ff",
};

const STEP_ABOVE = {
  SC:true,  AR:false, ST:true,  B1:false, B2:true,  B3:false,
  C:true,   DSOW:false, LPS:true, DSOS:false, BU:true,
};

const STORAGE_KEY = "pcg_cycles_v1";

function loadCycles() {
  try {
    const cycles = JSON.parse(localStorage.getItem(STORAGE_KEY)||"[]")||[];
    // Migreer oude cycles zonder cycleMode
    return cycles.map(c => c.cycleMode ? c : {...c, cycleMode: "acc"});
  }
  catch { return []; }
}
function saveCycles(cycles) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cycles)); } catch {}
}
function makeId() {
  return Date.now().toString(36)+Math.random().toString(36).slice(2,6);
}

function useCycleStore() {
  const [cycles, setCyclesRaw] = useState(()=>loadCycles());
  // Gezet door LiveSessionPanel zodra er een live-sessie-verbinding actief is.
  // Elke lokale wijziging roept deze aan zodat 'ie naar de room gebroadcast wordt.
  const broadcastRef = useRef(null);

  const setCycles = useCallback((fn,opts={})=>{
    setCyclesRaw(prev=>{
      const next = typeof fn==="function" ? fn(prev) : fn;
      saveCycles(next);
      if(!opts.remote && broadcastRef.current) broadcastRef.current(next);
      return next;
    });
  },[]);

  // Wordt aangeroepen door useLiveSession wanneer er een cycles-update
  // binnenkomt van een andere deelnemer — overschrijft lokaal zonder opnieuw
  // te broadcasten (voorkomt een echo-lus).
  const applyRemoteCycles = useCallback(remoteCycles=>{
    setCycles(()=>remoteCycles, {remote:true});
  },[setCycles]);

  const addCycle = useCallback((level="micro", tf="1m", cycleMode="acc")=>{
    const lvl = LEVEL_MAP[level]||LEVEL_MAP.micro;
    const id  = makeId();
    const now = new Date();
    const hh  = String(now.getHours()).padStart(2,"0");
    const mm  = String(now.getMinutes()).padStart(2,"0");
    setCycles(prev=>[...prev,{
      id, level,
      label:`${lvl.label} ${hh}:${mm}`,
      color: lvl.col,
      tf, points:[], createdAt:Date.now(),
      cycleMode,
    }]);
    return id;
  },[setCycles]);

  const setCyclePoint = useCallback((id,step,price,barIdx,time,tf)=>{
    setCycles(prev=>prev.map(c=>{
      if(c.id!==id) return c;
      const pts = c.points.filter(p=>p.step!==step);
      return {...c, points:[...pts,{step,price,barIdx,time,tf}]};
    }));
  },[setCycles]);

  const removeCyclePoint = useCallback((id,step)=>{
    setCycles(prev=>prev.map(c=>
      c.id!==id ? c : {...c, points:c.points.filter(p=>p.step!==step)}
    ));
  },[setCycles]);

  const deleteCycle = useCallback((id)=>{
    setCycles(prev=>prev.filter(c=>c.id!==id));
  },[setCycles]);

  const updateCycleMeta = useCallback((id,meta)=>{
    setCycles(prev=>prev.map(c=>c.id===id?{...c,...meta}:c));
  },[setCycles]);

  const clearAllCycles = useCallback(()=>setCycles([]),[setCycles]);

  return {
    cycles, addCycle, setCyclePoint, removeCyclePoint,
    deleteCycle, updateCycleMeta, clearAllCycles,
    broadcastRef, applyRemoteCycles,
  };
}

// ── CycleManagerPanel ──────────────────────────────────────────
function CycleManagerPanel({ store, activeCycleId, setActiveCycleId, manualStep, setManualStep, currentTf, cycleType, isDistMode }) {
  const { cycles, addCycle, deleteCycle, updateCycleMeta, removeCyclePoint, clearAllCycles } = store;
  const activeCycle = cycles.find(c=>c.id===activeCycleId)||null;

  return (
    <div style={{fontFamily:"'Courier New',monospace",fontSize:11}}>
      {/* Header + Add buttons */}
      <div style={{display:"flex",gap:3,alignItems:"center",padding:"5px 10px",
        borderBottom:"1px solid #1a2535",background:"rgba(0,212,255,0.05)"}}>
        <span style={{fontSize:9,fontWeight:900,color:"#00d4ff",letterSpacing:"0.12em",flex:1}}>CYCLES</span>
        {CYCLE_LEVELS.map(lvl=>(
          <button key={lvl.key}
            onClick={()=>{
              const id=addCycle(lvl.key,currentTf,cycleType||"acc");
              setActiveCycleId(id);
              setManualStep("SC");
            }}
            title={`Nieuw ${lvl.label} cycle (${lvl.tfHint})`}
            style={{padding:"2px 5px",borderRadius:2,cursor:"pointer",fontFamily:"monospace",
              fontSize:8,fontWeight:900,border:`1px solid ${lvl.col}44`,
              background:`${lvl.col}11`,color:lvl.col}}>
            +{lvl.label[0]}
          </button>
        ))}
        {cycles.length>0&&(
          <button onClick={()=>{
            if(window.confirm("Alle cycles verwijderen?")) {
              clearAllCycles(); setActiveCycleId(null); setManualStep(null);
            }
          }} style={{padding:"2px 4px",borderRadius:2,cursor:"pointer",fontFamily:"monospace",
            fontSize:8,border:"1px solid #1a2535",background:"transparent",color:"#ff3f50"}}>
            ✕all
          </button>
        )}
      </div>

      {/* Cycle list */}
      {cycles.length===0 ? (
        <div style={{padding:"8px 10px",fontSize:10,color:"#8aabb8",lineHeight:1.6}}>
          Geen cycles. Voeg toe:<br/>
          <span style={{color:"#f5a623"}}>+G</span> Global ·{" "}
          <span style={{color:"#00d4ff"}}>+M</span> Macro ·{" "}
          <span style={{color:"#00e5a0"}}>+μ</span> Micro ·{" "}
          <span style={{color:"#c084fc"}}>+N</span> Nano
        </div>
      ) : (
        <div style={{maxHeight:130,overflowY:"auto"}}>
          {cycles.map(c=>{
            const lvl = LEVEL_MAP[c.level]||CYCLE_LEVELS[2];
            const isActive = c.id===activeCycleId;
            const lastStep = [...c.points]
              .sort((a,b)=>CYCLE_STEPS.indexOf(b.step)-CYCLE_STEPS.indexOf(a.step))[0]?.step||"—";
            return (
              <div key={c.id} onClick={()=>setActiveCycleId(isActive?null:c.id)}
                style={{display:"flex",alignItems:"center",gap:4,padding:"3px 10px",
                  cursor:"pointer",borderBottom:"1px solid #1a2535",
                  background:isActive?`${c.color}0f`:"transparent",
                  borderLeft:isActive?`3px solid ${c.color}`:"3px solid transparent"}}>
                <span style={{fontSize:7,fontWeight:900,padding:"1px 4px",borderRadius:2,
                  background:`${lvl.col}22`,color:lvl.col,flexShrink:0}}>
                  {lvl.label[0]}
                </span>
                <input value={c.label}
                  onChange={e=>{e.stopPropagation();updateCycleMeta(c.id,{label:e.target.value});}}
                  onClick={e=>e.stopPropagation()}
                  style={{flex:1,background:"transparent",border:"none",
                    color:isActive?c.color:"#8aabb8",fontFamily:"monospace",
                    fontSize:9,outline:"none",cursor:"text",minWidth:0}}/>
                <span style={{fontSize:7,color:"#8aabb8",flexShrink:0}}>{c.points.length}pt</span>
                <span style={{fontSize:7,color:STEP_COLORS[lastStep]||"#8aabb8",
                  flexShrink:0,fontWeight:700}}>{lastStep}</span>
                <span style={{fontSize:7,color:"#8aabb8",flexShrink:0}}>{c.tf}</span>
                <span onClick={e=>{e.stopPropagation();deleteCycle(c.id);
                  if(isActive){setActiveCycleId(null);setManualStep(null);}}}
                  style={{color:"#ff3f50",cursor:"pointer",fontSize:9,flexShrink:0}}>✕</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Active cycle editor */}
      {activeCycle&&(
        <div style={{padding:"6px 10px",borderTop:"1px solid #1a2535"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
            <span style={{fontSize:9,color:activeCycle.color,fontWeight:700,letterSpacing:"0.08em"}}>
              {activeCycle.label}
            </span>
            <span style={{fontSize:7,color:"#8aabb8",padding:"1px 4px",borderRadius:2,
              border:"1px solid #1a2535"}}>
              {LEVEL_MAP[activeCycle.level]?.tfHint||activeCycle.tf}
            </span>
          </div>

          {[["SC","AR","ST"],["B1","B2","B3"],["C","DSOW","LPS"],["DSOS","BU"]].map((row,ri)=>(
            <div key={ri} style={{display:"flex",gap:2,marginBottom:2}}>
              {row.map(step=>{
                const done   = activeCycle.points.find(p=>p.step===step);
                const active = manualStep===step;
                return (
                  <button key={step} onClick={()=>setManualStep(active?null:step)}
                    style={{flex:1,padding:"3px 0",borderRadius:2,cursor:"pointer",
                      fontFamily:"monospace",fontSize:8,fontWeight:700,
                      border:`1px solid ${active?activeCycle.color:done?STEP_COLORS[step]:"rgba(255,255,255,0.1)"}`,
                      background:active?`${activeCycle.color}30`:done?`${STEP_COLORS[step]}18`:"rgba(255,255,255,0.03)",
                      color:active?activeCycle.color:done?STEP_COLORS[step]:"#8aabb8"}}>
                    {done?"✓":""}{step==="SC"?(isDistMode?"BC":"SC"):step}
                  </button>
                );
              })}
            </div>
          ))}

          {manualStep&&(
            <div style={{padding:"3px 6px",borderRadius:2,fontSize:8,marginTop:4,
              background:`${activeCycle.color}15`,border:`1px solid ${activeCycle.color}88`,
              color:activeCycle.color,textAlign:"center"}}>
              ⊕ Klik chart → zet {manualStep}
            </div>
          )}

          {activeCycle.points.length>0&&(
            <div style={{marginTop:5,background:"rgba(0,0,0,0.25)",borderRadius:2,padding:"3px 5px"}}>
              {[...activeCycle.points]
                .sort((a,b)=>CYCLE_STEPS.indexOf(a.step)-CYCLE_STEPS.indexOf(b.step))
                .map((pt,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",fontSize:7.5,
                    borderBottom:"1px solid rgba(255,255,255,0.04)",padding:"1px 0"}}>
                    <span style={{color:STEP_COLORS[pt.step]||"#f5a623",fontWeight:700,width:32}}>{pt.step}</span>
                    <span style={{fontFamily:"monospace",color:"#e8f0f8",flex:1,textAlign:"right"}}>{pt.price.toFixed(2)}</span>
                    <span style={{color:"#8aabb8",width:32,textAlign:"right"}}>{pt.time}</span>
                    <span style={{color:"#8aabb8",width:22,textAlign:"right",fontSize:7}}>{pt.tf}</span>
                    <span onClick={()=>removeCyclePoint(activeCycle.id,pt.step)}
                      style={{color:"#ff3f50",cursor:"pointer",marginLeft:4,fontSize:9}}>✕</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── renderCyclesOnChart ────────────────────────────────────────
function renderCyclesOnChart({ cycles, xS, yS, iW, bars, cx, L }) {
  if (!cycles||!cycles.length||!L.badge) return;

  cycles.forEach(cycle=>{
    if (!cycle.points||!cycle.points.length) return;
    const lvl       = LEVEL_MAP[cycle.level]||LEVEL_MAP.micro;
    const col       = cycle.color||lvl.col;
    const thickness = lvl.thickness;
    const opacity   = lvl.opacity;
    const dotR      = lvl.dotR;
    const barsLen   = bars?bars.length:0;
    const lvlPrefix = lvl.label[0].toLowerCase();

    const pts = [...cycle.points].sort(
      (a,b)=>CYCLE_STEPS.indexOf(a.step)-CYCLE_STEPS.indexOf(b.step)
    );

    const scPt = pts.find(p=>p.step==="SC");
    const arPt = pts.find(p=>p.step==="AR");

    if (scPt&&arPt) {
      const scP  = scPt.price;
      const arP  = arPt.price;
      const mcpP = Math.round(((scP+arP)/2)*4)/4;
      const yTop = Math.min(yS(scP),yS(arP));
      const yBot = Math.max(yS(scP),yS(arP));

      L.levels.insert("rect","line")
        .attr("x",0).attr("y",yTop).attr("width",iW)
        .attr("height",Math.max(0,yBot-yTop)).attr("fill",`${col}04`);

      L.levels.append("line").attr("x1",0).attr("x2",iW)
        .attr("y1",yS(scP)).attr("y2",yS(scP))
        .attr("stroke",(cycle.cycleMode||"acc")==="dist"?"#ff6060":"#ff40ff").attr("stroke-width",thickness*0.8)
        .attr("stroke-dasharray","6,4").attr("opacity",opacity*0.85);
      L.levels.append("line").attr("x1",0).attr("x2",iW)
        .attr("y1",yS(arP)).attr("y2",yS(arP))
        .attr("stroke","#00bcd4").attr("stroke-width",thickness*0.8)
        .attr("stroke-dasharray","6,4").attr("opacity",opacity*0.85);
      L.levels.append("line").attr("x1",0).attr("x2",iW)
        .attr("y1",yS(mcpP)).attr("y2",yS(mcpP))
        .attr("stroke","rgba(210,220,230,0.7)").attr("stroke-width",thickness*0.5)
        .attr("stroke-dasharray","3,4").attr("opacity",opacity*0.7);

      L.rlbls.append("text").attr("x",iW-68).attr("y",yS(scP)+4)
        .attr("fill",(cycle.cycleMode||"acc")==="dist"?"#ff6060":"#ff40ff").attr("font-size",7).attr("font-family","monospace")
        .attr("font-weight","bold").attr("opacity",opacity)
        .text(`${lvlPrefix}${(cycle.cycleMode||"acc")==="dist"?"BC":"SC"} ${scP.toFixed(2)}`);
      L.rlbls.append("text").attr("x",iW-68).attr("y",yS(arP)+4)
        .attr("fill","#00bcd4").attr("font-size",7).attr("font-family","monospace")
        .attr("font-weight","bold").attr("opacity",opacity)
        .text(`${lvlPrefix}AR ${arP.toFixed(2)}`);
      L.rlbls.append("text").attr("x",iW-68).attr("y",yS(mcpP)+4)
        .attr("fill","rgba(200,210,220,0.8)").attr("font-size",7).attr("font-family","monospace")
        .attr("opacity",opacity*0.8).text(`${lvlPrefix}MCP ${mcpP.toFixed(2)}`);
    }

    const cPt = pts.find(p=>p.step==="C");
    if (cPt) {
      L.levels.append("line").attr("x1",0).attr("x2",iW)
        .attr("y1",yS(cPt.price)).attr("y2",yS(cPt.price))
        .attr("stroke","#ff40ff").attr("stroke-width",thickness*0.6)
        .attr("stroke-dasharray","2,3").attr("opacity",opacity*0.55);
      L.rlbls.append("text").attr("x",iW-68).attr("y",yS(cPt.price)+4)
        .attr("fill","#ff40ff").attr("font-size",7).attr("font-family","monospace")
        .attr("opacity",opacity*0.6).text(`${lvlPrefix}C ${cPt.price.toFixed(2)}`);
    }

    const visPts = pts.filter(p=>p.barIdx!==undefined&&p.barIdx!==null&&p.barIdx<barsLen);

    for(let i=1;i<visPts.length;i++){
      const pa=visPts[i-1], pb=visPts[i];
      const xa=cx(pa.barIdx,xS), ya=yS(pa.price);
      const xb=cx(pb.barIdx,xS), yb=yS(pb.price);
      if(xa<-50&&xb<-50) continue;
      if(xa>iW+50&&xb>iW+50) continue;
      L.badge.append("line").attr("class","pcg-lbl")
        .attr("x1",xa).attr("y1",ya).attr("x2",xb).attr("y2",yb)
        .attr("stroke",col).attr("stroke-width",thickness*0.6)
        .attr("stroke-dasharray","4,3").attr("opacity",opacity*0.5);
    }

    // Label above for low-priced points, below for high-priced points
    const _allPrices = visPts.map(p=>p.price);
    const _median = _allPrices.length ? _allPrices.reduce((a,b)=>a+b,0)/_allPrices.length : 0;

    visPts.forEach(pt=>{
      const x = cx(pt.barIdx,xS);
      if(x<-50||x>iW+50) return;
      const y     = yS(pt.price);
      const stepC = STEP_COLORS[pt.step]||col;
      const above = pt.price > _median;

      L.badge.append("circle").attr("class","pcg-lbl")
        .attr("cx",x).attr("cy",y).attr("r",dotR)
        .attr("fill",stepC).attr("stroke","#000").attr("stroke-width",0.5)
        .attr("opacity",opacity);

      const tw = pt.step.length*5.5+8;
      const yOff = above?-16:5;
      const yTxt = above?-7:14;
      L.badge.append("rect").attr("class","pcg-lbl")
        .attr("x",x-tw/2).attr("y",y+yOff).attr("width",tw).attr("height",11).attr("rx",2)
        .attr("fill","rgba(0,0,0,0.85)").attr("opacity",opacity);
      L.badge.append("text").attr("class","pcg-lbl")
        .attr("x",x).attr("y",y+yTxt).attr("text-anchor","middle")
        .attr("fill",stepC).attr("font-size",Math.max(7,Math.round(thickness*3+5)))
        .attr("font-weight","bold").attr("font-family","monospace").attr("opacity",opacity)
        .text(pt.step);
    });

    // Price-only points (no barIdx)
    pts.filter(p=>p.barIdx===undefined||p.barIdx===null).forEach(pt=>{
      const stepC = STEP_COLORS[pt.step]||col;
      L.levels.append("line").attr("x1",0).attr("x2",iW)
        .attr("y1",yS(pt.price)).attr("y2",yS(pt.price))
        .attr("stroke",stepC).attr("stroke-width",thickness*0.5)
        .attr("stroke-dasharray","2,5").attr("opacity",opacity*0.45);
      L.rlbls.append("text").attr("x",iW-68).attr("y",yS(pt.price)+4)
        .attr("fill",stepC).attr("font-size",7).attr("font-family","monospace")
        .attr("opacity",opacity*0.55)
        .text(`${lvlPrefix}${pt.step} ${pt.price.toFixed(2)}`);
    });
  });
}

// ══════════════════════════════════════════════════════════════
//  PANDAS ZONE INTEGRATION
//  Hook + renderer voor Supply/Demand/HVN zones uit zones_api.py
// ══════════════════════════════════════════════════════════════

const ZONES_API = "/api/zones";
const ZONES_POLL_MS = 60_000; // herbereken elke 60s (1m candle cadentie)
const AGENT_LEVELS_POLL_MS = 30_000; // agent session_levels.json elke 30s

/**
 * useAgentLevels — fetcht session_levels.json die agent.py schrijft.
 * Toont SC, AR, MCP, entry zone, SL, TP1 op de chart.
 */
function useAgentLevels(enabled = true) {
  const [levels, setLevels] = useState(null);

  const fetchLevels = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await fetch('/session_levels.json?' + Date.now(), {
        signal: AbortSignal.timeout(5000),
      });
      if (!r.ok) return;
      const data = await r.json();
      setLevels(data);
    } catch (e) {
      // silent fail — agent misschien niet actief
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    fetchLevels();
    const id = setInterval(fetchLevels, AGENT_LEVELS_POLL_MS);
    return () => clearInterval(id);
  }, [fetchLevels, enabled]);

  return levels;
}

/**
 * renderAgentLevelsOnChart — tekent agent SC/AR/MCP/entry zone/SL/TP op de D3 chart.
 */
function renderAgentLevelsOnChart({ levels, yS, iW, L }) {
  if (!levels || !L.levels || !L.rlbls) return;

  const sess = levels.london || levels.newyork || levels.NewYork_Sessie || levels.London_Sessie;
  if (!sess) return;

  const draw = (price, color, label, dash, lw, opacity) => {
    if (!price || price <= 0) return;
    const y = yS(price);
    L.levels.append('line')
      .attr('x1', 0).attr('x2', iW).attr('y1', y).attr('y2', y)
      .attr('stroke', color).attr('stroke-width', lw)
      .attr('stroke-dasharray', dash).attr('opacity', opacity);
    L.rlbls.append('text')
      .attr('x', iW - 68).attr('y', y + 4)
      .attr('fill', color).attr('font-size', 8)
      .attr('font-family', 'monospace').attr('font-weight', 'bold')
      .attr('opacity', opacity)
      .text(label);
  };

  const drawZone = (lo, hi, color, alpha) => {
    if (!lo || !hi || lo <= 0 || hi <= 0) return;
    const yTop = Math.min(yS(lo), yS(hi));
    const yBot = Math.max(yS(lo), yS(hi));
    const h = Math.max(2, yBot - yTop);
    L.levels.append('rect')
      .attr('x', 0).attr('y', yTop).attr('width', iW).attr('height', h)
      .attr('fill', `rgba(${color},${alpha})`).attr('pointer-events', 'none');
  };

  // SC — rood/roze
  draw(sess.mcp,          '#c084fc', `AGENT MCP ${sess.mcp?.toFixed(2)}`,        '4,3', 2.0, 0.95);
  draw(sess.invalidation, '#ff3f50', `SL ${sess.invalidation?.toFixed(2)}`,        '8,4', 1.4, 0.85);
  draw(sess.tp1,          '#00e5a0', `TP1 ${sess.tp1?.toFixed(2)}`,               '6,3', 1.4, 0.85);

  // Entry zone — blauw gevuld
  if (sess.entry_zone_low && sess.entry_zone_high) {
    drawZone(sess.entry_zone_low, sess.entry_zone_high, '68,136,255', 0.18);
    draw(sess.entry_zone_high, '#4488ff', `ENTRY ${sess.entry_zone_low?.toFixed(2)}–${sess.entry_zone_high?.toFixed(2)}`, '5,3', 1.2, 0.90);
    draw(sess.entry_zone_low,  '#4488ff', '', '5,3', 0.8, 0.60);
  }

  // Support / Resistance
  draw(sess.support,    'rgba(0,229,160,0.8)',  `S ${sess.support?.toFixed(2)}`,    '4,4', 1.0, 0.70);
  draw(sess.resistance, 'rgba(255,63,80,0.8)',  `R ${sess.resistance?.toFixed(2)}`, '4,4', 1.0, 0.70);
}

/**
 * usePandasZones — fetcht zone data van zones_api.py
 * Retourneert { zones, loading, error, lastUpdate, refresh }
 */
function usePandasZones(coin = "SP500", enabled = true) {
  const [zones,      setZones]      = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchZones = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${ZONES_API}?coin=${encodeURIComponent(coin)}`, {
        signal: AbortSignal.timeout(8000),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setZones(data);
      setLastUpdate(new Date());
    } catch (e) {
      // Zones API niet beschikbaar → silent fail (chart blijft werken zonder)
      setError(e.message);
      console.warn("[PandasZones] API niet bereikbaar:", e.message);
    } finally {
      setLoading(false);
    }
  }, [coin, enabled]);

  // Initial fetch + polling
  useEffect(() => {
    if (!enabled) return;
    fetchZones();
    const id = setInterval(fetchZones, ZONES_POLL_MS);
    return () => clearInterval(id);
  }, [fetchZones, enabled]);

  return { zones, loading, error, lastUpdate, refresh: fetchZones };
}

// ── MARKET STATE AGENT INTEGRATION ─────────────────────────────
const MARKET_STATE_POLL_MS = 30_000;

function useMarketState(enabled = true) {
  const [marketState, setMarketState] = useState(null);
  const [msError, setMsError] = useState(null);
  const [msLastUpdate, setMsLastUpdate] = useState(null);

  const fetchMarketState = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await fetch('/api/market_state?' + Date.now(), {
        signal: AbortSignal.timeout(5000),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setMarketState(data);
      setMsLastUpdate(new Date());
      setMsError(null);
    } catch (e) {
      setMsError(e.message);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    fetchMarketState();
    const id = setInterval(fetchMarketState, MARKET_STATE_POLL_MS);
    return () => clearInterval(id);
  }, [fetchMarketState, enabled]);

  return { marketState, msError, msLastUpdate, refreshMs: fetchMarketState };
}

/**
 * renderPandasZonesOnChart — tekent Supply/Demand zones + HVN nodes op de D3 chart.
 *
 * Zone types:
 *   - Supply (rood) : gevulde rect + bottom/top/midden label rechts
 *   - Demand (groen): gevulde rect + bottom/top/midden label rechts
 *   - HVN (geel)    : stippellijn + volume label
 *   - micro SC/AR/MCP: dashed horizontalen (als geen manual cycle aanwezig)
 *
 * Wordt aangeroepen vanuit de redraw loop — ALTIJD na renderCyclesOnChart.
 */
function renderPandasZonesOnChart({ zones, yS, iW, L }) {
  if (!zones || !L.levels || !L.rlbls) return;

  const SUPPLY_COL  = "rgba(255,63,80,0.18)";
  const SUPPLY_LINE = "#ff3f50";
  const DEMAND_COL  = "rgba(0,229,160,0.14)";
  const DEMAND_LINE = "#00e5a0";
  const HVN_COL     = "rgba(255,208,96,0.7)";
  const MCP_COL     = "rgba(192,132,252,0.9)";

  // ── Supply zones (rood) ────────────────────────────────────────
  (zones.supply || []).forEach((z, i) => {
    const yTop = Math.min(yS(z.top), yS(z.bottom));
    const yBot = Math.max(yS(z.top), yS(z.bottom));
    const h    = Math.max(1, yBot - yTop);
    const is1m = z.timeframe === "1m";
    const alpha = is1m ? 0.22 : 0.12;
    const lw    = is1m ? 1.2  : 0.7;

    // Gevuld rechthoek
    L.levels.append("rect")
      .attr("x", 0).attr("y", yTop).attr("width", iW).attr("height", h)
      .attr("fill", `rgba(255,63,80,${alpha})`).attr("pointer-events", "none");

    // Zone grenzen
    [z.top, z.bottom].forEach((p, pi) => {
      L.levels.append("line")
        .attr("x1", 0).attr("x2", iW).attr("y1", yS(p)).attr("y2", yS(p))
        .attr("stroke", SUPPLY_LINE).attr("stroke-width", pi === 0 ? lw : lw * 0.6)
        .attr("stroke-dasharray", pi === 0 ? "6,3" : "3,5")
        .attr("opacity", is1m ? 0.80 : 0.45);
    });

    // Midlijn
    L.levels.append("line")
      .attr("x1", 0).attr("x2", iW).attr("y1", yS(z.midden)).attr("y2", yS(z.midden))
      .attr("stroke", SUPPLY_LINE).attr("stroke-width", 0.5)
      .attr("stroke-dasharray", "1,4").attr("opacity", 0.35);

    // Label rechts
    const tf = is1m ? "1m" : "15m";
    const volStr = z.vol_ratio.toFixed(1);
    L.rlbls.append("text")
      .attr("x", iW - 68).attr("y", yS(z.top) + 4)
      .attr("fill", SUPPLY_LINE).attr("font-size", is1m ? 7 : 6)
      .attr("font-family", "monospace").attr("font-weight", is1m ? "bold" : "normal")
      .attr("opacity", is1m ? 0.9 : 0.6)
      .text(`SZ${i + 1}(${tf}) ${z.top.toFixed(2)}`);
    L.rlbls.append("text")
      .attr("x", iW - 68).attr("y", yS(z.bottom) + 4)
      .attr("fill", SUPPLY_LINE).attr("font-size", 6)
      .attr("font-family", "monospace").attr("opacity", is1m ? 0.7 : 0.45)
      .text(`${z.bottom.toFixed(2)} v${volStr}x`);
  });

  // ── Demand zones (groen) ───────────────────────────────────────
  (zones.demand || []).forEach((z, i) => {
    const yTop = Math.min(yS(z.top), yS(z.bottom));
    const yBot = Math.max(yS(z.top), yS(z.bottom));
    const h    = Math.max(1, yBot - yTop);
    const is1m = z.timeframe === "1m";
    const alpha = is1m ? 0.18 : 0.10;
    const lw    = is1m ? 1.2  : 0.7;

    L.levels.append("rect")
      .attr("x", 0).attr("y", yTop).attr("width", iW).attr("height", h)
      .attr("fill", `rgba(0,229,160,${alpha})`).attr("pointer-events", "none");

    [z.top, z.bottom].forEach((p, pi) => {
      L.levels.append("line")
        .attr("x1", 0).attr("x2", iW).attr("y1", yS(p)).attr("y2", yS(p))
        .attr("stroke", DEMAND_LINE).attr("stroke-width", pi === 0 ? lw * 0.6 : lw)
        .attr("stroke-dasharray", pi === 1 ? "6,3" : "3,5")
        .attr("opacity", is1m ? 0.80 : 0.45);
    });

    L.levels.append("line")
      .attr("x1", 0).attr("x2", iW).attr("y1", yS(z.midden)).attr("y2", yS(z.midden))
      .attr("stroke", DEMAND_LINE).attr("stroke-width", 0.5)
      .attr("stroke-dasharray", "1,4").attr("opacity", 0.35);

    const tf = is1m ? "1m" : "15m";
    const volStr = z.vol_ratio.toFixed(1);
    L.rlbls.append("text")
      .attr("x", iW - 68).attr("y", yS(z.bottom) + 4)
      .attr("fill", DEMAND_LINE).attr("font-size", is1m ? 7 : 6)
      .attr("font-family", "monospace").attr("font-weight", is1m ? "bold" : "normal")
      .attr("opacity", is1m ? 0.9 : 0.6)
      .text(`DZ${i + 1}(${tf}) ${z.bottom.toFixed(2)}`);
    L.rlbls.append("text")
      .attr("x", iW - 68).attr("y", yS(z.top) + 4)
      .attr("fill", DEMAND_LINE).attr("font-size", 6)
      .attr("font-family", "monospace").attr("opacity", is1m ? 0.7 : 0.45)
      .text(`${z.top.toFixed(2)} v${volStr}x`);
  });

  // ── High Volume Nodes (geel stippel) ───────────────────────────
  (zones.hvn || []).forEach(node => {
    L.levels.append("line")
      .attr("x1", 0).attr("x2", iW).attr("y1", yS(node.price)).attr("y2", yS(node.price))
      .attr("stroke", HVN_COL).attr("stroke-width", 1.0)
      .attr("stroke-dasharray", "3,6").attr("opacity", 0.65);
    L.rlbls.append("text")
      .attr("x", iW - 68).attr("y", yS(node.price) + 4)
      .attr("fill", HVN_COL).attr("font-size", 7)
      .attr("font-family", "monospace").attr("opacity", 0.7)
      .text(`HVN ${node.price.toFixed(2)}`);
  });

  // ── Pandas MCP micro (paars stippel) — alleen als geen manual cycle MCP ──
  if (zones.micro && zones.micro.mcp > 0) {
    const mcpP = zones.micro.mcp;
    L.levels.append("line")
      .attr("x1", 0).attr("x2", iW).attr("y1", yS(mcpP)).attr("y2", yS(mcpP))
      .attr("stroke", MCP_COL).attr("stroke-width", 1.0)
      .attr("stroke-dasharray", "4,4").attr("opacity", 0.55);
    L.rlbls.append("text")
      .attr("x", iW - 68).attr("y", yS(mcpP) + 4)
      .attr("fill", MCP_COL).attr("font-size", 7)
      .attr("font-family", "monospace").attr("opacity", 0.6)
      .text(`pMCP ${mcpP.toFixed(2)}`);
  }
}

/**
 * renderMarketStateOnChart — tekent Market State Agent levels op de D3 chart.
 *
 * Levels:
 *   MCP  (#00d4ff)  — Market Control Point, solide lijn, dikst
 *   SC   (#c084fc)  — Selling Climax niveau
 *   AR   (#00bcd4)  — Automatic Rally niveau
 *   LFS  (#f5a623)  — Last Failed Swing
 *   RFP  (#00e5a0)  — Rising Fair Price
 *   FFP  (#ff3f50)  — Falling Fair Price
 *   Fase badge rechts boven
 */
function renderMarketStateOnChart({ ms, yS, iW, L }) {
  if (!ms || !L.levels || !L.rlbls) return;

  const [yLo, yHi] = yS.domain ? yS.domain() : [0, 99999];

  const drawLine = (price, color, label, dash, lw, opacity) => {
    if (!price || price <= 0 || price < yLo * 0.85 || price > yHi * 1.15) return;
    const y = yS(price);
    L.levels.append("line")
      .attr("x1", 0).attr("x2", iW).attr("y1", y).attr("y2", y)
      .attr("stroke", color).attr("stroke-width", lw)
      .attr("stroke-dasharray", dash).attr("opacity", opacity);
    if (label) {
      L.rlbls.append("text")
        .attr("x", iW - 72).attr("y", y - 2)
        .attr("fill", color).attr("font-size", 7)
        .attr("font-family", "monospace").attr("font-weight", "bold")
        .attr("opacity", opacity)
        .text(label);
    }
  };

  // MCP — dikste lijn, meest belangrijk
  drawLine(ms.mcp, "#00d4ff", `MS·MCP ${ms.mcp?.toFixed(2)}`, "6,3", 1.8, 0.90);

  // SC / AR
  drawLine(ms.sc,  "#c084fc", `MS·SC  ${ms.sc?.toFixed(2)}`,  "5,4", 1.2, 0.75);
  drawLine(ms.ar,  "#00bcd4", `MS·AR  ${ms.ar?.toFixed(2)}`,  "5,4", 1.2, 0.75);

  // LFS
  drawLine(ms.lfs, "#f5a623", `MS·LFS ${ms.lfs?.toFixed(2)}`, "3,5", 1.0, 0.65);

  // RFP / FFP
  drawLine(ms.rfp > 0 ? ms.rfp : null, "#00e5a0", `RFP ${ms.rfp?.toFixed(2)}`, "2,5", 0.9, 0.55);
  drawLine(ms.ffp > 0 ? ms.ffp : null, "#ff3f50", `FFP ${ms.ffp?.toFixed(2)}`, "2,5", 0.9, 0.55);

  // Fase + Bias badge (rechts boven in chart)
  const FASE_COL = {C:"#c084fc",D:"#00e5a0",E:"#80ff40",B3:"#f5a623",B2:"#f5a623",B1:"#f5a623",B:"#f5a623",A:"#8aabb8",UNKNOWN:"#4a6070"};
  const BIAS_COL = {BULLISH:"#00e5a0",BEARISH:"#ff3f50",DISHARMONY:"#ffd060",NEUTRAL:"#8aabb8"};
  const faseCol = FASE_COL[ms.fase] || "#8aabb8";
  const biasCol = BIAS_COL[ms.bias] || "#8aabb8";
  const badgeX  = iW - 72;
  const badgeY  = 14;

  L.badge.append("rect")
    .attr("class", "pcg-lbl")
    .attr("x", badgeX - 2).attr("y", badgeY - 10)
    .attr("width", 70).attr("height", 14)
    .attr("fill", `${faseCol}22`).attr("rx", 2)
    .attr("stroke", `${faseCol}66`).attr("stroke-width", 0.8);
  L.badge.append("text")
    .attr("class", "pcg-lbl")
    .attr("x", badgeX + 1).attr("y", badgeY)
    .attr("fill", faseCol).attr("font-size", 8)
    .attr("font-family", "monospace").attr("font-weight", "bold")
    .text(`${ms.fase}`);
  L.badge.append("text")
    .attr("class", "pcg-lbl")
    .attr("x", badgeX + 24).attr("y", badgeY)
    .attr("fill", biasCol).attr("font-size", 7)
    .attr("font-family", "monospace")
    .text(`${ms.bias}`);
}

// ── MARKET STATE PANEL COMPONENT ──────────────────────────────
function MarketStatePanel({ marketState, msError, msLastUpdate, refreshMs }) {
  if (!marketState && !msError) {
    return (
      <div style={{fontSize:8,color:"#4a6070",lineHeight:1.6,padding:"4px 0"}}>
        <span style={{color:"#ff3f50"}}>⚠ API offline</span><br/>
        Start: <span style={{color:"#00d4ff",fontFamily:"monospace"}}>uvicorn market_state_api:app --port 8002</span>
      </div>
    );
  }
  if (msError && !marketState) {
    return <div style={{fontSize:8,color:"#ff3f50",padding:"4px 0"}}>⚠ {msError}</div>;
  }
  if (!marketState) return null;

  const { fase, bias, environment, harmony, nano_side, micro_side, trade_ok,
          sc, ar, mcp, atr: msAtr, lfs, rfp, ffp, reden } = marketState;

  const FASE_COL = {'C':'#c084fc','D':'#00e5a0','E':'#80ff40','B3':'#f5a623','B2':'#f5a623','B1':'#f5a623','B':'#f5a623','A':'#8aabb8','UNKNOWN':'#4a6070'};
  const faseCol = FASE_COL[fase] || '#8aabb8';
  const BIAS_COL = {'BULLISH':'#00e5a0','BEARISH':'#ff3f50','DISHARMONY':'#ffd060','NEUTRAL':'#8aabb8'};
  const biasCol = BIAS_COL[bias] || '#8aabb8';
  const ENV_COL = {'ACCUMULATION':'#00e5a0','DISTRIBUTION':'#ff3f50','UNDECIDED':'#8aabb8'};
  const envCol = ENV_COL[environment] || '#8aabb8';
  const sideIcon = s => s==='FRONTSIDE'?'▲F':s==='BACKSIDE'?'▼B':'?';
  const sideCol = s => s==='FRONTSIDE'?'#80ff40':s==='BACKSIDE'?'#ff3f50':'#4a6070';
  const updateTime = msLastUpdate ? msLastUpdate.toLocaleTimeString('nl-NL',{hour:'2-digit',minute:'2-digit',second:'2-digit'}) : '—';

  return (
    <div style={{fontFamily:"'Courier New',monospace",fontSize:10}}>
      <div style={{display:"flex",gap:4,marginBottom:6,alignItems:"center"}}>
        <div style={{padding:"3px 8px",borderRadius:2,fontWeight:900,fontSize:11,background:`${faseCol}18`,border:`1px solid ${faseCol}66`,color:faseCol}}>{fase}</div>
        <div style={{flex:1,padding:"3px 6px",borderRadius:2,fontWeight:700,fontSize:10,background:trade_ok?"rgba(0,229,160,0.12)":"rgba(255,63,80,0.08)",border:`1px solid ${trade_ok?"rgba(0,229,160,0.4)":"rgba(255,63,80,0.25)"}`,color:trade_ok?"#00e5a0":"#ff3f50",textAlign:"center"}}>{trade_ok?"✅ TRADE OK":"⛔ NO TRADE"}</div>
      </div>
      <div style={{display:"flex",gap:3,marginBottom:5}}>
        <div style={{flex:1,padding:"2px 5px",borderRadius:2,fontSize:8,fontWeight:700,background:`${biasCol}10`,border:`1px solid ${biasCol}44`,color:biasCol,textAlign:"center"}}>{bias}</div>
        <div style={{flex:1,padding:"2px 5px",borderRadius:2,fontSize:8,fontWeight:700,background:`${envCol}10`,border:`1px solid ${envCol}44`,color:envCol,textAlign:"center"}}>{environment?.slice(0,5)}</div>
        <div style={{padding:"2px 5px",borderRadius:2,fontSize:8,fontWeight:700,background:harmony?"rgba(0,229,160,0.08)":"rgba(255,208,96,0.08)",border:`1px solid ${harmony?"rgba(0,229,160,0.3)":"rgba(255,208,96,0.4)"}`,color:harmony?"#00e5a0":"#ffd060"}}>{harmony?"SYNC":"DISHRM"}</div>
      </div>
      <div style={{display:"flex",gap:3,marginBottom:5}}>
        <div style={{flex:1,padding:"2px 5px",borderRadius:2,fontSize:8,background:"rgba(255,255,255,0.03)",border:"1px solid #1a2535",textAlign:"center"}}><span style={{color:"#4a6070",marginRight:3}}>NANO</span><span style={{color:sideCol(nano_side),fontWeight:700}}>{sideIcon(nano_side)}</span></div>
        <div style={{flex:1,padding:"2px 5px",borderRadius:2,fontSize:8,background:"rgba(255,255,255,0.03)",border:"1px solid #1a2535",textAlign:"center"}}><span style={{color:"#4a6070",marginRight:3}}>MICRO</span><span style={{color:sideCol(micro_side),fontWeight:700}}>{sideIcon(micro_side)}</span></div>
      </div>
      <div style={{background:"rgba(0,0,0,0.2)",borderRadius:2,padding:"4px 6px",marginBottom:5}}>
        {sc>0&&<div style={{display:"flex",justifyContent:"space-between",padding:"1px 0",fontSize:10}}><span style={{color:"#8aabb8"}}>SC</span><span style={{color:"#ff40ff",fontFamily:"monospace",fontWeight:700}}>{sc.toFixed(2)}</span></div>}
        {ar>0&&<div style={{display:"flex",justifyContent:"space-between",padding:"1px 0",fontSize:10}}><span style={{color:"#8aabb8"}}>AR</span><span style={{color:"#00bcd4",fontFamily:"monospace",fontWeight:700}}>{ar.toFixed(2)}</span></div>}
        {mcp>0&&<div style={{display:"flex",justifyContent:"space-between",padding:"1px 0",fontSize:10}}><span style={{color:"#8aabb8"}}>MCP</span><span style={{color:"#c084fc",fontFamily:"monospace",fontWeight:700}}>{mcp.toFixed(2)}</span></div>}
        {msAtr>0&&<div style={{display:"flex",justifyContent:"space-between",padding:"1px 0",fontSize:10}}><span style={{color:"#8aabb8"}}>ATR</span><span style={{color:"#ffd060",fontFamily:"monospace"}}>{msAtr.toFixed(2)}</span></div>}
        {lfs>0&&<div style={{display:"flex",justifyContent:"space-between",padding:"1px 0",fontSize:10}}><span style={{color:"#8aabb8"}}>LFS</span><span style={{color:"#00d4ff",fontFamily:"monospace"}}>{lfs.toFixed(2)}</span></div>}
        {rfp>0&&<div style={{display:"flex",justifyContent:"space-between",padding:"1px 0",fontSize:10}}><span style={{color:"#8aabb8"}}>RFP</span><span style={{color:"#00e5a0",fontFamily:"monospace"}}>{rfp.toFixed(2)}</span></div>}
        {ffp>0&&<div style={{display:"flex",justifyContent:"space-between",padding:"1px 0",fontSize:10}}><span style={{color:"#8aabb8"}}>FFP</span><span style={{color:"#ff3f50",fontFamily:"monospace"}}>{ffp.toFixed(2)}</span></div>}
      </div>
      {reden&&<div style={{fontSize:7.5,color:"#6a8a9a",lineHeight:1.5,marginBottom:4,padding:"3px 5px",borderRadius:2,background:"rgba(0,0,0,0.15)",borderLeft:"2px solid #1a2535"}}>{reden.slice(0,90)}{reden.length>90?"…":""}</div>}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:7,color:"#4a6070"}}>{updateTime}</span>
        <button onClick={refreshMs} style={{padding:"1px 5px",borderRadius:2,cursor:"pointer",fontFamily:"monospace",fontSize:7,border:"1px solid #1a2535",background:"transparent",color:"#4a6070"}}>↺</button>
      </div>
    </div>
  );
}

// ── AUTH PAGE (registratie + login, API-backed) ────────────────
// Vervangt de oude hardgecodeerde USERS-array + client-side LoginPage:
// accounts leven nu in Cloudflare D1 via de Worker (zie src/auth/).
function AuthPage({ auth }) {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [shake, setShake] = useState(false);
  const [info, setInfo] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    auth.clearError();
    setInfo("");
    if (mode === "register") {
      const ok = await auth.register({ username: username.trim(), password, name: name.trim() });
      if (ok) {
        setInfo("Account created. You can now sign in.");
        setMode("login");
        setPassword("");
      } else {
        setShake(true); setTimeout(() => setShake(false), 500);
      }
      return;
    }
    const ok = await auth.login({ username: username.trim(), password });
    if (!ok) { setShake(true); setTimeout(() => setShake(false), 500); }
  }

  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:"#080c10",fontFamily:"'Courier New',monospace"}}>
      <style>{`
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}
        @keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-8px)}40%,80%{transform:translateX(8px)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        .login-box{animation:fadeIn 0.4s ease}.login-box.shake{animation:shake 0.4s ease}
        .login-input:focus{outline:none;border-color:#00d4ff!important}.login-btn:hover{background:rgba(0,212,255,0.18)!important}
        .login-tab{cursor:pointer;padding:6px 0;text-align:center;flex:1;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#8aabb8;border-bottom:2px solid transparent}
        .login-tab.active{color:#00d4ff;border-bottom-color:#00d4ff}
      `}</style>
      <div className={`login-box${shake?" shake":""}`} style={{width:340,background:"#0b1018",border:"1px solid #1a2535",borderRadius:4,padding:"28px 32px 28px"}}>
        <div style={{textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:28,fontWeight:900,color:"#00d4ff",letterSpacing:"0.08em",marginBottom:4}}>WYCKOFF CAFE</div>
          <div style={{fontSize:10,color:"#8aabb8",letterSpacing:"0.18em",textTransform:"uppercase"}}>Trading Platform</div>
          <div style={{width:40,height:1,background:"rgba(0,212,255,0.35)",margin:"10px auto 0"}}/>
        </div>
        <div style={{display:"flex",marginBottom:20,borderBottom:"1px solid #1a2535"}}>
          <div className={`login-tab${mode==="login"?" active":""}`} onClick={()=>{setMode("login");auth.clearError();setInfo("");}}>Sign in</div>
          <div className={`login-tab${mode==="register"?" active":""}`} onClick={()=>{setMode("register");auth.clearError();setInfo("");}}>Create account</div>
        </div>
        <form onSubmit={handleSubmit}>
          {mode === "register" && (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:9,color:"#8aabb8",marginBottom:5,letterSpacing:"0.1em",textTransform:"uppercase"}}>Name</div>
              <input className="login-input" type="text" value={name} onChange={e=>setName(e.target.value)} autoComplete="name"
                style={{width:"100%",background:"#0f1620",border:"1px solid #243040",color:"#e8f0f8",padding:"8px 10px",borderRadius:2,fontFamily:"monospace",fontSize:12,boxSizing:"border-box",transition:"border-color 0.15s"}}/>
            </div>
          )}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:9,color:"#8aabb8",marginBottom:5,letterSpacing:"0.1em",textTransform:"uppercase"}}>Username</div>
            <input className="login-input" type="text" value={username} onChange={e=>setUsername(e.target.value)} autoComplete="username"
              style={{width:"100%",background:"#0f1620",border:"1px solid #243040",color:"#e8f0f8",padding:"8px 10px",borderRadius:2,fontFamily:"monospace",fontSize:12,boxSizing:"border-box",transition:"border-color 0.15s"}}/>
          </div>
          <div style={{marginBottom:20}}>
            <div style={{fontSize:9,color:"#8aabb8",marginBottom:5,letterSpacing:"0.1em",textTransform:"uppercase"}}>Password{mode==="register" && " (min. 6 characters)"}</div>
            <input className="login-input" type="password" value={password} onChange={e=>setPassword(e.target.value)} autoComplete={mode==="register"?"new-password":"current-password"}
              style={{width:"100%",background:"#0f1620",border:"1px solid #243040",color:"#e8f0f8",padding:"8px 10px",borderRadius:2,fontFamily:"monospace",fontSize:12,boxSizing:"border-box",transition:"border-color 0.15s"}}/>
          </div>
          {auth.error && <div style={{fontSize:10,color:"#ff3f50",marginBottom:14,padding:"6px 8px",background:"rgba(255,63,80,0.08)",border:"1px solid rgba(255,63,80,0.25)",borderRadius:2}}>{auth.error}</div>}
          {info && <div style={{fontSize:10,color:"#00e5a0",marginBottom:14,padding:"6px 8px",background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.25)",borderRadius:2}}>{info}</div>}
          <button type="submit" disabled={auth.loading} className="login-btn" style={{width:"100%",padding:"9px 0",background:"rgba(0,212,255,0.1)",border:"1px solid rgba(0,212,255,0.4)",color:"#00d4ff",fontFamily:"monospace",fontSize:11,fontWeight:700,letterSpacing:"0.12em",cursor:auth.loading?"default":"pointer",borderRadius:2,transition:"background 0.15s",opacity:auth.loading?0.6:1}}>
            {auth.loading ? "PLEASE WAIT..." : mode === "register" ? "CREATE ACCOUNT" : "ENTER PLATFORM"}
          </button>
        </form>
        <div style={{marginTop:20,fontSize:9,color:"#4a6070",textAlign:"center"}}>Wyckoff Trading Café · PCG Framework</div>
      </div>
    </div>
  );
}

// ── MEMBERSHIP GATE ──────────────────────────────────────────────
// Toegang tot de Chart (en de PnF Terminal) = betaald maandelijks
// lidmaatschap via Stripe. Coaches en accounts met gratis toegang
// (comp_access) krijgen van de server altijd meteen "approved".
function AccessGate({ auth, onHome }) {
  const status = auth.accessStatus;
  const m = auth.membership;
  const busy = status === null; // nog aan het laden
  const params = new URLSearchParams(window.location.search);
  const pastStatus = m && ["canceled", "unpaid", "incomplete_expired"].includes(m.status);
  // "Processing payment" alleen direct na terugkomst van Stripe, hooguit 90 s,
  // en nooit als het lidmaatschap al beëindigd is (anders blijft een oude
  // ?checkout=success in de adresbalk eeuwig hangen).
  const [waitOver, setWaitOver] = useState(false);
  useEffect(() => { const t = setTimeout(() => setWaitOver(true), 90000); return () => clearTimeout(t); }, []);
  const justPaid = params.get("checkout") === "success" && !pastStatus && !waitOver;
  const cancelled = params.get("checkout") === "cancel";

  let heading = "Become a Pro member";
  let body = "A Pro membership gives you access to the Trading Dashboard and the PnF Terminal, plus the Pro member role in our Discord. Cancel anytime.";
  let showBtn = true;

  if (busy) {
    heading = "One moment...";
    body = "Checking your status.";
    showBtn = false;
  } else if (justPaid) {
    heading = "Processing payment";
    body = "Thank you! Your membership is being activated. This screen continues automatically once the payment is confirmed (usually within seconds; iDEAL/SEPA can take a little longer).";
    showBtn = false;
  } else if (pastStatus) {
    heading = "Membership ended";
    body = "Your membership has ended. Become a Pro member again to regain access.";
  }

  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:"#080c10",fontFamily:"'Courier New',monospace"}}>
      <div style={{width:380,background:"#0b1018",border:"1px solid #1a2535",borderRadius:4,padding:"32px 32px 28px",textAlign:"center"}}>
        <div style={{fontSize:13,color:"#8aabb8",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>{heading}</div>
        {justPaid && (
          <div style={{width:10,height:10,borderRadius:"50%",background:"#00e5a0",margin:"0 auto 14px",animation:"pulse 1.4s ease infinite"}}/>
        )}
        <div style={{fontSize:12,color:"#c8dae8",lineHeight:1.5,marginBottom:18}}>{body}</div>
        {showBtn && m && m.priceLabel && (
          <div style={{fontSize:22,color:"#e8f0f8",marginBottom:18,fontFamily:"Georgia,serif"}}>{m.priceLabel}</div>
        )}
        {cancelled && !busy && <div style={{fontSize:10,color:"#ffd060",marginBottom:14}}>Payment cancelled — you have not been charged.</div>}
        {auth.error && <div style={{fontSize:10,color:"#ff3f50",marginBottom:16,padding:"6px 8px",background:"rgba(255,63,80,0.08)",border:"1px solid rgba(255,63,80,0.25)",borderRadius:2}}>{auth.error}</div>}
        {showBtn && (
          <button onClick={auth.startCheckout} className="login-btn" style={{width:"100%",padding:"10px 0",background:"rgba(0,212,255,0.1)",border:"1px solid rgba(0,212,255,0.4)",color:"#00d4ff",fontFamily:"monospace",fontSize:11,fontWeight:700,letterSpacing:"0.12em",cursor:"pointer",borderRadius:2}}>
            BECOME A PRO MEMBER
          </button>
        )}
        {m && m.hasBilling && !justPaid && (
          <div style={{marginTop:12}}>
            <span onClick={auth.openBillingPortal} style={{fontSize:10,color:"#8aabb8",cursor:"pointer",textDecoration:"underline"}}>Invoices & payment details</span>
          </div>
        )}
        <div style={{marginTop:18}}>
          {onHome && <span onClick={onHome} style={{fontSize:10,color:"#4a6070",cursor:"pointer",textDecoration:"underline",marginRight:16}}>← Back to home</span>}
          <span onClick={auth.logout} style={{fontSize:10,color:"#4a6070",cursor:"pointer",textDecoration:"underline"}}>Sign out</span>
        </div>
      </div>
    </div>
  );
}

// ── THEME ──────────────────────────────────────────────────────
const T = {
  bg:"#080c10", panel:"#0b1018", card:"#0f1620", border:"#1a2535", borderLt:"#243040",
  white:"#e8f0f8", bright:"#c8dae8", dim:"#8aabb8",
  green:"#00e5a0", greenDim:"rgba(0,229,160,0.35)", red:"#ff3f50", redDim:"rgba(255,63,80,0.35)",
  yellow:"#ffd060", orange:"#ff8c42", accent:"#00d4ff", purple:"#c084fc", gold:"#f5a623", lime:"#80ff40",
};

const calcATR = atr => ({ wt1:atr*0.125, wt2:atr*0.25, wt3:atr*0.375, wt4:atr*0.5, wt5:atr*0.625, wt6:atr*0.75, wt7:atr*0.875, so:atr*0.5, full:atr });

// ── HYPERLIQUID DATAFEED ───────────────────────────────────────
const TF_CONFIG = { "1m":{interval:"1m",label:"1m"}, "5m":{interval:"5m",label:"5m"}, "15m":{interval:"15m",label:"15m"}, "1h":{interval:"1h",label:"1h"} };

async function fetchHyperliquid(tf="1m") {
  const cfg = TF_CONFIG[tf]||TF_CONFIG["1m"];
  const intervalMs = {"1m":60*1000,"5m":5*60*1000,"15m":15*60*1000,"1h":60*60*1000}[cfg.interval]||60*1000;
  const endTime = Date.now(), startTime = endTime-(5000*intervalMs);
  const coins = ["@SP500","xyz:SP500","SP500"];
  for(const coin of coins){
    try{
      const r = await fetch("https://api.hyperliquid.xyz/info",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({type:"candleSnapshot",req:{coin,interval:cfg.interval,startTime,endTime}})});
      if(!r.ok) continue;
      const candles = await r.json();
      if(!Array.isArray(candles)||candles.length<5) continue;
      console.log("[FEED] success with coin:",coin,"candles:",candles.length);
      return candles.map(c=>({time:new Date(c.t),open:Math.round(parseFloat(c.o)*4)/4,high:Math.round(parseFloat(c.h)*4)/4,low:Math.round(parseFloat(c.l)*4)/4,close:Math.round(parseFloat(c.c)*4)/4,vol:parseFloat(c.v)||0}));
    }catch(e){console.log("[FEED] failed with coin:",coin,e.message);}
  }
  throw new Error("All SP500 coin names failed");
}
const fetchYahoo = fetchHyperliquid;


function makeFallback() {
  const bars=[];let p=5320,t=new Date("2026-04-27T13:30:00Z").getTime();
  const biases=[[30,-0.3],[30,0.15],[70,-0.05],[15,-0.4],[55,0.25],[40,0.05],[20,0.5],[70,0.2],[30,-0.1],[30,-0.35]];
  let bi=0,bc=0;
  for(let i=0;i<390;i++){
    if(bc>=biases[bi][0]){bi=Math.min(bi+1,biases.length-1);bc=0;}bc++;
    const bias=biases[bi][1],o=p,m=(Math.random()-0.48+bias)*4,c=Math.round((p+m)*4)/4;
    const h=Math.round((Math.max(o,c)+Math.random()*2)*4)/4,l=Math.round((Math.min(o,c)-Math.random()*2)*4)/4;
    bars.push({time:new Date(t),open:o,high:h,low:l,close:c,vol:Math.floor(400+Math.random()*1200)});
    p=c;t+=60000;
  }
  return bars;
}
const BASE=makeFallback();

// ── PCG CYCLE ENGINE ───────────────────────────────────────────
function buildZigzag(bars,minMove) {
  const swings=[];let zzP=null,zzT=1,zzB=0;
  for(let i=0;i<bars.length;i++){
    const b=bars[i];
    if(zzP===null){zzP=b.close;zzT=1;zzB=i;continue;}
    if(zzT===1){if(b.high>zzP){zzP=b.high;zzB=i;}else if(zzP-b.low>=minMove){swings.push({i:zzB,price:zzP,type:1});zzP=b.low;zzT=-1;zzB=i;}}
    else{if(b.low<zzP){zzP=b.low;zzB=i;}else if(b.high-zzP>=minMove){swings.push({i:zzB,price:zzP,type:-1});zzP=b.high;zzT=1;zzB=i;}}
  }
  if(zzP!==null) swings.push({i:zzB,price:zzP,type:zzT});
  return swings;
}

function classifyVSASwings(bars,swings) {
  if(!swings.length||!bars.length) return [];
  const window=Math.min(20,bars.length);
  const classified=[];
  for(let si=0;si<swings.length-1;si++){
    const from=swings[si],to=swings[si+1];
    const startIdx=Math.min(from.i,to.i),endIdx=Math.max(from.i,to.i);
    const swingBars=bars.slice(startIdx,endIdx+1);
    if(!swingBars.length) continue;
    const ctxStart=Math.max(0,startIdx-window),ctxBars=bars.slice(ctxStart,endIdx+1);
    const avgVol=ctxBars.reduce((s,b)=>s+(b.vol||0),0)/ctxBars.length;
    const avgSpread=ctxBars.reduce((s,b)=>s+(b.high-b.low),0)/ctxBars.length;
    const isUp=to.price>from.price;
    const swingVol=swingBars.reduce((s,b)=>s+(b.vol||0),0)/swingBars.length;
    const swingSpread=swingBars.reduce((s,b)=>s+(b.high-b.low),0)/swingBars.length;
    const lastBar=bars[endIdx];
    const closePos=lastBar?(lastBar.close-lastBar.low)/(lastBar.high-lastBar.low||1):0.5;
    const highVol=swingVol>avgVol*1.1,wideSpread=swingSpread>avgSpread*1.0,closesHigh=closePos>0.5;
    const vsaClass=isUp?(highVol&&wideSpread&&closesHigh?"D+":"D-"):(highVol&&wideSpread&&!closesHigh?"S+":"S-");
    classified.push({fromIdx:from.i,toIdx:to.i,fromPrice:from.price,toPrice:to.price,vsaClass,isUp,highVol,wideSpread,closesHigh});
  }
  return classified;
}


// ── VPA SWING SIGNALS (Anna Coulling) ─────────────────────────
// Swing-level VPA principles: Stopping Vol, Topping Out Vol,
// No Demand, No Supply, Shooting Star, Hammer
function detectVPASwingSignals(bars, swings, atr) {
  if (!swings.length || !bars.length) return [];
  const a = calcATR(atr);
  const ctxLen = Math.min(30, bars.length);
  const sigs = [];

  for (let si = 0; si < swings.length - 1; si++) {
    const from = swings[si], to = swings[si+1];
    const startIdx = Math.min(from.i, to.i);
    const endIdx   = Math.max(from.i, to.i);
    const swingBars = bars.slice(startIdx, endIdx+1);
    if (swingBars.length < 2) continue;

    const ctxBars  = bars.slice(Math.max(0, startIdx - ctxLen), endIdx+1);
    const avgVol   = ctxBars.reduce((s,b)=>s+(b.vol||0),0)/ctxBars.length || 1;
    const avgSpread= ctxBars.reduce((s,b)=>s+(b.high-b.low),0)/ctxBars.length || 1;

    const isUp   = to.price > from.price;
    const lastB  = bars[endIdx];
    if (!lastB) continue;

    const swingVol    = swingBars.reduce((s,b)=>s+(b.vol||0),0)/swingBars.length;
    const swingSpread = swingBars.reduce((s,b)=>s+(b.high-b.low),0)/swingBars.length;
    const highVol     = swingVol > avgVol * 1.2;
    const lowVol      = swingVol < avgVol * 0.75;
    const wideSpread  = swingSpread > avgSpread * 1.1;
    const narrowSpread= swingSpread < avgSpread * 0.8;

    // Spread narrowing over last 3 bars of swing
    const last3 = swingBars.slice(-3);
    const spreadsTrend = last3.length >= 2
      ? last3[last3.length-1].high - last3[last3.length-1].low <
        last3[0].high - last3[0].low
      : false;

    // Lower wicks present (buying pressure from below)
    const lowerWicks = swingBars.filter(b=>(b.close-b.low)>(b.high-b.low)*0.35).length;
    const hasLowerWicks = lowerWicks >= Math.ceil(swingBars.length*0.4);

    // Upper wicks present (selling from above)
    const upperWicks = swingBars.filter(b=>(b.high-b.close)>(b.high-b.low)*0.35).length;
    const hasUpperWicks = upperWicks >= Math.ceil(swingBars.length*0.4);

    // Last bar candle patterns
    const lastRange   = lastB.high - lastB.low || 0.01;
    const lastBody    = Math.abs(lastB.close - lastB.open);
    const lastUpWick  = lastB.high - Math.max(lastB.open, lastB.close);
    const lastDnWick  = Math.min(lastB.open, lastB.close) - lastB.low;
    const isShootStar = lastUpWick > lastBody * 1.5 && lastDnWick < lastBody * 0.5;
    const isHammer    = lastDnWick > lastBody * 1.5 && lastUpWick < lastBody * 0.5;

    let label = null, col = null, isLong = null, detail = null;

    // ── STOP-VOL: down swing, high vol, narrowing spreads, lower wicks → brakes applied
    if (!isUp && highVol && spreadsTrend && hasLowerWicks) {
      label = 'STOP-VOL'; col = '#00e5a0'; isLong = true;
      detail = 'Stopping volume — brakes applied';
    }
    // ── TOP-VOL: up swing, high vol, upper wicks, narrowing spreads → distribution
    else if (isUp && highVol && spreadsTrend && hasUpperWicks) {
      label = 'TOP-VOL'; col = '#ff3f50'; isLong = false;
      detail = 'Topping out volume — supply entering';
    }
    // ── NO DEMAND: up swing, low vol, narrow spread → weakness in rally
    else if (isUp && lowVol && narrowSpread) {
      label = 'NO DMD'; col = '#ff8c42'; isLong = false;
      detail = 'No Demand — weak rally, supply dominant';
    }
    // ── NO SUPPLY: down swing, low vol, narrow spread → strength (low vol test)
    else if (!isUp && lowVol && narrowSpread) {
      label = 'NO SUP'; col = '#00d4ff'; isLong = true;
      detail = 'No Supply — low vol test, demand holding';
    }
    // ── SHOOT: up swing ends in shooting star + high vol → sell signal
    else if (isUp && isShootStar && highVol) {
      label = 'SHOOT'; col = '#ff3f50'; isLong = false;
      detail = 'Shooting star + high vol — reversal warning';
    }
    // ── HAMMER: down swing ends in hammer + high vol → buy signal
    else if (!isUp && isHammer && highVol) {
      label = 'HAMMER'; col = '#00e5a0'; isLong = true;
      detail = 'Hammer + high vol — reversal strength';
    }

    if (label) {
      sigs.push({
        barIdx: to.i, fromIdx: from.i,
        label, col, isLong, detail,
        swingVol, avgVol, toPrice: to.price, fromPrice: from.price,
        type: 'vpa',
      });
    }
  }
  return sigs;
}

function detectTransients(bars,pivLen=5,bias="front",eqLow=null,eqHigh=null) {
  const highs=[],lows=[];
  for(let i=pivLen;i<bars.length-pivLen;i++){
    let isH=true,isL=true;
    for(let j=i-pivLen;j<=i+pivLen;j++){if(j===i)continue;if(bars[j].high>=bars[i].high)isH=false;if(bars[j].low<=bars[i].low)isL=false;}
    if(isH)highs.push({i,price:bars[i].high});if(isL)lows.push({i,price:bars[i].low});
  }
  let rdLines=[],fsLines=[],mom=null;
  if(bias==="front"&&eqLow!==null){
    const hhSorted=[...highs].sort((a,b)=>a.i-b.i);
    for(const h of hhSorted){if(h.price>eqLow){const mcp=Math.round(((eqLow+h.price)/2)*4)/4;rdLines.push({price:mcp,hhPrice:h.price,anchor:eqLow,barIdx:h.i});}}
    if(rdLines.length>0)mom=rdLines[rdLines.length-1].price;
  } else if(bias==="back"&&eqHigh!==null){
    const llSorted=[...lows].sort((a,b)=>a.i-b.i);
    for(const l of llSorted){if(l.price<eqHigh){const mcp=Math.round(((eqHigh+l.price)/2)*4)/4;fsLines.push({price:mcp,llPrice:l.price,anchor:eqHigh,barIdx:l.i});}}
    if(fsLines.length>0)mom=fsLines[fsLines.length-1].price;
  } else {
    for(const h of highs.slice(-2))fsLines.push({price:h.price,barIdx:h.i,raw:true});
    for(const l of lows.slice(-2))rdLines.push({price:l.price,barIdx:l.i,raw:true});
  }
  const latRD=rdLines.slice(-5).reverse(),latFS=fsLines.slice(-5).reverse();
  const ctrl=mom!==null?((bars[bars.length-1]?.close||0)>mom?"DEMAND > SUPPLY":"SUPPLY > DEMAND"):"NO SIGNAL";
  return{rdLines,fsLines,latestRising:latRD,latestFalling:latFS,mom,ctrl,pivots:{highs,lows},bias};
}

function runPCG(bars,atr,anchor,staticMBZ,anchorTimes,zzMult,cycleType) {
  const a=calcATR(atr),zzMin=a.wt1*zzMult,swings=buildZigzag(bars,zzMin),n=swings.length;
  const labels=[],signals=[];
  const MAX_SWINGS_AFTER_B3=8,MAX_SWINGS_AFTER_C=10,TEMPO_MULT=2.5,B1_MIN_PCT=20;
  const hasAnchor=anchor&&(parseFloat(anchor.scbc)||0)>0&&(parseFloat(anchor.ar)||0)>0;
  const isAccum=!anchor||anchor.type!=="dist";
  const anchorSCBC=hasAnchor?parseFloat(anchor.scbc):null,anchorAR=hasAnchor?parseFloat(anchor.ar):null;

  function findBarByTime(timeStr){
    if(!timeStr||!bars.length)return null;
    const[hh,mm]=timeStr.split(":").map(Number);if(isNaN(hh)||isNaN(mm))return null;
    let bestI=null,bestD=Infinity;
    for(let i=0;i<bars.length;i++){const t=bars[i].time instanceof Date?bars[i].time:new Date(bars[i].time);const d=Math.abs((t.getHours()*60+t.getMinutes())-(hh*60+mm));if(d<bestD){bestD=d;bestI=i;}}
    return bestI;
  }
  const lbl=(bi,p,txt,col,above)=>{if(bi===null||bi===undefined)return;labels.push({barIdx:bi,price:p,text:txt,col,above});};

  let wyk_state=0,scV=null,scB=null,arV=null,arB=null,stV=null,stB=null,rH=null,rL=null;
  let b1V=null,b1B=null,b2V=null,b2B=null,b3V=null,b3B=null,cEx=null,cB=null,cType=0;
  let dsowV=null,dsowB=null,lpsV=null,lpsB=null,lpsCand=null,lpsCandB=null,dsosV=null,dsosB=null;
  let frontside=null,phaseATempo=null,avgBWaveBars=null,cTempoDeadline=null;
  let swingsSinceB3=0,swingsSinceC=0,lastCountedSw=null;
  let stBarGuard=null,b1BarGuard=null,b2BarGuard=null,b3BarGuard=null,cBarGuard=null,dsowBarGuard=null;

  if(hasAnchor){
    const scbcTimeBar=anchorTimes?.scbc?findBarByTime(anchorTimes.scbc):null;
    const arTimeBar=anchorTimes?.ar?findBarByTime(anchorTimes.ar):null;
    if(scbcTimeBar!==null&&scbcTimeBar!==undefined){scB=scbcTimeBar;scV=anchorSCBC;}
    else{const scbcType=isAccum?-1:1;let bestD=Infinity;for(const sw of swings){if(sw.type===scbcType){const d=Math.abs(sw.price-anchorSCBC);if(d<bestD){bestD=d;scB=sw.i;scV=sw.price;}}}}
    if(arTimeBar!==null&&arTimeBar!==undefined){arB=arTimeBar;arV=anchorAR;}
    else{const arType=isAccum?1:-1;let bestD=Infinity;for(const sw of swings){if(sw.type===arType&&sw.i>(scB||0)){const d=Math.abs(sw.price-anchorAR);if(d<bestD){bestD=d;arB=sw.i;arV=sw.price;}}}}
    if(scB!==null&&arB!==null&&arB>scB){
      const stType=isAccum?-1:1;
      for(const sw of swings){if(sw.type===stType&&sw.i>arB){stV=sw.price;stB=sw.i;break;}}
      if(stB!==null){
        rH=isAccum?Math.max(arV,anchorAR):Math.max(anchorSCBC,scV);rL=isAccum?Math.min(anchorSCBC,scV):Math.min(arV,anchorAR);
        stBarGuard=stB;const barsDiff=Math.abs(arB-scB);if(barsDiff>0)phaseATempo=isAccum?(arV-scV)/barsDiff:(scV-arV)/barsDiff;
        wyk_state=2;
        // Use cycleType for climax label, fallback to isAccum if no anchor
        const useDistMode = anchor ? anchor.type === "dist" : cycleType === "dist";
        const climaxLabel = useDistMode ? "BC" : "SC"; // Distribution = BC, Accumulation = SC
        const climaxColor = useDistMode ? "#ff6060" : "#ff40ff";
        const climaxAbove = useDistMode;
        lbl(scB,anchorSCBC,climaxLabel,climaxColor,climaxAbove);lbl(arB,anchorAR,"AR","#00bcd4",isAccum);lbl(stB,stV,"ST","#4488ff",!isAccum);
        if(isAccum){for(let i=swings.length-1;i>=0;i--){const sw=swings[i];if(sw.type===-1&&sw.i<scB&&sw.price>anchorSCBC){lbl(sw.i,sw.price,"PS","#ff8060",false);break;}}}
      }
    }
  } else {
    if(n>=3){
      for(let i=2;i<n;i++){
        const t2=swings[i-2].type,t1=swings[i-1].type,t0=swings[i].type;
        const p2=swings[i-2].price,p1=swings[i-1].price,p0=swings[i].price;
        const b2=swings[i-2].i,b1r=swings[i-1].i,b0=swings[i].i;
        // Use cycleType for swing-based climax detection too
        const distMode = anchor ? anchor.type === "dist" : cycleType === "dist";
        if(t2===-1&&t1===1&&t0===-1&&!distMode){scV=p2;scB=b2;arV=p1;arB=b1r;stV=p0;stB=b0;rH=p1;rL=p2;stBarGuard=b0;const bd=Math.abs(b1r-b2);if(bd>0)phaseATempo=(p1-p2)/bd;wyk_state=2;lbl(scB,scV,"SC","#ff40ff",false);lbl(arB,arV,"AR","#00bcd4",true);lbl(stB,stV,"ST","#4488ff",false);break;}
        if(t2===1&&t1===-1&&t0===1&&distMode){scV=p2;scB=b2;arV=p1;arB=b1r;stV=p0;stB=b0;rH=p2;rL=p1;stBarGuard=b0;const bd=Math.abs(b1r-b2);if(bd>0)phaseATempo=(p2-p1)/bd;wyk_state=2;lbl(scB,scV,"BC","#ff6060",true);lbl(arB,arV,"AR","#00bcd4",false);lbl(stB,stV,"ST","#4488ff",true);break;}
      }
    }
  }

  if(wyk_state<2){return{st:0,isAccum,labels,signals,zigzag:swings,scbcV:anchorSCBC,arV:anchorAR,stV:null,rH:null,rL:null,b1V:null,b2V:null,b3V:null,cEx:null,cType:0,dsowV:null,lpsV:null,dsosV:null,frontside:null,phaseLabel:"Phase A — Set SC/BC + AR",scbcB:scB,arB:arB};}

  const phaseABars=scB!==null&&arB!==null?Math.abs(arB-scB):0;
  const b1MinBar=stB!==null?stB+Math.round(phaseABars*B1_MIN_PCT/100):stB;

  for(let si=0;si<swings.length;si++){
    const{i:bi,price:p,type:t}=swings[si];
    if(bi<=arB)continue;if(bi!==lastCountedSw){lastCountedSw=bi;}

    if(wyk_state===2){
      if(b3B!==null){if(bi>lastCountedSw||lastCountedSw===null)swingsSinceB3++;if(cTempoDeadline!==null&&bi>cTempoDeadline){wyk_state=0;break;}if(swingsSinceB3>MAX_SWINGS_AFTER_B3){wyk_state=0;break;}}
      if(isAccum){
        if(!b1V&&t===1&&bi>=(b1MinBar||0)){b1V=p;b1B=bi;b1BarGuard=bi;lbl(bi,p,"B","#f5a623",true);}
        else if(b1V&&!b2V&&t===-1&&bi>b1BarGuard){b2V=p;b2B=bi;b2BarGuard=bi;rL=Math.min(rL,p);lbl(bi,p,"B","#f5a623",false);}
        else if(b2V&&!b3V&&t===1&&bi>b2BarGuard){b3V=p;b3B=bi;b3BarGuard=bi;rH=Math.max(rH,p);lbl(bi,p,"B","#f5a623",true);const totalBBars=Math.abs(bi-scB);avgBWaveBars=totalBBars>0?totalBBars/3:null;cTempoDeadline=avgBWaveBars?Math.round(bi+avgBWaveBars*TEMPO_MULT):null;swingsSinceB3=0;}
        if(b3V!==null&&t===-1&&bi>b3BarGuard){
          if(p<rL){cEx=p;cB=bi;cType=1;cBarGuard=bi;swingsSinceC=0;wyk_state=3;lbl(bi,p,"C","#ff40ff",false);signals.push({barIdx:bi,type:"long",setup:"C — Spring",entry:Math.round((p+0.25)*4)/4,stop:Math.round((p-a.wt2)*4)/4,target:Math.round((p+0.25+4)*4)/4,qualified:true});}
          else if(p>=rL&&p<=rL+a.wt4){cEx=p;cB=bi;cType=2;cBarGuard=bi;swingsSinceC=0;wyk_state=3;lbl(bi,p,"C2","#ffd060",false);signals.push({barIdx:bi,type:"long",setup:"C2 Spring Test",entry:Math.round((p+0.25)*4)/4,stop:Math.round((p-a.wt2)*4)/4,target:Math.round((p+0.25+4)*4)/4,qualified:true});}
        }
      } else {
        if(!b1V&&t===-1&&bi>=(b1MinBar||0)){b1V=p;b1B=bi;b1BarGuard=bi;lbl(bi,p,"B","#f5a623",false);}
        else if(b1V&&!b2V&&t===1&&bi>b1BarGuard){b2V=p;b2B=bi;b2BarGuard=bi;rH=Math.max(rH,p);lbl(bi,p,"B","#f5a623",true);}
        else if(b2V&&!b3V&&t===-1&&bi>b2BarGuard){b3V=p;b3B=bi;b3BarGuard=bi;rL=Math.min(rL,p);lbl(bi,p,"B","#f5a623",false);const totalBBars=Math.abs(bi-scB);avgBWaveBars=totalBBars>0?totalBBars/3:null;cTempoDeadline=avgBWaveBars?Math.round(bi+avgBWaveBars*TEMPO_MULT):null;swingsSinceB3=0;}
        if(b3V!==null&&t===1&&bi>b3BarGuard){
          if(p>rH){cEx=p;cB=bi;cType=1;cBarGuard=bi;swingsSinceC=0;wyk_state=3;lbl(bi,p,"C","#ff40ff",true);signals.push({barIdx:bi,type:"short",setup:"C — UTAD",entry:Math.round((p-0.25)*4)/4,stop:Math.round((p+a.wt2)*4)/4,target:Math.round((p-0.25-4)*4)/4,qualified:true});}
          else if(p<=rH&&p>=rH-a.wt4){cEx=p;cB=bi;cType=2;cBarGuard=bi;swingsSinceC=0;wyk_state=3;lbl(bi,p,"C2","#ffd060",true);signals.push({barIdx:bi,type:"short",setup:"C2 UTAD Test",entry:Math.round((p-0.25)*4)/4,stop:Math.round((p+a.wt2)*4)/4,target:Math.round((p-0.25-4)*4)/4,qualified:true});}
        }
      }
    }

    if(wyk_state===3){
      swingsSinceC++;if(swingsSinceC>MAX_SWINGS_AFTER_C){wyk_state=0;break;}
      if(cType===2){if(isAccum&&p<cEx&&t===-1){wyk_state=2;cEx=null;cB=null;cType=0;continue;}if(!isAccum&&p>cEx&&t===1){wyk_state=2;cEx=null;cB=null;cType=0;continue;}}
      if(isAccum&&t===1&&bi>cBarGuard&&p<rH){dsowV=p;dsowB=bi;dsowBarGuard=bi;lpsCand=null;lpsCandB=null;wyk_state=4;lbl(bi,p,"D-SOW","#00cc88",true);}
      if(!isAccum&&t===-1&&bi>cBarGuard&&p>rL){dsowV=p;dsowB=bi;dsowBarGuard=bi;lpsCand=null;lpsCandB=null;wyk_state=4;lbl(bi,p,"D-SOW","#00cc88",false);}
    }

    if(wyk_state===4){
      if(isAccum){
        if(t===-1&&bi>dsowBarGuard){if(lpsCand===null||p<lpsCand){lpsCand=p;lpsCandB=bi;}}
        if(t===1&&bi>dsowBarGuard&&lpsCand!==null&&bi>lpsCandB&&lpsCand>cEx){lpsV=lpsCand;lpsB=lpsCandB;wyk_state=5;lbl(lpsCandB,lpsCand,"LPS/BT","#00e090",false);const e=Math.round((lpsCand+0.25)*4)/4;signals.push({barIdx:lpsB,type:"long",setup:"LPS/BT",entry:e,stop:Math.round((lpsCand-a.wt2)*4)/4,target:Math.round((e+a.wt5)*4)/4,target2:Math.round((e+a.so)*4)/4,qualified:true});}
      } else {
        if(t===1&&bi>dsowBarGuard){if(lpsCand===null||p>lpsCand){lpsCand=p;lpsCandB=bi;}}
        if(t===-1&&bi>dsowBarGuard&&lpsCand!==null&&bi>lpsCandB&&lpsCand<cEx){lpsV=lpsCand;lpsB=lpsCandB;wyk_state=5;lbl(lpsCandB,lpsCand,"LPS","#00e090",true);const e=Math.round((lpsCand-0.25)*4)/4;signals.push({barIdx:lpsB,type:"short",setup:"LPS",entry:e,stop:Math.round((lpsCand+a.wt2)*4)/4,target:Math.round((e-a.wt5)*4)/4,target2:Math.round((e-a.so)*4)/4,qualified:true});}
      }
    }

    if(wyk_state===5){
      if(isAccum&&p>rH&&t===1){dsosV=p;dsosB=bi;wyk_state=6;lbl(bi,p,"D-SOS","#80ff40",true);}
      if(!isAccum&&p<rL&&t===-1){dsosV=p;dsosB=bi;wyk_state=6;lbl(bi,p,"D-SOS","#80ff40",false);}
    }

    if(wyk_state===6&&dsosV!==null&&isAccum){
      if(t===-1&&p<=arV+a.wt1&&p>=arV-a.wt2){lbl(bi,p,"BU","#00d4ff",false);const e=Math.round((p+0.25)*4)/4;signals.push({barIdx:bi,type:"long",setup:"Back-Up (BU)",entry:e,stop:Math.round((p-a.wt2)*4)/4,target:Math.round((e+a.wt3)*4)/4,target2:Math.round((e+a.wt5)*4)/4,qualified:true});}
    }
  }

  if(cEx!==null&&cB!==null){
    let clPrice,clBar;
    if(isAccum){const swingsToCbar=swings.filter(s=>s.i<=cB&&s.type===-1);const lowestSw=swingsToCbar.reduce((min,s)=>s.price<min.price?s:min,{price:Infinity,i:scB||0});if(cType===1&&cEx<=lowestSw.price){clPrice=cEx;clBar=cB;}else if(lowestSw.price<Infinity){clPrice=lowestSw.price;clBar=lowestSw.i;}else{clPrice=cEx;clBar=cB;}}
    else{const swingsToCbar=swings.filter(s=>s.i<=cB&&s.type===1);const highestSw=swingsToCbar.reduce((max,s)=>s.price>max.price?s:max,{price:-Infinity,i:scB||0});if(cType===1&&cEx>=highestSw.price){clPrice=cEx;clBar=cB;}else if(highestSw.price>-Infinity){clPrice=highestSw.price;clBar=highestSw.i;}else{clPrice=cEx;clBar=cB;}}
    frontside={cl:clPrice,clB:clBar,rd1:null,rd1B:null,bt:null,btB:null,lol:null,lolB:null,complete:false};
    if(cType===2){frontside.rd1=cEx;frontside.rd1B=cB;lbl(scB,clPrice,"CL","#ff40ff",!isAccum);lbl(cB,cEx,isAccum?"1st RD":"1st FS",isAccum?"#00e5a0":"#ff3f50",isAccum);}
    let fsPhase=cType===2?1:0,lastHL=clPrice,hadHHafterRD=false,hadHHafterBT=false;
    if(lpsV!==null){frontside.bt=lpsV;frontside.btB=lpsB;if(cType===2)frontside.rd1=cEx;fsPhase=2;lastHL=lpsV;hadHHafterRD=true;}
    const barsAfterC=bars.slice(cB),fsSwings=buildZigzag(barsAfterC,a.wt2);
    for(let i=0;i<fsSwings.length;i++){
      const sw=fsSwings[i],gi=cB+sw.i;
      const isHigh=isAccum?sw.type===1:sw.type===-1,isLow=isAccum?sw.type===-1:sw.type===1;
      if(isHigh){
        if(fsPhase===0){frontside.rd1=sw.price;frontside.rd1B=gi;lbl(gi,sw.price,isAccum?"1st RD":"1st FS",isAccum?"#00e5a0":"#ff3f50",isAccum);fsPhase=1;hadHHafterRD=true;}
        else if(fsPhase===1){hadHHafterRD=true;}else if(fsPhase===2){hadHHafterBT=true;}
        else if(fsPhase===3){frontside.complete=true;lbl(gi,sw.price,"EXP","#80ff40",isAccum);const magnet=staticMBZ?staticMBZ.mid:isAccum?Math.round((rH+(rH-rL)*0.2)*4)/4:Math.round((rL-(rH-rL)*0.2)*4)/4;signals.push({barIdx:gi,type:isAccum?"long":"short",setup:isAccum?"Full Frontside — Magnet":"Full Backside — Magnet",entry:sw.price,stop:isAccum?lastHL-a.wt2:lastHL+a.wt2,target:magnet,qualified:true});break;}
      }
      if(isLow){
        if(fsPhase===1&&hadHHafterRD&&sw.price>lastHL){frontside.bt=sw.price;frontside.btB=gi;lbl(gi,sw.price,isAccum?"BT":"BT(LH)",isAccum?"#ffd060":"#ff8c42",!isAccum);const e=isAccum?Math.round((sw.price+0.25)*4)/4:Math.round((sw.price-0.25)*4)/4;signals.push({barIdx:gi,type:isAccum?"long":"short",setup:"BT",entry:e,stop:isAccum?Math.round((sw.price-a.wt1)*4)/4:Math.round((sw.price+a.wt1)*4)/4,target:isAccum?Math.round((e+a.wt3)*4)/4:Math.round((e-a.wt3)*4)/4,target2:isAccum?Math.round((e+a.wt5)*4)/4:Math.round((e-a.wt5)*4)/4,qualified:true});lastHL=sw.price;fsPhase=2;}
        else if(fsPhase===2&&hadHHafterBT&&sw.price>lastHL){frontside.lol=sw.price;frontside.lolB=gi;lbl(gi,sw.price,isAccum?"LoL":"LoL(LH)",isAccum?"#ff8c42":"#ffd060",!isAccum);const e=isAccum?Math.round((sw.price+0.25)*4)/4:Math.round((sw.price-0.25)*4)/4;signals.push({barIdx:gi,type:isAccum?"long":"short",setup:"LoL",entry:e,stop:isAccum?Math.round((sw.price-a.wt1)*4)/4:Math.round((sw.price+a.wt1)*4)/4,target:isAccum?Math.round((e+a.wt3)*4)/4:Math.round((e-a.wt3)*4)/4,target2:isAccum?Math.round((e+a.wt5)*4)/4:Math.round((e-a.wt5)*4)/4,qualified:true});lastHL=sw.price;fsPhase=3;hadHHafterBT=false;}
        else if(fsPhase===2&&hadHHafterBT){hadHHafterBT=false;}
      }
    }
  }

  const phaseLabel=wyk_state<=1?"Phase A":wyk_state===2?"Phase B":wyk_state===3?"Phase C":wyk_state===4?"Phase D (DSOW)":wyk_state===5?"Phase D (LPS)":"Phase E";
  return{st:wyk_state,isAccum,scbcV:anchorSCBC||scV,scbcB:scB,arV:anchorAR||arV,arB,stV,stB,rH,rL,b1V,b1B,b2V,b2B,b3V,b3B,cEx,cB,cType,dsowV,dsowB,lpsV,lpsB,dsosV,dsosB,frontside,labels,signals,zigzag:swings,lock2:cEx!==null,phaseLabel,avgBWaveBars,cTempoDeadline,tempoBarsleft:cTempoDeadline?cTempoDeadline-(bars.length-1):null};
}

// ── UI COMPONENTS ──────────────────────────────────────────────
const Sec=({title,col,children})=>(
  <div style={{padding:"8px 10px",borderBottom:`1px solid ${T.border}`}}>
    <div style={{fontSize:9,fontWeight:800,color:col||T.dim,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:6}}>{title}</div>
    {children}
  </div>
);
const Row=({label,val,col,bold})=>(
  <div style={{display:"flex",justifyContent:"space-between",padding:"2px 0",fontSize:11}}>
    <span style={{color:T.dim}}>{label}</span>
    <span style={{color:col||T.white,fontFamily:"monospace",fontWeight:bold?700:400}}>{val}</span>
  </div>
);

// ── PCG D+/S- BIAS TABLE ─────────────────────────────────────────
// Port van PCG_DS_Bias_v4.pine (7-rij tabel) — gevoed door reeds
// berekende state in Platform: vsaSwings (D+/D-/S+/S-), vpaSwingSignals
// (STOP-VOL/TOP-VOL/NO DMD/NO SUP/HAMMER/SHOOT), pandasZones (Supply/
// Demand zone engine van zones_api.py), pcgData.frontside (FS/BS +
// Leave&Return structuur) en transData (Rising/Falling Fair Price).
// Geen losse berekeningen — puur een view-laag op bestaande state,
// zodat de tabel altijd 1:1 matcht met wat de chart al toont.
function computePCGBiasRow({vsaSwings,vpaSwingSignals,pandasZones,pcgData,transData,bars}) {
  // ── D/S classificatie: laatste geclassificeerde swing ──────────
  const lastSwing = vsaSwings && vsaSwings.length ? vsaSwings[vsaSwings.length-1] : null;
  const dsClass   = lastSwing ? lastSwing.vsaClass : "—";
  const dsBullish = dsClass==="D+"||dsClass==="S-";

  // ── VPA: laatste swing-level signaal (STOP-VOL/TOP-VOL/NO DMD/...) ─
  const lastVpa = vpaSwingSignals && vpaSwingSignals.length ? vpaSwingSignals[vpaSwingSignals.length-1] : null;
  const vpaLabel = lastVpa ? lastVpa.label : "—";
  const vpaBull  = lastVpa ? !!lastVpa.isLong : true;

  // ── BIAS SCORE — zelfde optelling als Pine: D/S + VPA ───────────
  let score = 0;
  if (lastSwing) score += dsBullish ? 1 : -1;
  if (lastVpa) score += vpaBull ? 1 : -1;
  const biasIsBull = score > 0;
  const biasIsBear = score < 0;
  const biasLabel  = biasIsBull ? "D+/S-" : biasIsBear ? "S+/D-" : "NEUTRAL";

  // ── CONFLICT — 2+ bearish signalen op een bull bias ─────────────
  const bearVSA = lastSwing && !dsBullish ? 1 : 0;
  const bearVPA = lastVpa && !vpaBull ? 1 : 0;
  const conflict = (bearVSA+bearVPA) >= 2 && biasIsBull;

  // ── MCP — macro swing SC/AR uit pandasZones micro panel, anders
  //         fallback op transData rising/falling fair price ────────
  let mcp = null;
  if (pandasZones && pandasZones.micro && pandasZones.micro.sc>0 && pandasZones.micro.ar>0) {
    mcp = (pandasZones.micro.sc + pandasZones.micro.ar) / 2;
  } else if (pcgData && pcgData.scbcV && pcgData.arV) {
    mcp = (pcgData.scbcV + pcgData.arV) / 2;
  }

  // ── FS/BS — frontside/backside state machine. Bron: pcgData.frontside
  //    (CL→RD→BT→LOL sequentie, zelfde logica als Skill 11 LOL-state) ─
  let fsbsLabel = "NEUTRAL";
  let lolLevel  = null;
  const fs = pcgData && pcgData.frontside;
  const isAccum = pcgData ? pcgData.isAccum : true;
  if (fs) {
    if (fs.lol!==null && fs.lol!==undefined) {
      lolLevel = fs.lol;
      const lastClose = bars && bars.length ? bars[bars.length-1].close : null;
      const lolBroken = lastClose!==null && (isAccum ? lastClose < lolLevel*0.9998 : lastClose > lolLevel*1.0002);
      fsbsLabel = lolBroken ? (isAccum?"→ BACK":"→ FRONT") : "FRONTSIDE";
      if (!isAccum) fsbsLabel = lolBroken ? "→ FRONT" : "BACKSIDE";
    } else if (fs.bt!==null && fs.bt!==undefined) {
      fsbsLabel = isAccum ? "FRONTSIDE" : "BACKSIDE";
    } else if (fs.rd1!==null && fs.rd1!==undefined) {
      fsbsLabel = isAccum ? "→ FRONT" : "→ BACK";
    }
  }
  // Pandas micro bias als secundaire confirmatie wanneer geen frontside struct
  if (fsbsLabel==="NEUTRAL" && pandasZones && pandasZones.micro && pandasZones.micro.bias) {
    const b = pandasZones.micro.bias;
    fsbsLabel = b==="front" ? "FRONTSIDE" : b==="back" ? "BACKSIDE" : "NEUTRAL";
  }

  // ── L&R status — afgeleid van frontside-stappen (CL/RD/BT/LOL) ──
  let lrtTxt = "—", lrtBull = true, lrtWarn = false;
  if (fs) {
    if (fs.lol!==null && fs.lol!==undefined)      { lrtTxt = isAccum?"LOL ✓":"LOL ✓(LH)"; lrtBull = isAccum; }
    else if (fs.bt!==null && fs.bt!==undefined)   { lrtTxt = isAccum?"BT ✓":"BT(LH) ✓";   lrtBull = isAccum; }
    else if (fs.rd1!==null && fs.rd1!==undefined) { lrtTxt = isAccum?"1st RD":"1st FS";    lrtBull = isAccum; }
    else if (fs.cl!==null && fs.cl!==undefined)   { lrtTxt = "CL SET";                     lrtBull = true; lrtWarn = true; }
  }

  // ── Volume referentie — laatste swing-volume gemiddelde ─────────
  let dRef="—", sRef="—";
  if (lastSwing && bars && bars.length) {
    const s=Math.min(lastSwing.fromIdx,lastSwing.toIdx), e=Math.max(lastSwing.fromIdx,lastSwing.toIdx);
    const swingBars = bars.slice(s,e+1);
    const v = swingBars.reduce((acc,b)=>acc+(b.vol||0),0);
    if (dsBullish) dRef = Math.round(v/1000)+"K"; else sRef = Math.round(v/1000)+"K";
  }

  return { dsClass, dsBullish, vpaLabel, vpaBull, biasLabel, biasIsBull, biasIsBear,
    conflict, mcp, fsbsLabel, lrtTxt, lrtBull, lrtWarn, dRef, sRef, lolLevel };
}

function PCGBiasTable({vsaSwings,vpaSwingSignals,pandasZones,pcgData,transData,bars}) {
  const row = useMemo(
    ()=>computePCGBiasRow({vsaSwings,vpaSwingSignals,pandasZones,pcgData,transData,bars}),
    [vsaSwings,vpaSwingSignals,pandasZones,pcgData,transData,bars]
  );

  const BG_BULL="rgba(0,229,160,0.14)", BG_BEAR="rgba(255,63,80,0.14)", BG_WARN="rgba(255,208,96,0.14)", BG_BASE="rgba(255,255,255,0.03)";
  const TC_BULL=T.green, TC_BEAR=T.red, TC_WARN=T.yellow, TC_DIM=T.dim;

  const cellStyle = (bg,fs=11) => ({padding:"4px 8px",fontFamily:"monospace",fontWeight:700,fontSize:fs,background:bg,borderRadius:2});
  const labelStyle = {padding:"4px 8px",fontSize:9,fontWeight:800,color:T.dim,letterSpacing:"0.08em",textTransform:"uppercase"};
  const tr = {display:"grid",gridTemplateColumns:"56px 1fr",alignItems:"center",borderBottom:`1px solid ${T.border}`};

  const biasBG = row.conflict?BG_WARN:row.biasIsBull?BG_BULL:row.biasIsBear?BG_BEAR:BG_BASE;
  const biasTC = row.conflict?TC_WARN:row.biasIsBull?TC_BULL:row.biasIsBear?TC_BEAR:T.white;
  const biasTxt = row.conflict?"CONFLICT":row.biasLabel;

  const dsBG = row.dsClass==="—"?BG_BASE:(row.dsBullish?BG_BULL:BG_BEAR);
  const dsTC = row.dsClass==="—"?TC_DIM:(row.dsBullish?TC_BULL:TC_BEAR);

  const vpaBG = row.vpaLabel==="—"?BG_BASE:(row.vpaBull?BG_BULL:BG_BEAR);
  const vpaTC = row.vpaLabel==="—"?TC_DIM:(row.vpaBull?TC_BULL:TC_BEAR);

  const fsbsBG = row.fsbsLabel==="FRONTSIDE"?BG_BULL:row.fsbsLabel==="BACKSIDE"?BG_BEAR:BG_BASE;
  const fsbsTC = row.fsbsLabel==="FRONTSIDE"?TC_BULL:row.fsbsLabel==="BACKSIDE"?TC_BEAR:TC_DIM;

  const lrtBG = row.lrtTxt==="—"?BG_BASE:row.lrtWarn?BG_WARN:(row.lrtBull?BG_BULL:BG_BEAR);
  const lrtTC = row.lrtTxt==="—"?TC_DIM:row.lrtWarn?TC_WARN:(row.lrtBull?TC_BULL:TC_BEAR);

  return (
    <div style={{border:`1px solid ${T.border}`,borderRadius:3,overflow:"hidden",marginTop:2}}>
      <div style={tr}>
        <div style={labelStyle}>BIAS</div>
        <div style={cellStyle(biasBG,13)}><span style={{color:biasTC}}>{biasTxt}</span></div>
      </div>
      <div style={tr}>
        <div style={labelStyle}>D / S</div>
        <div style={cellStyle(dsBG,13)}><span style={{color:dsTC}}>{row.dsClass}</span></div>
      </div>
      <div style={tr}>
        <div style={labelStyle}>VPA</div>
        <div style={cellStyle(vpaBG)}><span style={{color:vpaTC}}>{row.vpaLabel}</span></div>
      </div>
      <div style={tr}>
        <div style={labelStyle}>MCP</div>
        <div style={cellStyle(BG_BASE)}><span style={{color:T.white}}>{row.mcp!==null?row.mcp.toFixed(2):"—"}</span></div>
      </div>
      <div style={tr}>
        <div style={labelStyle}>FS/BS</div>
        <div style={cellStyle(fsbsBG)}><span style={{color:fsbsTC}}>{row.fsbsLabel}</span></div>
      </div>
      <div style={tr}>
        <div style={labelStyle}>L &amp; R</div>
        <div style={cellStyle(lrtBG)}><span style={{color:lrtTC}}>{row.lrtTxt}</span></div>
      </div>
      <div style={{...tr,borderBottom:"none"}}>
        <div style={labelStyle}>REF</div>
        <div style={cellStyle(BG_BASE,9)}><span style={{color:TC_DIM}}>D {row.dRef}  S {row.sRef}</span></div>
      </div>
    </div>
  );
}
const NumInput=({label,val,onChange,col})=>(
  <div style={{marginBottom:5}}>
    <div style={{fontSize:9,color:"#8aabb8",marginBottom:2}}>{label}</div>
    <input value={val||""} onChange={e=>onChange(e.target.value)}
      style={{width:"100%",background:T.card,border:`1px solid ${T.borderLt}`,color:col||T.white,padding:"4px 7px",borderRadius:2,fontFamily:"monospace",fontSize:11,outline:"none",boxSizing:"border-box"}}/>
  </div>
);

// ── CANDLE CHART ───────────────────────────────────────────────
const _clickBridge={pending:null,handler:null};

function CandleChart({staticBars,liveBar,atr,keyLevel,transData,pcgData,staticMBZ,
  htfMBZ,htfSCBC,htfAR,htfCycle,htfAtrGrid,htfSR,showVWAP,height,onVwapUpdate,
  pcgSignals,pcgContextSignals,pcgVsaSwings,pcgVsaSignals,pcgVpaSignals,
  cycles,chartTrades,fibData,clickMode,onBarClick,onZoom,
  pandasZones,showPandasZones,agentLevels,showAgentLevels,
  marketState,showMarketState}) {

  const svgRef=useRef(null),xBaseRef=useRef(null),yRef=useRef(null);
  const transformRef=useRef(d3.zoomIdentity),layersRef=useRef({});
  const dimsRef=useRef({iW:700,iH:360}),tickingRef=useRef(false);
  const crosshairRef=useRef(null),autoScrollRef=useRef(true),allBarsRef=useRef([]);
  const onBarClickRef=useRef(onBarClick),clickModeRef=useRef(clickMode);
  useEffect(()=>{onBarClickRef.current=onBarClick;},[onBarClick]);
  useEffect(()=>{clickModeRef.current=clickMode;},[clickMode]);
  useEffect(()=>{_clickBridge.handler=onBarClick;},[onBarClick]);
  const atrL=calcATR(atr);
  const allBars=useMemo(()=>[...staticBars,liveBar],[staticBars,liveBar]);
  const n=allBars.length;
  useEffect(()=>{allBarsRef.current=allBars;},[allBars]);
  const cx=(i,xS)=>xS(i)+(xS(1)-xS(0))/2;
  const bw=(xS)=>Math.max(2,(xS(1)-xS(0))*0.6);
  const cr=v=>Math.round(v)+0.5;

  function safeRedraw(xS){if(!tickingRef.current){tickingRef.current=true;requestAnimationFrame(()=>{redraw(xS);tickingRef.current=false;});}}

  function redraw(xS) {
    const{iW,iH}=dimsRef.current,yS=yRef.current,L=layersRef.current;
    if(!yS||!L.candles) return;
    const bars=allBarsRef.current,n=bars.length,w=bw(xS);
    const[dMin,dMax]=xS.domain();
    const vMin=Math.max(0,Math.floor(dMin)-2),vMax=Math.min(n-1,Math.ceil(dMax)+2);
    const vis=bars.slice(vMin,vMax+1);
    if(vis.length>0){yS.domain([d3.min(vis,d=>d.low)-2,d3.max(vis,d=>d.high)+2]);}
    L.grid.selectAll("*").remove();L.yax.selectAll("*").remove();
    yS.ticks(8).forEach(t=>{L.grid.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(t)).attr("y2",yS(t)).attr("stroke",T.border).attr("stroke-dasharray","1,6").attr("opacity",0.6);});
    const[pLo,pHi]=yS.domain();
    L.yax.call(d3.axisRight(yS).tickValues(d3.range(Math.ceil(pLo),Math.floor(pHi)+1,1)).tickFormat(d=>d.toFixed(0)))
      .call(g=>g.select(".domain").remove()).call(g=>g.selectAll("text").attr("fill","#e0eaf5").attr("font-size",10).attr("font-family","monospace")).call(g=>g.selectAll(".tick line").attr("stroke","transparent"));
    L.vwap.selectAll("*").remove();
    if(showVWAP){
        // Dagelijkse VWAP — reset op UTC middernacht per dag
        // Teken alleen de VWAP van de meest recente handelsdag
        const getDay=b=>{const t=b.time instanceof Date?b.time:new Date(b.time);return t.toISOString().slice(0,10);};
        const today=bars.length?getDay(bars[bars.length-1]):"";
        // Vind startindex van de huidige dag
        let dayStart=0;
        for(let i=bars.length-1;i>=0;i--){if(getDay(bars[i])!==today){dayStart=i+1;break;}}
        const todayBars=bars.slice(dayStart);
        if(todayBars.length>0){
          let cTP=0,cV=0;
          const vpts=todayBars.map((b,i)=>{
            const v=b.vol||1;
            cTP+=((b.high+b.low+b.close)/3)*v;
            cV+=v;
            return{i:dayStart+i,v:cV>0?cTP/cV:b.close};
          });
          // VWAP lijn
          L.vwap.append("path").datum(vpts).attr("fill","none").attr("stroke",T.yellow).attr("stroke-width",1.5).attr("stroke-dasharray","4,3").attr("opacity",0.8).attr("d",d3.line().x(d=>cx(d.i,xS)).y(d=>yS(d.v)).curve(d3.curveMonotoneX));
          // VWAP label + waarde rechts
          const lastVwap=vpts[vpts.length-1];
          if(lastVwap){
            if(typeof onVwapUpdate==="function")onVwapUpdate(Math.round(lastVwap.v*4)/4);
            L.vwap.append("text").attr("x",iW-68).attr("y",yS(lastVwap.v)+4).attr("fill",T.yellow).attr("font-size",7).attr("font-family","monospace").attr("font-weight","bold").text(`VWAP ${lastVwap.v.toFixed(2)}`);
          }
        }
      }
    L.levels.selectAll(":not(.atr-grid)").remove();L.rlbls.selectAll(":not(.atr-lbl)").remove();

    if(staticMBZ){[[staticMBZ.top,"#ff8c42","EQH"],[staticMBZ.bot,"#42c8ff","EQL"],[staticMBZ.mid,T.yellow,"sMCP"]].forEach(([p,c,lbl])=>{L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(p)).attr("y2",yS(p)).attr("stroke",c).attr("stroke-width",p===staticMBZ.mid?1.8:1.2).attr("stroke-dasharray",p===staticMBZ.mid?"4,3":"6,3").attr("opacity",0.85);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(p)+4).attr("fill",c).attr("font-size",8).attr("font-family","monospace").attr("font-weight","bold").text(lbl);});}

    {const PCG_ANCHOR=allBarsRef.current[0]?.close||7264.75,PCG_STEP=atrL.so*0.5,[yLo,yHi]=yS.domain();
    const visLevels=[];
    for(let p=PCG_ANCHOR;p>=yLo-PCG_STEP;p=Math.round((p-PCG_STEP)*4)/4){if(p>yHi+PCG_STEP)continue;if(p<yLo-PCG_STEP)break;visLevels.push(p);}
    for(let p=Math.round((PCG_ANCHOR+PCG_STEP)*4)/4;p<=yHi+PCG_STEP;p=Math.round((p+PCG_STEP)*4)/4){if(p<yLo-PCG_STEP)continue;if(p>yHi+PCG_STEP)break;visLevels.push(p);}
    const gridLines=L.levels.selectAll("line.atr-grid").data(visLevels,d=>d);
    gridLines.enter().append("line").attr("class","atr-grid").merge(gridLines).each(function(p){const distFromAnchor=Math.round(Math.abs(p-PCG_ANCHOR)/PCG_STEP);const isAnchor=Math.abs(p-PCG_ANCHOR)<0.1;const isFullATR=distFromAnchor%2===0&&distFromAnchor>0;const col=isAnchor?"rgba(255,208,96,0.9)":isFullATR?"rgba(255,140,66,0.55)":"rgba(255,208,96,0.18)";d3.select(this).attr("x1",0).attr("x2",iW).attr("y1",yS(p)).attr("y2",yS(p)).attr("stroke",col).attr("stroke-width",isAnchor?1.4:isFullATR?0.8:0.4).attr("stroke-dasharray",isAnchor?"0":"4,4").attr("opacity",isAnchor?0.9:isFullATR?0.55:0.3);});
    gridLines.exit().remove();
    const labelLevels=visLevels.filter(p=>{const d=Math.round(Math.abs(p-PCG_ANCHOR)/PCG_STEP);return Math.abs(p-PCG_ANCHOR)<0.1||d%2===0;});
    const gridLabels=L.rlbls.selectAll("text.atr-lbl").data(labelLevels,d=>d);
    gridLabels.enter().append("text").attr("class","atr-lbl").merge(gridLabels).each(function(p){const distFromAnchor=Math.round(Math.abs(p-PCG_ANCHOR)/PCG_STEP);const isAnchor=Math.abs(p-PCG_ANCHOR)<0.1;const mult=distFromAnchor/2;const dir=p>PCG_ANCHOR?"+":"-";const lbl=isAnchor?"PCG 100%":`${dir}${mult}ATR`;d3.select(this).attr("x",iW-68).attr("y",yS(p)+4).attr("fill",isAnchor?"rgba(255,208,96,0.9)":"rgba(255,140,66,0.65)").attr("font-size",7).attr("font-family","monospace").attr("font-weight","bold").text(`${lbl} ${p.toFixed(2)}`);});
    gridLabels.exit().remove();}

    // ── PCG Fibonacci Transients ──────────────────────────────
    if(fibData){
      fibData.levels.forEach(({price,col,lw,label,r})=>{
        const y=yS(price);
        const isMajor=r===0||r===1||r===0.5;
        L.levels.append("line").attr("x1",0).attr("x2",iW)
          .attr("y1",y).attr("y2",y)
          .attr("stroke",col).attr("stroke-width",lw)
          .attr("stroke-dasharray",isMajor?"0":"4,3")
          .attr("opacity",isMajor?0.9:0.55);
        L.rlbls.append("text").attr("x",iW-68).attr("y",y+4)
          .attr("fill",col).attr("font-size",isMajor?8:7)
          .attr("font-family","monospace")
          .attr("font-weight",isMajor?"bold":"normal")
          .text(`F${label} ${price.toFixed(2)}`);
      });
      
    }

    if(htfMBZ){[[htfMBZ.top,"#ff8c42","hEQH"],[htfMBZ.bot,"#42c8ff","hEQL"],[htfMBZ.mid,"#ffd060","hMCP"]].forEach(([p,c,lbl])=>{L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(p)).attr("y2",yS(p)).attr("stroke",c).attr("stroke-width",p===htfMBZ.mid?2.2:1.5).attr("stroke-dasharray",p===htfMBZ.mid?"4,3":"8,4").attr("opacity",0.7);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(p)+4).attr("fill",c).attr("font-size",7).attr("font-family","monospace").attr("font-weight","bold").text(lbl);});}

    if(htfSR){
      if(htfSR.s1){L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(htfSR.s1)).attr("y2",yS(htfSR.s1)).attr("stroke","rgba(0,229,160,0.7)").attr("stroke-width",1.2).attr("stroke-dasharray","6,3").attr("opacity",0.8);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(htfSR.s1)+4).attr("fill","rgba(0,229,160,0.8)").attr("font-size",7).attr("font-family","monospace").text(`S1 ${htfSR.s1.toFixed(2)}`);}
      if(htfSR.s2){L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(htfSR.s2)).attr("y2",yS(htfSR.s2)).attr("stroke","rgba(0,229,160,0.4)").attr("stroke-width",0.8).attr("stroke-dasharray","4,4").attr("opacity",0.6);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(htfSR.s2)+4).attr("fill","rgba(0,229,160,0.5)").attr("font-size",7).attr("font-family","monospace").text(`S2 ${htfSR.s2.toFixed(2)}`);}
      if(htfSR.r1){L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(htfSR.r1)).attr("y2",yS(htfSR.r1)).attr("stroke","rgba(255,63,80,0.7)").attr("stroke-width",1.2).attr("stroke-dasharray","6,3").attr("opacity",0.8);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(htfSR.r1)+4).attr("fill","rgba(255,63,80,0.8)").attr("font-size",7).attr("font-family","monospace").text(`R1 ${htfSR.r1.toFixed(2)}`);}
      if(htfSR.r2){L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(htfSR.r2)).attr("y2",yS(htfSR.r2)).attr("stroke","rgba(255,63,80,0.4)").attr("stroke-width",0.8).attr("stroke-dasharray","4,4").attr("opacity",0.6);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(htfSR.r2)+4).attr("fill","rgba(255,63,80,0.5)").attr("font-size",7).attr("font-family","monospace").text(`R2 ${htfSR.r2.toFixed(2)}`);}
    }

    if(htfSCBC){const col=htfCycle==="acc"?"rgba(200,64,255,0.7)":"rgba(255,96,96,0.7)";L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(htfSCBC)).attr("y2",yS(htfSCBC)).attr("stroke",col).attr("stroke-width",1.5).attr("stroke-dasharray","8,4").attr("opacity",0.8);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(htfSCBC)+4).attr("fill",col).attr("font-size",7).attr("font-family","monospace").attr("font-weight","bold").text(htfCycle==="acc"?"hSC":"hBC");}
    if(htfAR){L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(htfAR)).attr("y2",yS(htfAR)).attr("stroke","rgba(0,188,212,0.7)").attr("stroke-width",1.5).attr("stroke-dasharray","8,4").attr("opacity",0.8);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(htfAR)+4).attr("fill","rgba(0,188,212,0.8)").attr("font-size",7).attr("font-family","monospace").attr("font-weight","bold").text("hAR");}
    if(htfAtrGrid&&htfAtrGrid.length){htfAtrGrid.forEach(({label,price,col,mult})=>{L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(price)).attr("y2",yS(price)).attr("stroke",col).attr("stroke-width",mult>=3.5?1.2:0.7).attr("stroke-dasharray","3,6").attr("opacity",mult>=3.5?0.7:0.4);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(price)+4).attr("fill",col).attr("font-size",6).attr("font-family","monospace").text(`h${label} ${price.toFixed(2)}`);});}

    if(pcgData&&pcgData.rH){[[pcgData.rH,T.accent,"RH"],[pcgData.rL,T.accent,"RL"]].forEach(([p,c,l])=>{if(!p)return;L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(p)).attr("y2",yS(p)).attr("stroke",c).attr("stroke-dasharray","5,4").attr("stroke-width",0.8).attr("opacity",0.6);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(p)+4).attr("fill",c).attr("font-size",8).attr("font-family","monospace").attr("font-weight","bold").text(l);});}
    if(pcgData&&pcgData.cEx){L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(pcgData.cEx)).attr("y2",yS(pcgData.cEx)).attr("stroke",T.purple).attr("stroke-width",1.5).attr("opacity",0.9);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(pcgData.cEx)+4).attr("fill",T.purple).attr("font-size",8).attr("font-family","monospace").attr("font-weight","bold").text(`C${pcgData.cType||""}`);}

    if(keyLevel){const wtLevels=[["1/8","wt1"],["2/8","wt2"],["3/8","wt3"],["4/8","wt4"],["5/8","wt5"],["6/8","wt6"],["7/8","wt7"],["FULL","full"]];wtLevels.forEach(([lbl,k])=>{const pp=keyLevel+atrL[k];L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(pp)).attr("y2",yS(pp)).attr("stroke","rgba(80,220,120,0.6)").attr("stroke-dasharray","2,4").attr("stroke-width",0.8).attr("opacity",0.75);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(pp)+4).attr("fill","rgba(80,220,120,0.7)").attr("font-size",7).attr("font-family","monospace").text(`${lbl} ${pp.toFixed(2)}`);});L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(keyLevel)).attr("y2",yS(keyLevel)).attr("stroke","rgba(80,220,120,0.9)").attr("stroke-width",1.2).attr("stroke-dasharray","1,0");L.rlbls.append("text").attr("x",iW-68).attr("y",yS(keyLevel)+4).attr("fill","rgba(80,220,120,1)").attr("font-size",8).attr("font-family","monospace").attr("font-weight","bold").text(`0 ATR ${keyLevel.toFixed(2)}`);}

    if(transData){const isFront=transData.bias==="front";
      if(isFront){transData.latestRising.forEach((t2,i)=>{if(t2.raw)return;const p=t2.price;L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(p)).attr("y2",yS(p)).attr("stroke","#4488ff").attr("stroke-width",i===0?1.6:0.9).attr("stroke-dasharray",i===0?"0":"6,3").attr("opacity",i===0?0.9:0.5);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(p)+4).attr("fill","#4488ff").attr("font-size",7).attr("font-family","monospace").text(`RD${transData.latestRising.length-i} ${p.toFixed(2)}`);if(t2.hhPrice){L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(t2.hhPrice)).attr("y2",yS(t2.hhPrice)).attr("stroke","rgba(68,136,255,0.25)").attr("stroke-width",0.6).attr("stroke-dasharray","2,6").attr("opacity",i===0?0.6:0.3);}});}
      else{transData.latestFalling.forEach((t2,i)=>{if(t2.raw)return;const p=t2.price;L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(p)).attr("y2",yS(p)).attr("stroke","#ff3f50").attr("stroke-width",i===0?1.6:0.9).attr("stroke-dasharray",i===0?"0":"6,3").attr("opacity",i===0?0.9:0.5);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(p)+4).attr("fill","#ff3f50").attr("font-size",7).attr("font-family","monospace").text(`FS${transData.latestFalling.length-i} ${p.toFixed(2)}`);if(t2.llPrice){L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(t2.llPrice)).attr("y2",yS(t2.llPrice)).attr("stroke","rgba(255,63,80,0.25)").attr("stroke-width",0.6).attr("stroke-dasharray","2,6").attr("opacity",i===0?0.6:0.3);}});}
      if(transData.mom){const p=transData.mom;L.levels.append("line").attr("x1",0).attr("x2",iW).attr("y1",yS(p)).attr("y2",yS(p)).attr("stroke","rgba(220,225,235,0.85)").attr("stroke-width",1.4).attr("stroke-dasharray","5,3").attr("opacity",0.9);L.rlbls.append("text").attr("x",iW-68).attr("y",yS(p)+4).attr("fill","rgba(220,225,235,0.95)").attr("font-size",8).attr("font-family","monospace").attr("font-weight","bold").text(`MCP ${p.toFixed(2)}`);}}

    L.candles.selectAll("*").remove();
    for(let i=vMin;i<=vMax;i++){const b=bars[i];if(!b)continue;const x=cx(i,xS),bull=b.close>=b.open;const col=bull?T.green:T.red,wCol=bull?T.greenDim:T.redDim;L.candles.append("line").attr("x1",cr(x)).attr("x2",cr(x)).attr("y1",cr(yS(b.high))).attr("y2",cr(yS(b.low))).attr("stroke",wCol).attr("stroke-width",1);const bTop=cr(yS(Math.max(b.open,b.close))),bH=Math.max(1,Math.abs(Math.round(yS(b.open))-Math.round(yS(b.close))));L.candles.append("rect").attr("x",cr(x-w/2)).attr("y",bTop).attr("width",Math.round(w)).attr("height",bH).attr("fill",col).attr("shape-rendering","crispEdges");}
    L.badge.selectAll(".pcg-lbl").remove();

    // ── Render all persistent cycles ──────────────────────────
    renderCyclesOnChart({ cycles: cycles||[], xS, yS, iW, bars, cx, L });

    // ── Render Pandas Supply/Demand/HVN zones ─────────────────
    if (showPandasZones && pandasZones) {
      renderPandasZonesOnChart({ zones: pandasZones, yS, iW, L });
      if (showAgentLevels && agentLevels) {
        renderAgentLevelsOnChart({ levels: agentLevels, yS, iW, L });
      }
    }

    // ── Render Market State Agent levels ───────────────────────
    if (showMarketState && marketState) {
      renderMarketStateOnChart({ ms: marketState, yS, iW, L });
    }

    // ── ATR level touch markers ────────────────────────────────
    if(atrL&&atrL.so){const PCG_ANCHOR=allBarsRef.current[0]?.close||7264.75,PCG_STEP=atrL.so*0.5,[yLo,yHi]=yS.domain();
      function checkLevel(lvl){const ly=yS(lvl);const brs=allBarsRef.current;for(let i=Math.max(0,brs.length-300);i<brs.length;i++){const b=brs[i];if(b.high>=lvl&&b.low<=lvl){const bx=cx(i,xS);if(bx<0||bx>iW)continue;L.badge.append("line").attr("class","pcg-lbl").attr("x1",bx-2).attr("x2",bx+2).attr("y1",ly).attr("y2",ly).attr("stroke","rgba(255,208,96,0.5)").attr("stroke-width",2);}}}
      for(let p=PCG_ANCHOR;p>=yLo-PCG_STEP;p=Math.round((p-PCG_STEP)*4)/4){if(p<yLo||p>yHi){if(p<yLo)break;continue;}checkLevel(p);}
      for(let p=Math.round((PCG_ANCHOR+PCG_STEP)*4)/4;p<=yHi+PCG_STEP;p=Math.round((p+PCG_STEP)*4)/4){if(p<yLo||p>yHi){if(p>yHi)break;continue;}checkLevel(p);}
    }

    // ── Frontside/Backside visualization ──────────────────────
    if(transData&&transData.rdLines&&transData.fsLines&&pcgData){
      const isAcc=pcgData.isAccum;
      if(isAcc&&pcgData.frontside){
        const fs=pcgData.frontside,points=[];
        if(fs.cl!==null&&fs.clB!==null)points.push({barIdx:fs.clB,price:fs.cl,label:"CL",col:T.purple,above:false});
        if(fs.rd1!==null&&fs.rd1B!==null)points.push({barIdx:fs.rd1B,price:fs.rd1,label:"1st RD",col:T.green,above:false});
        if(fs.bt!==null&&fs.btB!==null)points.push({barIdx:fs.btB,price:fs.bt,label:"BT",col:T.yellow,above:false});
        if(fs.lol!==null&&fs.lolB!==null)points.push({barIdx:fs.lolB,price:fs.lol,label:"LoL",col:T.orange,above:false});
        let prevX=null,prevY=null;
        points.forEach(pt=>{if(pt.barIdx===undefined||pt.barIdx===null)return;const x=cx(pt.barIdx,xS);if(x<-50||x>iW+50)return;const y=yS(pt.price);if(prevX!==null){L.badge.append("line").attr("class","pcg-lbl").attr("x1",prevX).attr("x2",x).attr("y1",prevY).attr("y2",y).attr("stroke",pt.col).attr("stroke-width",1.2).attr("stroke-dasharray","4,3").attr("opacity",0.6);}L.badge.append("circle").attr("class","pcg-lbl").attr("cx",x).attr("cy",y).attr("r",4).attr("fill",pt.col).attr("opacity",0.9);const tw=pt.label.length*5.5+8;L.badge.append("rect").attr("class","pcg-lbl").attr("x",x-tw/2).attr("y",y+5).attr("width",tw).attr("height",11).attr("rx",2).attr("fill","rgba(0,0,0,0.85)");L.badge.append("text").attr("class","pcg-lbl").attr("x",x).attr("y",y+14).attr("text-anchor","middle").attr("fill",pt.col).attr("font-size",8).attr("font-weight","bold").attr("font-family","monospace").text(pt.label);prevX=x;prevY=y;});
        if(fs.complete&&prevX!==null){L.badge.append("text").attr("class","pcg-lbl").attr("x",prevX).attr("y",(prevY||0)-20).attr("text-anchor","middle").attr("fill",T.lime).attr("font-size",9).attr("font-weight","bold").attr("font-family","monospace").text("▲ EXP");}
      } else if(!isAcc&&pcgData.frontside){
        const fs=pcgData.frontside,points=[];
        if(fs.cl!==null&&fs.clB!==null)points.push({barIdx:fs.clB,price:fs.cl,label:"CL",col:T.purple,above:true});
        if(fs.rd1!==null&&fs.rd1B!==null)points.push({barIdx:fs.rd1B,price:fs.rd1,label:"1st FS",col:T.red,above:true});
        if(fs.bt!==null&&fs.btB!==null)points.push({barIdx:fs.btB,price:fs.bt,label:"BT",col:T.orange,above:true});
        if(fs.lol!==null&&fs.lolB!==null)points.push({barIdx:fs.lolB,price:fs.lol,label:"LoL",col:T.yellow,above:true});
        let prevX=null,prevY=null;
        points.forEach(pt=>{if(pt.barIdx===undefined||pt.barIdx===null)return;const x=cx(pt.barIdx,xS);if(x<-50||x>iW+50)return;const y=yS(pt.price);if(prevX!==null){L.badge.append("line").attr("class","pcg-lbl").attr("x1",prevX).attr("x2",x).attr("y1",prevY).attr("y2",y).attr("stroke",pt.col).attr("stroke-width",1.2).attr("stroke-dasharray","4,3").attr("opacity",0.6);}L.badge.append("circle").attr("class","pcg-lbl").attr("cx",x).attr("cy",y).attr("r",4).attr("fill",pt.col).attr("opacity",0.9);const tw=pt.label.length*5.5+8;L.badge.append("rect").attr("class","pcg-lbl").attr("x",x-tw/2).attr("y",y-16).attr("width",tw).attr("height",11).attr("rx",2).attr("fill","rgba(0,0,0,0.85)");L.badge.append("text").attr("class","pcg-lbl").attr("x",x).attr("y",y-7).attr("text-anchor","middle").attr("fill",pt.col).attr("font-size",8).attr("font-weight","bold").attr("font-family","monospace").text(pt.label);prevX=x;prevY=y;});
        if(fs.complete&&prevX!==null){L.badge.append("text").attr("class","pcg-lbl").attr("x",prevX).attr("y",(prevY||0)+20).attr("text-anchor","middle").attr("fill",T.lime).attr("font-size",9).attr("font-weight","bold").attr("font-family","monospace").text("▼ EXP");}
      }
    }

    if(pcgData&&pcgData.labels){pcgData.labels.forEach(l=>{if(l.barIdx>=n)return;const x=cx(l.barIdx,xS);if(x<-20||x>iW+20)return;const tw=l.text.length*6+10,yp=l.above?yS(l.price)+14:yS(l.price)-6,yb=l.above?yS(l.price)+5:yS(l.price)-16;L.badge.append("rect").attr("class","pcg-lbl").attr("x",x-tw/2).attr("y",yb).attr("width",tw).attr("height",12).attr("rx",2).attr("fill","rgba(0,0,0,0.85)");L.badge.append("text").attr("class","pcg-lbl").attr("x",x).attr("y",yp).attr("text-anchor","middle").attr("fill",l.col).attr("font-size",9).attr("font-weight","bold").attr("font-family","monospace").text(l.text);});}

    if(pcgVsaSwings&&pcgVsaSwings.length){pcgVsaSwings.forEach(sw=>{if(sw.toIdx>=n)return;const x=cx(sw.toIdx,xS);if(x<-20||x>iW+20)return;const barAtEnd=bars[sw.toIdx];if(!barAtEnd)return;const col=sw.vsaClass==="D+"?"#00e5a0":sw.vsaClass==="D-"?"rgba(0,229,160,0.4)":sw.vsaClass==="S+"?"#ff3f50":"rgba(255,63,80,0.4)";const above=sw.vsaClass.startsWith("S");const yPos=above?yS(barAtEnd.high)-14:yS(barAtEnd.low)+14;L.badge.append("text").attr("class","pcg-lbl").attr("x",x).attr("y",yPos).attr("text-anchor","middle").attr("fill",col).attr("font-size",7).attr("font-weight","bold").attr("font-family","monospace").text(sw.vsaClass);});}

    // ── VPA Swing Signal labels ───────────────────────────────
    if(pcgVpaSignals&&pcgVpaSignals.length){pcgVpaSignals.forEach(sig=>{
      if(sig.barIdx>=n)return;
      const x=cx(sig.barIdx,xS);if(x<-30||x>iW+30)return;
      const barAtEnd=bars[sig.barIdx];if(!barAtEnd)return;
      const above=sig.isLong;
      const yAnchor=above?yS(barAtEnd.low):yS(barAtEnd.high);
      const yOff=above?22:-22;
      const tw=sig.label.length*5.5+10;
      L.badge.append("rect").attr("class","pcg-lbl")
        .attr("x",x-tw/2).attr("y",yAnchor+yOff-9).attr("width",tw).attr("height",11).attr("rx",2)
        .attr("fill","rgba(0,0,0,0.88)").attr("opacity",0.95);
      L.badge.append("text").attr("class","pcg-lbl")
        .attr("x",x).attr("y",yAnchor+yOff).attr("text-anchor","middle")
        .attr("fill",sig.col).attr("font-size",7).attr("font-weight","bold")
        .attr("font-family","monospace").text(sig.label);
      // small dot at swing endpoint
      L.badge.append("circle").attr("class","pcg-lbl")
        .attr("cx",x).attr("cy",yAnchor).attr("r",3)
        .attr("fill",sig.col).attr("stroke","#000").attr("stroke-width",0.5).attr("opacity",0.85);
    });}

        if(chartTrades&&chartTrades.length){chartTrades.forEach(tr=>{if(tr.barIdx===undefined||tr.barIdx>=n)return;const b=bars[tr.barIdx],x=cx(tr.barIdx,xS);if(x<-30||x>iW+30||!b)return;const isLong=tr.type==="long",col=isLong?T.green:T.red,sz=Math.max(bw(xS)*1.5,7),ty=isLong?yS(b.low)+sz+4:yS(b.high)-sz-4;if(isLong){L.badge.append("polygon").attr("class","pcg-lbl").attr("points",`${x},${ty-sz} ${x-sz*0.6},${ty+sz*0.3} ${x+sz*0.6},${ty+sz*0.3}`).attr("fill",col).attr("opacity",0.85);}else{L.badge.append("polygon").attr("class","pcg-lbl").attr("points",`${x},${ty+sz} ${x-sz*0.6},${ty-sz*0.3} ${x+sz*0.6},${ty-sz*0.3}`).attr("fill",col).attr("opacity",0.85);}const setupShort=tr.setup.startsWith("Absorption")?"Abs":tr.setup.startsWith("Exhaustion")?"Exh":tr.setup.startsWith("Spring")?"Spr":"●";const tw=setupShort.length*5+6;L.badge.append("rect").attr("class","pcg-lbl").attr("x",x-tw/2).attr("y",isLong?ty+sz*0.3+2:ty-sz*0.3-12).attr("width",tw).attr("height",10).attr("rx",1).attr("fill","rgba(0,0,0,0.8)");L.badge.append("text").attr("class","pcg-lbl").attr("x",x).attr("y",isLong?ty+sz*0.3+9:ty-sz*0.3-4).attr("text-anchor","middle").attr("fill",col).attr("font-size",7).attr("font-weight","bold").attr("font-family","monospace").text(setupShort);if(tr.entry){L.badge.append("line").attr("class","pcg-lbl").attr("x1",x).attr("x2",x+40).attr("y1",yS(tr.entry)).attr("y2",yS(tr.entry)).attr("stroke",col).attr("stroke-width",0.8).attr("opacity",0.5);}if(tr.stop){L.badge.append("line").attr("class","pcg-lbl").attr("x1",x).attr("x2",x+40).attr("y1",yS(tr.stop)).attr("y2",yS(tr.stop)).attr("stroke",T.red).attr("stroke-width",0.5).attr("stroke-dasharray","2,2").attr("opacity",0.4);}});}

    if(pcgVsaSignals&&pcgVsaSignals.length){pcgVsaSignals.slice(-5).forEach(sig=>{if(sig.barIdx>=n)return;const b=bars[sig.barIdx],x=cx(sig.barIdx,xS);if(x<-30||x>iW+30||!b)return;const isLong=sig.vsaClass==="D+→S-",sz=Math.max(bw(xS)*2,10),col=isLong?T.green:T.red,ty=isLong?yS(b.low)+sz*0.5+8:yS(b.high)-sz*0.5-8;if(isLong){L.badge.append("polygon").attr("class","pcg-lbl").attr("points",`${x},${ty-sz*0.8} ${x-sz*0.5},${ty+sz*0.4} ${x+sz*0.5},${ty+sz*0.4}`).attr("fill",col).attr("stroke",col).attr("stroke-width",1).attr("opacity",0.9);}else{L.badge.append("polygon").attr("class","pcg-lbl").attr("points",`${x},${ty+sz*0.8} ${x-sz*0.5},${ty-sz*0.4} ${x+sz*0.5},${ty-sz*0.4}`).attr("fill",col).attr("stroke",col).attr("stroke-width",1).attr("opacity",0.9);}});}

    if(pcgContextSignals){pcgContextSignals.slice(-10).forEach(sig=>{if(sig.barIdx===undefined||sig.barIdx>=n)return;const b=bars[sig.barIdx],x=cx(sig.barIdx,xS);if(x<-30||x>iW+30||!b)return;const isUp=sig.setup.includes("Spring")||sig.setup.includes("SOW")||sig.setup.includes("Absorption"),yPos=isUp?yS(b.low)+18:yS(b.high)-18,sz=6;L.badge.append("polygon").attr("class","pcg-lbl").attr("points",`${x},${yPos-sz} ${x+sz},${yPos} ${x},${yPos+sz} ${x-sz},${yPos}`).attr("fill",sig.col).attr("opacity",0.9);const tw=sig.setup.length*5+8;L.badge.append("rect").attr("class","pcg-lbl").attr("x",x-tw/2).attr("y",isUp?yPos+sz+1:yPos-sz-13).attr("width",tw).attr("height",11).attr("rx",2).attr("fill","rgba(0,0,0,0.8)");L.badge.append("text").attr("class","pcg-lbl").attr("x",x).attr("y",isUp?yPos+sz+9:yPos-sz-4).attr("text-anchor","middle").attr("fill",sig.col).attr("font-size",7).attr("font-weight","bold").attr("font-family","monospace").text(sig.setup);});}

    pcgSignals.slice(-5).forEach(sig=>{if(sig.barIdx>=n)return;const b=bars[sig.barIdx],x=cx(sig.barIdx,xS),sz=Math.max(bw(xS)*2,10);if(x<-30||x>iW+30||!b)return;if(sig.type==="long"){const ty=yS(b.low)+sz*0.5+8,col=sig.qualified?T.green:"rgba(0,229,160,0.4)";L.badge.append("polygon").attr("class","pcg-lbl").attr("points",`${x},${ty-sz*0.8} ${x-sz*0.5},${ty+sz*0.4} ${x+sz*0.5},${ty+sz*0.4}`).attr("fill",col).attr("stroke",T.green).attr("stroke-width",1).attr("opacity",0.95);if(sig.entry)L.badge.append("line").attr("class","pcg-lbl").attr("x1",x).attr("x2",iW).attr("y1",yS(sig.entry)).attr("y2",yS(sig.entry)).attr("stroke",T.green).attr("stroke-dasharray","3,3").attr("stroke-width",0.8).attr("opacity",0.6);if(sig.stop)L.badge.append("line").attr("class","pcg-lbl").attr("x1",x).attr("x2",iW).attr("y1",yS(sig.stop)).attr("y2",yS(sig.stop)).attr("stroke",T.red).attr("stroke-dasharray","2,4").attr("stroke-width",0.7).attr("opacity",0.5);if(sig.target)L.badge.append("line").attr("class","pcg-lbl").attr("x1",x).attr("x2",iW).attr("y1",yS(sig.target)).attr("y2",yS(sig.target)).attr("stroke",T.gold).attr("stroke-dasharray","3,3").attr("stroke-width",0.8).attr("opacity",0.65);if(sig.target2)L.badge.append("line").attr("class","pcg-lbl").attr("x1",x).attr("x2",iW).attr("y1",yS(sig.target2)).attr("y2",yS(sig.target2)).attr("stroke",T.orange).attr("stroke-dasharray","2,4").attr("stroke-width",0.8).attr("opacity",0.5);}});

    L.live.selectAll("*").remove();
    const lb=liveBar,lx=cx(n-1,xS),lBull=lb.close>=lb.open;
    if(lx>-w&&lx<iW+w){L.live.append("line").attr("x1",cr(lx)).attr("x2",cr(lx)).attr("y1",cr(yS(lb.high))).attr("y2",cr(yS(lb.low))).attr("stroke",lBull?T.greenDim:T.redDim).attr("stroke-width",1);const lbH=Math.max(1,Math.abs(Math.round(yS(lb.open))-Math.round(yS(lb.close))));L.live.append("rect").attr("x",cr(lx-w/2)).attr("y",cr(yS(Math.max(lb.open,lb.close)))).attr("width",Math.round(w)).attr("height",lbH).attr("fill",lBull?T.green:T.red).attr("shape-rendering","crispEdges");}
    const lp=lb.close;
    L.live.append("line").attr("x1",0).attr("x2",iW).attr("y1",cr(yS(lp))).attr("y2",cr(yS(lp))).attr("stroke",lBull?T.green:T.red).attr("stroke-dasharray","2,3").attr("stroke-width",0.8).attr("opacity",0.6);
    L.live.append("rect").attr("x",iW-67).attr("y",yS(lp)-8).attr("width",64).attr("height",14).attr("fill",lBull?T.green:T.red).attr("rx",2);
    L.live.append("text").attr("x",iW-63).attr("y",yS(lp)+3).attr("fill",T.bg).attr("font-size",9).attr("font-weight","bold").attr("font-family","monospace").text(lp.toFixed(2));
    L.xax.selectAll("*").remove();
    const step=Math.max(1,Math.floor((vMax-vMin)/6));
    for(let i=vMin;i<=vMax;i+=step){if(i>=bars.length)continue;const px=cx(i,xS);if(px<0||px>iW)continue;L.xax.append("text").attr("x",px).attr("y",16).attr("text-anchor","middle").attr("fill","rgba(180,200,220,0.9)").attr("font-size",9).attr("font-family","monospace").text(d3.timeFormat("%H:%M")(bars[i].time));}
    if(crosshairRef.current) crosshairRef.current.raise();
  }

  useEffect(()=>{
    if(!svgRef.current) return;
    const el=svgRef.current;
    const W=Math.max(400,(el.clientWidth||800)),H=height;
    const M={top:12,right:72,bottom:22,left:0};
    const iW=W-M.right,iH=H-M.top-M.bottom;
    dimsRef.current={iW,iH};
    d3.select(el).selectAll("*").remove();
    const svg=d3.select(el).attr("width",W).attr("height",H).style("overflow","hidden").style("display","block");
    const defs=svg.append("defs");
    defs.append("clipPath").attr("id","chart-clip").append("rect").attr("width",iW+1).attr("height",iH+12).attr("y",-6);
    defs.append("clipPath").attr("id","label-clip").append("rect").attr("x",iW).attr("width",88).attr("height",iH+12).attr("y",-6);
    const root=svg.append("g").attr("transform",`translate(0,${M.top})`);
    const clip="url(#chart-clip)";
    const yS=d3.scaleLinear().domain([5200,5500]).range([iH,0]);
    yRef.current=yS;
    const xBase=d3.scaleLinear().domain([0,1]).range([0,iW]);
    xBaseRef.current=xBase;
    layersRef.current={grid:root.append("g"),yax:root.append("g").attr("transform",`translate(${iW},0)`),levels:root.append("g").attr("clip-path",clip),vwap:root.append("g").attr("clip-path",clip),candles:root.append("g").attr("clip-path",clip),live:root.append("g"),badge:root.append("g").attr("clip-path",clip),rlbls:root.append("g"),xax:root.append("g").attr("transform",`translate(0,${iH})`)};
    root.append("text").attr("x",iW-2).attr("y",-2).attr("text-anchor","end").attr("fill","rgba(180,200,220,0.9)").attr("font-size",9).attr("font-family","monospace").text("scroll=zoom · drag=pan");
    const ch=root.append("g").style("pointer-events","none").style("display","none");
    crosshairRef.current=ch;
    const vL=ch.append("line").attr("y1",0).attr("y2",iH).attr("stroke","rgba(200,220,240,0.45)").attr("stroke-dasharray","3,3").attr("stroke-width",1);
    const hL=ch.append("line").attr("x1",0).attr("x2",iW).attr("stroke","rgba(200,220,240,0.45)").attr("stroke-dasharray","3,3").attr("stroke-width",1);
    const chHighlight=ch.append("rect").attr("y",0).attr("height",iH).attr("fill","rgba(255,255,255,0.03)");
    const pLabel=root.append("g").style("pointer-events","none").style("display","none");
    const pLabelBg=pLabel.append("rect").attr("x",iW-67).attr("width",64).attr("height",14).attr("fill","#2a3f55").attr("rx",2);
    const pLabelTxt=pLabel.append("text").attr("x",iW-63).attr("font-size",9).attr("font-weight","bold").attr("font-family","monospace").attr("fill",T.white);
    const timeLabelBg=pLabel.append("rect").attr("x",iW-67).attr("width",64).attr("height",12).attr("fill","#1a2f45").attr("rx",2);
    const timeLabelTxt=pLabel.append("text").attr("x",iW-63).attr("font-size",8).attr("font-weight","bold").attr("font-family","monospace").attr("fill","rgba(180,220,255,0.9)");
    const ohlcBox=root.append("g").style("pointer-events","none").style("display","none");
    ohlcBox.append("rect").attr("x",4).attr("y",-12).attr("width",300).attr("height",12).attr("fill","rgba(0,0,0,0.85)").attr("rx",2);
    const ohlcTxt=ohlcBox.append("text").attr("x",8).attr("y",-2).attr("font-size",8.5).attr("font-family","monospace");
    d3.select(el).on("mouseenter",()=>{ch.style("display",null);pLabel.style("display",null);ohlcBox.style("display",null);}).on("mouseleave",()=>{ch.style("display","none");pLabel.style("display","none");ohlcBox.style("display","none");})
      .on("mousemove.ch",function(event){
        const bars=allBarsRef.current,[mx,my]=d3.pointer(event,root.node());
        const xS=transformRef.current.rescaleX(xBaseRef.current);
        const idx=Math.round(xS.invert(mx)-0.5);if(idx<0||idx>=bars.length)return;
        const bar=bars[idx],snapX=cx(idx,xS),barW=bw(xS),cY=Math.max(0,Math.min(iH,my));
        let magnetY=cY,magnetPrice=yRef.current.invert(cY);
        if(clickModeRef.current){const barHighY=yRef.current(bar.high),barLowY=yRef.current(bar.low);magnetY=Math.abs(cY-barHighY)<Math.abs(cY-barLowY)?barHighY:barLowY;magnetPrice=Math.abs(cY-barHighY)<Math.abs(cY-barLowY)?bar.high:bar.low;}
        vL.attr("x1",snapX).attr("x2",snapX);hL.attr("y1",magnetY).attr("y2",magnetY);chHighlight.attr("x",snapX-barW/2).attr("width",barW);
        const price=magnetPrice;pLabelBg.attr("y",magnetY-8);pLabelTxt.attr("y",magnetY+3).text(price.toFixed(2));timeLabelBg.attr("y",magnetY+8);timeLabelTxt.attr("y",magnetY+18).text(d3.timeFormat("%H:%M")(bar.time));
        const bull=bar.close>=bar.open;ohlcTxt.attr("fill",bull?T.green:T.red).text(`${d3.timeFormat("%H:%M")(bar.time)}  O:${bar.open.toFixed(2)}  H:${bar.high.toFixed(2)}  L:${bar.low.toFixed(2)}  C:${bar.close.toFixed(2)}  V:${bar.vol.toLocaleString()}`);
        ch.raise();pLabel.raise();ohlcBox.raise();
      });
    const zoom=d3.zoom().scaleExtent([0.8,10]).filter(ev=>!ev.ctrlKey).on("zoom",ev=>{const{transform,sourceEvent}=ev;if(sourceEvent&&sourceEvent.type==="wheel"){const[mx]=d3.pointer(sourceEvent,el);const scale=transform.k,prevK=transformRef.current.k;const newX=mx-(mx-transformRef.current.x)*(scale/prevK);transformRef.current=d3.zoomIdentity.translate(newX,0).scale(scale);}else{transformRef.current=transform;}const xs0=transformRef.current.rescaleX(xBase);autoScrollRef.current=xs0.domain()[1]>=allBarsRef.current.length-5;const xs=transformRef.current.rescaleX(xBase);safeRedraw(xs);if(onZoom)onZoom(xs);});
    d3.select(el).call(zoom);d3.select(el).call(zoom.transform,transformRef.current);
    const bars0=allBarsRef.current,n0=bars0.length;xBase.domain([0,n0]);
    if(transformRef.current===d3.zoomIdentity){const start=Math.max(0,n0-200);transformRef.current=d3.zoomIdentity.translate(-xBase(start),0).scale(1);}
    const xS0=transformRef.current.rescaleX(xBase);redraw(xS0);if(onZoom)onZoom(xS0);
  },[height]);

  useEffect(()=>{onBarClickRef.current=onBarClick;clickModeRef.current=clickMode;});

  useEffect(()=>{
    const xBase=xBaseRef.current;if(!xBase)return;
    const{iW}=dimsRef.current,bars=allBarsRef.current,n=bars.length;
    xBase.domain([0,n]).range([0,iW]);
    if(autoScrollRef.current&&n>0){const k=transformRef.current.k,lastIdx=n-1;const newTx=iW-xBase(lastIdx+1)*k-30;transformRef.current=d3.zoomIdentity.translate(newTx,0).scale(k);}
    const xs=transformRef.current.rescaleX(xBase);safeRedraw(xs);if(onZoom)onZoom(xs);
  },[allBars,showVWAP,atr,keyLevel,transData,pcgData,staticMBZ,pcgSignals,cycles,pandasZones,agentLevels,showAgentLevels,marketState,showMarketState]);

  function handleChartClick(e) {
    if(!clickMode&&!clickModeRef.current&&!onBarClick)return;if(!onBarClick)return;
    const svgEl=svgRef.current;if(!svgEl)return;
    const rect=svgEl.getBoundingClientRect(),M_TOP=12;
    const mx=e.clientX-rect.left,my=e.clientY-rect.top-M_TOP;
    const xS=transformRef.current.rescaleX(xBaseRef.current);
    const bars=allBarsRef.current,idx=Math.round(xS.invert(mx)-0.5);
    if(idx<0||idx>=bars.length)return;
    const bar=bars[idx],yS=yRef.current;if(!yS)return;
    const barHighY=yS(bar.high),barLowY=yS(bar.low);
    const snapPrice=Math.abs(my-barHighY)<Math.abs(my-barLowY)?bar.high:bar.low;
    onBarClick({...bar,close:snapPrice,_clickMode:clickMode,_barIdx:idx});
  }

  return (
    <div style={{position:"relative",width:"100%",height}} onClick={handleChartClick}>
      <svg ref={svgRef} style={{width:"100%",height,display:"block",cursor:clickMode?"cell":"crosshair",shapeRendering:"crispEdges",outline:clickMode?"2px solid rgba(0,212,255,0.5)":"none"}}/>
      {clickMode&&<div style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",cursor:"cell",zIndex:10,background:"rgba(0,212,255,0.02)",pointerEvents:"none"}}/>}
    </div>
  );
}

// ── VOLUME CHART ───────────────────────────────────────────────
function VolumeChart({bars,liveBar,xScaleRef,volDrawRef}) {
  const ref=useRef(null),gRef=useRef(null);
  const all=[...bars.slice(0,-1),liveBar];
  const H=55,iH=H-4;
  function draw(xS){
    if(!gRef.current||!ref.current)return;
    const iW=(ref.current.clientWidth||800)-72;
    const yS=d3.scaleLinear().domain([0,d3.max(all,d=>d.vol)*1.1]).range([iH,0]);
    gRef.current.selectAll("*").remove();const vbw=Math.max(2,(xS(1)-xS(0))*0.6);
    all.forEach((b,i)=>{const x=xS(i)+(xS(1)-xS(0))/2;if(x<-vbw||x>(iW+vbw))return;gRef.current.append("rect").attr("x",Math.round(x-vbw/2)+0.5).attr("y",Math.round(yS(b.vol))).attr("width",Math.round(vbw)).attr("height",Math.max(0,iH-yS(b.vol))).attr("fill",b.close>=b.open?"rgba(0,229,160,0.4)":"rgba(255,63,80,0.4)").attr("shape-rendering","crispEdges");});
  }
  useEffect(()=>{
    if(!ref.current)return;const W=ref.current.clientWidth||800,iW=W-72;
    d3.select(ref.current).selectAll("*").remove();
    const svg=d3.select(ref.current).attr("width",W).attr("height",H).style("overflow","hidden").style("display","block");
    svg.append("defs").append("clipPath").attr("id","vol-clip").append("rect").attr("width",iW).attr("height",H);
    gRef.current=svg.append("g").attr("clip-path","url(#vol-clip)");
    const xBase=d3.scaleLinear().domain([0,all.length]).range([0,iW]);
    draw(xScaleRef&&xScaleRef.current||xBase);if(volDrawRef)volDrawRef.current=draw;
  },[all]);
  return <svg ref={ref} style={{width:"100%",height:H,display:"block"}}/>;
}

const MES_PER_PT=5;
const EMPTY_TRANS={rdLines:[],fsLines:[],latestRising:[],latestFalling:[],mom:null,ctrl:"NO SIGNAL",pivots:{highs:[],lows:[]},bias:"front"};

// ── MAIN PLATFORM ──────────────────────────────────────────────
function Platform({user,onLogout}) {
  const [tf,setTf]=useState("1m");
  const [bars,setBars]=useState(BASE);
  const [fullData,setFullData]=useState([]);
  const [liveBar,setLiveBar]=useState(BASE.length?{...BASE[BASE.length-1]}:{time:new Date(),open:7000,high:7001,low:6999,close:7000,vol:0});
  const [price,setPrice]=useState(BASE[BASE.length-1].close);
  const [prev,setPrev]=useState(BASE[BASE.length-1].close);
  const [feedSrc,setFeedSrc]=useState("loading");
  const [streaming,setStreaming]=useState(false);
  const [streamSpeed,setSpeed]=useState(800);
  const streamIdxRef=useRef(50);
  const [clock,setClock]=useState("");
  const [clickMode,setClickMode]=useState(null);
  const [showVWAP,setShowVWAP]=useState(true);
  const [vwapValue,setVwapValue]=useState(null); // live dagelijkse VWAP waarde
  const [showNano,setShowNano]=useState(false);
  const [chartH,setChartH]=useState(400);
  const containerRef=useRef(null);
  const xScaleRef=useRef(null),volDrawRef=useRef(null);
  const [atrInput,setAtrInput]=useState("63.50");
  const [zzMult,setZzMult]=useState(1.0);
  const [klInput,setKlInput]=useState("");
  const [pivLen,setPivLen]=useState(5);
  const [bias,setBias]=useState("front");
  const [eqHigh,setEqHigh]=useState("");
  const [eqLow,setEqLow]=useState("");
  const [cycleType,setCycleType]=useState("acc");
  const [anchorSCBC,setAnchorSCBC]=useState("");
  const [anchorSCBCTime,setAnchorSCBCTime]=useState("");
  const [anchorAR,setAnchorAR]=useState("");
  const [anchorARTime,setAnchorARTime]=useState("");

  // ── PCG Fibonacci Transients ─────────────────────────────────
  const [fibP1,setFibP1]=useState(()=>localStorage.getItem("pcg_fib_p1")||"");
  const [fibP2,setFibP2]=useState(()=>localStorage.getItem("pcg_fib_p2")||"");

  // ── Persistent Cycle Store (replaces manualCycles) ──────────
  const cycleStore = useCycleStore();
  const [activeCycleId, setActiveCycleId] = useState(null);
  const [manualStep, setManualStep] = useState(null);


  // HTF state — persisted in localStorage
  const LS_HTF="pcg_htf_v1";
  const _htfInit=(()=>{try{return JSON.parse(localStorage.getItem(LS_HTF)||"{}");}catch(e){return{};}})();
  const [htfCycle,setHtfCycleRaw]=useState(_htfInit.cycle||"acc");
  const [htfBias,setHtfBiasRaw]=useState(_htfInit.bias||"front");
  const [htfSCBC,setHtfSCBCRaw]=useState(_htfInit.scbc||"");
  const [htfAR,setHtfARRaw]=useState(_htfInit.ar||"");
  // MBZ anchors: SwingHigh (midden high), AnchorLow (links low voor groene 50%),
  //              AnchorHigh (midden high voor rode 50%), SwingLow (rechts low)
  const [htfSwingHigh,setHtfSwingHighRaw]=useState(_htfInit.swingHigh||"");
  const [htfAnchorLow,setHtfAnchorLowRaw]=useState(_htfInit.anchorLow||"");
  const [htfAnchorHigh,setHtfAnchorHighRaw]=useState(_htfInit.anchorHigh||"");
  const [htfSwingLow,setHtfSwingLowRaw]=useState(_htfInit.swingLow||"");
  const [htfAtr,setHtfAtrRaw]=useState(_htfInit.atr||"63.50");
  const [htfPhases,setHtfPhasesRaw]=useState(_htfInit.phases||{a:false,b:false,c:false,d:false,e:false});
  const [htfNotes,setHtfNotesRaw]=useState(_htfInit.notes||"");
  const _postHtfToAgent = (data) => {
    // POST HTF MBZ context naar agent.py /save-htf endpoint
    const payload = {
      mbz_green:    data.mbz_green    ?? null,
      mbz_momentum: data.mbz_momentum ?? null,
      mbz_red:      data.mbz_red      ?? null,
      bias:         data.bias         ?? "—",
      swing_high:   data.swing_high   ?? null,
      swing_low:    data.swing_low    ?? null,
      updated_at:   new Date().toISOString(),
    };
    fetch("http://192.168.68.53:5173/save-htf", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(() => {}); // silently ignore if agent not running
  };

  const _saveHtf=(patch)=>{try{const cur=JSON.parse(localStorage.getItem(LS_HTF)||"{}");localStorage.setItem(LS_HTF,JSON.stringify({...cur,...patch}));}catch(e){}};
  const setHtfCycle=v=>{setHtfCycleRaw(v);_saveHtf({cycle:v});};
  const setHtfBias=v=>{setHtfBiasRaw(v);_saveHtf({bias:v});};
  const setHtfSCBC=v=>{setHtfSCBCRaw(v);_saveHtf({scbc:v});};
  const setHtfAR=v=>{setHtfARRaw(v);_saveHtf({ar:v});};
  const _syncMbzToAgent=(overrides={})=>{
    // Bereken MBZ vanuit huidige + nieuwe waarden en stuur naar agent
    const sh=parseFloat(overrides.swingHigh??htfSwingHigh)||null;
    const al=parseFloat(overrides.anchorLow??htfAnchorLow)||null;
    const ah=parseFloat(overrides.anchorHigh??htfAnchorHigh)||null;
    const sl=parseFloat(overrides.swingLow??htfSwingLow)||null;
    const green=(al&&sh)?Math.round(((al+sh)/2)*4)/4:null;
    const red=(ah&&sl)?Math.round(((ah+sl)/2)*4)/4:null;
    const mom=(green&&red)?Math.round(((green+red)/2)*4)/4:null;
    _postHtfToAgent({mbz_green:green,mbz_momentum:mom,mbz_red:red,
      bias:htfBias,swing_high:sh,swing_low:sl,vwap:vwapValue});
  };
  const setHtfSwingHigh=v=>{setHtfSwingHighRaw(v);_saveHtf({swingHigh:v});_syncMbzToAgent({swingHigh:v});};
  const setHtfAnchorLow=v=>{setHtfAnchorLowRaw(v);_saveHtf({anchorLow:v});_syncMbzToAgent({anchorLow:v});};
  const setHtfAnchorHigh=v=>{setHtfAnchorHighRaw(v);_saveHtf({anchorHigh:v});_syncMbzToAgent({anchorHigh:v});};
  const setHtfSwingLow=v=>{setHtfSwingLowRaw(v);_saveHtf({swingLow:v});_syncMbzToAgent({swingLow:v});};
  const setHtfAtr=v=>{setHtfAtrRaw(v);_saveHtf({atr:v});};
  const setHtfPhases=fn=>{setHtfPhasesRaw(prev=>{const next=typeof fn==="function"?fn(prev):fn;_saveHtf({phases:next});return next;});};
  const setHtfNotes=v=>{setHtfNotesRaw(v);_saveHtf({notes:v});};
  const [ltfPhases,setLtfPhases]=useState({a:false,b:false,c:false,d:false,e:false});

  // ── Pandas Zone Integration ──────────────────────────────────
  const [showPandasZones, setShowPandasZones] = useState(true);
  const { zones: pandasZones, loading: zonesLoading, error: zonesError, lastUpdate: zonesLastUpdate, refresh: zonesRefresh } = usePandasZones("SP500", showPandasZones);
  const [showAgentLevels, setShowAgentLevels] = useState(true);
  const agentLevels = useAgentLevels(showAgentLevels);
  const [showMarketState, setShowMarketState] = useState(true);
  const { marketState, msError, msLastUpdate, refreshMs } = useMarketState(showMarketState);
  const [chk,setChk]=useState({setup1:null,setup2:null,of:null,forceType:"Supply",effortWR:null,sot:null,spring:null,structural:null,s1:"",s2:"",r1:"",r2:"",poe1:null,poe2:null,poe3:null,poe4:null,swing:null,poex1:null,poex2:null,poex3:null,poex4:null,poex5:null});
  const setC=(key,val)=>setChk(p=>({...p,[key]:val}));

  const atr=parseFloat(atrInput)||63.50;
  const atrL=calcATR(atr);
  const keyLevel=parseFloat(klInput)||null;
  const eqH=parseFloat(eqHigh)||null;
  const eqL=parseFloat(eqLow)||null;
  const staticMBZ=(eqH&&eqL)?{top:Math.max(eqH,eqL),bot:Math.min(eqH,eqL),mid:Math.round(((eqH+eqL)/2)*4)/4}:null;
  const htfAtrVal=parseFloat(htfAtr)||63.50;
  // Legacy refs voor chart rendering compatibiliteit
  const htfEqH=parseFloat(htfSwingHigh)||null;
  const htfEqL=parseFloat(htfSwingLow)||null;
  const htfSCBCVal=parseFloat(htfSCBC)||null;
  const htfARVal=parseFloat(htfAR)||null;
  // MBZ berekening: groen = links low → midden high (50%), rood = midden high → rechts low (50%)
  const _sh=parseFloat(htfSwingHigh)||null;   // midden high (anchor voor beide)
  const _al=parseFloat(htfAnchorLow)||null;   // links low  → groen 50%
  const _ah=parseFloat(htfAnchorHigh)||null;  // midden high → rood 50% (zelfde als _sh vaak)
  const _sl=parseFloat(htfSwingLow)||null;    // rechts low
  const htfMbzGreen=(_al!=null&&_sh!=null)?Math.round(((_al+_sh)/2)*4)/4:null;  // 50% links→midden
  const htfMbzRed=(_ah!=null&&_sl!=null)?Math.round(((_ah+_sl)/2)*4)/4:null;    // 50% midden→rechts
  const htfMbzMomentum=(htfMbzGreen!=null&&htfMbzRed!=null)?Math.round(((htfMbzGreen+htfMbzRed)/2)*4)/4:null;
  // htfMBZ voor chart rendering (gebruik groen+rood als zone boundaries)
  const htfMBZ=(htfMbzGreen!=null&&htfMbzRed!=null)?{
    top:Math.max(htfMbzGreen,htfMbzRed),
    bot:Math.min(htfMbzGreen,htfMbzRed),
    mid:htfMbzMomentum
  }:(htfEqH&&htfEqL)?{top:Math.max(htfEqH,htfEqL),bot:Math.min(htfEqH,htfEqL),mid:Math.round(((htfEqH+htfEqL)/2)*4)/4}:null;
  const htfPhaseLabel=htfPhases.e?"Phase E":htfPhases.d?"Phase D":htfPhases.c?"Phase C":htfPhases.b?"Phase B":htfPhases.a?"Phase A":"—";
  const ltfPhaseLabel=ltfPhases.e?"Phase E":ltfPhases.d?"Phase D":ltfPhases.c?"Phase C":ltfPhases.b?"Phase B":ltfPhases.a?"Phase A":"—";
  const htfAtrGrid=htfEqL?[0.5,1,1.5,2,2.5,3,3.5,4].map(mult=>({label:mult===4?"4× EXH":mult===3?"3×":mult===2?"2×":mult===1?"1×":`${mult}×`,price:Math.round((htfEqL+htfAtrVal*mult)*4)/4,mult,col:mult>=3.5?"#ff3f50":mult>=2.5?"#ff8c42":mult>=1.5?"#ffd060":"#00e5a0"})):[];
  const [trades,setTrades]=useState([]);
  const [botTrades,setBotTrades]=useState([]);
  const loggedRef=useRef(new Set());

  useEffect(()=>{
    setFeedSrc("loading");
    fetchHyperliquid(tf).then(data=>{setFullData(data);setBars(data);setLiveBar({...data[data.length-1]});setPrice(data[data.length-1].close);setPrev(data[data.length-1].close);setFeedSrc("hl");setStreaming(false);})
    .catch(()=>{setFeedSrc("error");setBars(BASE);setLiveBar({...BASE[BASE.length-1]});setPrice(BASE[BASE.length-1].close);setPrev(BASE[BASE.length-1].close);setStreaming(false);});
  },[]);

  useEffect(()=>{const id=setInterval(()=>{const n=new Date(),z=v=>String(v).padStart(2,"0");setClock(`${z(n.getUTCHours())}:${z(n.getUTCMinutes())}:${z(n.getUTCSeconds())} UTC`);},1000);return()=>clearInterval(id);},[]);



  const wsPriceBuffer=useRef(null);
  useEffect(()=>{
    // Inline Hyperliquid WebSocket (vervangt verwijderde subscribeHyperliquidLive)
    let ws=null,alive=true;
    const connect=()=>{
      try{
        ws=new WebSocket("wss://api.hyperliquid.xyz/ws");
        ws.onopen=()=>{ws.send(JSON.stringify({method:"subscribe",subscription:{type:"trades",coin:"xyz:SP500"}}));};
        ws.onmessage=(ev)=>{
          try{const m=JSON.parse(ev.data);
            if(m.channel==="trades"&&Array.isArray(m.data)){
              const last=m.data[m.data.length-1];
              if(last&&last.px)wsPriceBuffer.current=parseFloat(last.px);
            }
          }catch(e){}
        };
        ws.onclose=()=>{if(alive)setTimeout(connect,3000);};
      }catch(e){if(alive)setTimeout(connect,3000);}
    };
    connect();
    const unsub=()=>{alive=false;ws&&ws.close();};
    const flush=setInterval(()=>{const livePrice=wsPriceBuffer.current;if(livePrice===null||livePrice===undefined)return;wsPriceBuffer.current=null;setPrice(old=>{setPrev(old);return livePrice;});setBars(prev=>{if(!prev.length)return prev;const last=prev[prev.length-1];const now=Date.now();const barMs={"1m":60000,"5m":300000,"15m":900000,"1h":3600000}[tf]||60000;if(now-new Date(last.time).getTime()>=barMs){return[...prev,{time:new Date(now),open:livePrice,high:livePrice,low:livePrice,close:livePrice,vol:0}];}else{const updated=[...prev];updated[updated.length-1]={...last,close:livePrice,high:Math.max(last.high,livePrice),low:Math.min(last.low,livePrice)};return updated;}});setLiveBar(prev=>({...prev,close:livePrice,high:Math.max(prev.high||livePrice,livePrice),low:Math.min(prev.low||livePrice,livePrice)}));},500);
    return()=>{unsub();clearInterval(flush);};
  },[]);

  useEffect(()=>{if(!containerRef.current)return;const ro=new ResizeObserver(()=>{const h=containerRef.current.clientHeight;setChartH(Math.max(200,h-55-(showNano?130:0)-22));});ro.observe(containerRef.current);return()=>ro.disconnect();},[showNano]);

  // ── PCG Fibonacci levels ──────────────────────────────────────
  const fibData=useMemo(()=>{
    const p1=parseFloat(fibP1),p2=parseFloat(fibP2);
    if(!p1||!p2||p1===p2)return null;
    const lo=Math.min(p1,p2),hi=Math.max(p1,p2),range=hi-lo;
    // PCG fibonacci ratios from screenshot
    const ratios=[
      {r:0,    col:"#ffd060", lw:1.5, label:"0"},
      {r:0.0625,col:"#00e5a0",lw:0.7, label:"0.0625"},
      {r:0.125, col:"#ffffff",lw:0.6, label:"0.125"},
      {r:0.1875,col:"#00cc66",lw:0.7, label:"0.1875"},
      {r:0.25,  col:"#ffffff",lw:0.6, label:"0.25"},
      {r:0.3125,col:"#00cc66",lw:0.7, label:"0.3125"},
      {r:0.375, col:"#ffffff",lw:0.6, label:"0.375"},
      {r:0.4375,col:"#00cc66",lw:0.7, label:"0.4375"},
      {r:0.5,   col:"#4488ff", lw:1.4, label:"0.5 MCP"},
      {r:0.5625,col:"#ff6644",lw:0.7, label:"0.5625"},
      {r:0.625, col:"#ffffff",lw:0.6, label:"0.625"},
      {r:0.6875,col:"#ff6644",lw:0.7, label:"0.6875"},
      {r:0.75,  col:"#ffffff",lw:0.6, label:"0.75"},
      {r:0.8125,col:"#ff3f50",lw:0.7, label:"0.8125"},
      {r:0.875, col:"#ffffff",lw:0.6, label:"0.875"},
      {r:0.9375,col:"#ff3f50",lw:0.7, label:"0.9375"},
      {r:1,     col:"#ffd060", lw:1.5, label:"1"},
    ];
    return{lo,hi,range,levels:ratios.map(({r,col,lw,label})=>({price:Math.round((lo+range*r)*4)/4,r,col,lw,label}))};
  },[fibP1,fibP2]);

  // Auto-set KL from fibonacci low point
  useEffect(()=>{
    if(fibP1&&fibP2){
      const lo=Math.min(parseFloat(fibP1)||0,parseFloat(fibP2)||0);
      if(lo>0) setKlInput(lo.toFixed(2));
    }
  },[fibP1,fibP2]);

  const transData=useMemo(()=>{if(!bars||!bars.length)return EMPTY_TRANS;const dynPivLen=Math.max(2,Math.round(pivLen*zzMult));return detectTransients(bars,dynPivLen,bias,eqL,eqH);},[bars,pivLen,zzMult,bias,eqL,eqH]);

  const manualCycleOverride=useMemo(()=>{if(!cycleStore.cycles.length)return null;const best=[...cycleStore.cycles].sort((a,b)=>b.points.length-a.points.length)[0];if(!best||best.points.length<2)return null;const pts=Object.fromEntries(best.points.map(p=>[p.step,p]));const get=s=>pts[s]?.price||null;const scV=get('SC')||get('BC'),arV=get('AR');if(!scV||!arV)return null;const STEPS=['SC','AR','ST','B1','B2','B3','C','DSOW','LPS','DSOS','BU'];const present=STEPS.filter(s=>pts[s]);const last=present[present.length-1]||'SC';const phaseMap={SC:'Phase A',AR:'Phase A',ST:'Phase A',B1:'Phase B',B2:'Phase B',B3:'Phase B (B3)',C:'Phase C',DSOW:'Phase D (DSOW)',LPS:'Phase D (LPS)',DSOS:'Phase E',BU:'Phase E (BU)'};return{st:present.length>=7?4:present.length>=5?3:2,isAccum:!pts.BC,scbcV:scV,arV,stV:get('ST'),rH:arV,rL:scV,b1V:get('B1'),b2V:get('B2'),b3V:get('B3'),cEx:get('C'),cType:get('C')?1:0,dsowV:get('DSOW'),lpsV:get('LPS'),dsosV:get('DSOS'),phaseLabel:phaseMap[last]||'Phase B',labels:[],signals:[],zigzag:[],frontside:null,lfsPrice:null,lfsBarIdx:null,hrdPrice:null,hrdBarIdx:null,lock2:!!get('C'),_manual:true,_cycleId:best.id};},[cycleStore.cycles]);
  const pcgDataAuto=useMemo(()=>{if(!bars||!bars.length)return null;const anchor=(anchorSCBC&&anchorAR)?{type:cycleType,scbc:anchorSCBC,ar:anchorAR}:null;return runPCG(bars,atr,anchor,staticMBZ,{scbc:anchorSCBCTime,ar:anchorARTime},zzMult,cycleType);},[bars,atr,cycleType,anchorSCBC,anchorAR,staticMBZ,anchorSCBCTime,anchorARTime,zzMult]);
  const pcgData=useMemo(()=>{if(manualCycleOverride){const autoSigs=pcgDataAuto?.signals||[];return{...manualCycleOverride,signals:autoSigs};}if(pcgDataAuto)return{...pcgDataAuto,_unverified:true};return null;},[manualCycleOverride,pcgDataAuto]);

  useEffect(()=>{if(!pcgData)return;if(pcgData.cEx&&pcgData.cType===1&&!klInput){setKlInput(pcgData.cEx.toFixed(2));}else if(pcgData.scbcV&&!klInput&&!anchorSCBC){setKlInput(pcgData.scbcV.toFixed(2));}},[pcgData]);

  useEffect(()=>{if(!transData||!transData.pivots)return;if(bias==="front"){const highs=transData.pivots.highs;if(highs&&highs.length>0){const highest=highs.reduce((max,h)=>h.price>max?h.price:max,-Infinity);if(highest>-Infinity)setEqHigh(highest.toFixed(2));}}else{const lows=transData.pivots.lows;if(lows&&lows.length>0){const lowest=lows.reduce((min,l)=>l.price<min?l.price:min,Infinity);if(lowest<Infinity)setEqLow(lowest.toFixed(2));}}},[transData.pivots,bias]);

  const pcgSignals=useMemo(()=>(pcgData&&pcgData.signals)||[],[pcgData]);
  const vsaSwings=useMemo(()=>{if(!bars||!bars.length||!pcgData)return[];return classifyVSASwings(bars,pcgData.zigzag||[]);},[bars,pcgData]);
  const vsaSignals=useMemo(()=>{const sigs=[];if(!vsaSwings.length||!bars.length)return sigs;
    const minSwingSize=calcATR(atr).wt2; // minimum 0.25 ATR swing to qualify
    for(let i=1;i<vsaSwings.length;i++){
      const prev=vsaSwings[i-1],curr=vsaSwings[i];
      const prevSize=Math.abs(prev.toPrice-prev.fromPrice);
      const currSize=Math.abs(curr.toPrice-curr.fromPrice);
      if(prevSize<minSwingSize||currSize<minSwingSize)continue; // ignore tiny swings
      // Require the signal bar to close favorably (confirmation)
      const sigBar=bars[curr.toIdx];if(!sigBar)continue;
      if(prev.vsaClass==="D+"&&curr.vsaClass==="S-"){
        // Long: signal bar should close in upper half (buyers winning)
        const closePos=(sigBar.close-sigBar.low)/(sigBar.high-sigBar.low||1);
        if(closePos>0.45) sigs.push({barIdx:curr.toIdx,type:"context",setup:"D+ → S- (Long Setup)",detail:"Demand dominant → supply failing",col:T.green,icon:"▲",vsaClass:"D+→S-"});
      }
      if(prev.vsaClass==="S+"&&curr.vsaClass==="D-"){
        // Short: signal bar should close in lower half (sellers winning)
        const closePos=(sigBar.close-sigBar.low)/(sigBar.high-sigBar.low||1);
        if(closePos<0.55) sigs.push({barIdx:curr.toIdx,type:"context",setup:"S+ → D- (Short Setup)",detail:"Supply dominant → demand failing",col:T.red,icon:"▼",vsaClass:"S+→D-"});
      }
    }
    return sigs;},[vsaSwings,bars,atr]);

  const vpaSwingSignals=useMemo(()=>{
    if(!bars||!bars.length||!pcgData)return[];
    return detectVPASwingSignals(bars,pcgData.zigzag||[],atr);
  },[bars,pcgData,atr]);

  // ── Cycle-store based trade signals ─────────────────────────
  // When a cycle has DSOW + LPS set → generate LPS Long entry signal
  const cycleTradeSignals=useMemo(()=>{
    const sigs=[];
    if(!bars.length||!cycleStore.cycles.length)return sigs;
    const a=calcATR(atr);
    cycleStore.cycles.forEach(cycle=>{
      if(!cycle.points||!cycle.points.length)return;
      const pts=Object.fromEntries(cycle.points.map(p=>[p.step,p]));
      const isAcc=!!(pts.SC||pts.B1); // acc if has SC/B1 points
      if(isAcc){
        // LPS Long: need DSOW + LPS set, and current price near LPS
        if(pts.DSOW&&pts.LPS){
          const lpsPrice=pts.LPS.price;
          const dsowPrice=pts.DSOW.price;
          const lpsBarIdx=pts.LPS.barIdx;
          if(lpsBarIdx===undefined||lpsBarIdx===null)return;
          const entry=Math.round((lpsPrice+0.25)*4)/4;
          const stop=Math.round((lpsPrice-a.wt2)*4)/4;
          const t1=Math.round((entry+a.wt3)*4)/4;
          const t2=Math.round((entry+a.wt5)*4)/4;
          const sigKey=`cycle-lps-${cycle.id}-${lpsBarIdx}`;
          sigs.push({
            type:"long",
            setup:`${cycle.label} — LPS Long`,
            detail:`DSOW ${dsowPrice.toFixed(2)} → LPS ${lpsPrice.toFixed(2)}`,
            entry, stop, t1, t2,
            barIdx:lpsBarIdx,
            col:"#00e5a0",
            icon:"▲",
            qualified:true,
            cycleId:cycle.id,
            sigKey,
          });
        }
        // BU Long: need DSOS set
        if(pts.DSOS&&pts.AR){
          const buTarget=pts.AR.price;
          const dsosBarIdx=pts.DSOS.barIdx;
          if(dsosBarIdx===undefined||dsosBarIdx===null)return;
          const dsosPrice=pts.DSOS.price;
          // BU = pullback to AR level after DSOS
          const currentPrice=bars[bars.length-1]?.close||0;
          const nearAR=pts.AR&&Math.abs(currentPrice-pts.AR.price)<a.wt2;
          if(nearAR){
            const entry=Math.round((currentPrice+0.25)*4)/4;
            const stop=Math.round((currentPrice-a.wt2)*4)/4;
            sigs.push({
              type:"long",
              setup:`${cycle.label} — BU (Back-Up)`,
              detail:`DSOS ${dsosPrice.toFixed(2)}, BU near AR ${pts.AR.price.toFixed(2)}`,
              entry, stop,
              t1:Math.round((entry+a.wt3)*4)/4,
              t2:Math.round((entry+a.wt5)*4)/4,
              barIdx:bars.length-1,
              col:"#00d4ff",
              icon:"▲",
              qualified:true,
              cycleId:cycle.id,
            });
          }
        }
      } else {
        // Distribution: LPS Short
        if(pts.DSOW&&pts.LPS){
          const lpsPrice=pts.LPS.price;
          const lpsBarIdx=pts.LPS.barIdx;
          if(lpsBarIdx===undefined||lpsBarIdx===null)return;
          const entry=Math.round((lpsPrice-0.25)*4)/4;
          const stop=Math.round((lpsPrice+a.wt2)*4)/4;
          sigs.push({
            type:"short",
            setup:`${cycle.label} — LPS Short`,
            detail:`DSOW ${pts.DSOW.price.toFixed(2)} → LPS ${lpsPrice.toFixed(2)}`,
            entry, stop,
            t1:Math.round((entry-a.wt3)*4)/4,
            t2:Math.round((entry-a.wt5)*4)/4,
            barIdx:lpsBarIdx,
            col:"#ff3f50",
            icon:"▼",
            qualified:true,
            cycleId:cycle.id,
          });
        }
      }
    });
    return sigs;
  },[cycleStore.cycles,bars,atr]);

  const [contextSignals,setContextSignals]=useState([]);
  useEffect(()=>{
    const computeSignals=()=>{
      const sigs=[],scbc=parseFloat(anchorSCBC)||null,ar=parseFloat(anchorAR)||null;
      if(!scbc||!ar||!bars.length)return sigs;
      const isAcc=cycleType==="acc",atrV=calcATR(atr);
      const avgVol=bars.slice(-10).reduce((s,b)=>s+(b.vol||0),0)/10;
      const avgRange=bars.slice(-10).reduce((s,b)=>s+(b.high-b.low),0)/10;
      const arBarIdx=pcgData?.arB??null,scbcBarIdx=pcgData?.scbcB??null;
      if(ltfPhases?.d)return sigs;if(!ltfPhases?.b)return sigs;
      const startBar=arBarIdx!==null?arBarIdx+1:scbcBarIdx!==null?scbcBarIdx+1:1;
      for(let i=Math.max(1,startBar);i<bars.length;i++){
        const b=bars[i],pb=bars[i-1];
        if(isAcc){if(b.high>ar&&pb.high<=ar){sigs.push({barIdx:i,type:"context",setup:"B Upthrust (UT)",detail:`High crossed AR ${ar.toFixed(2)}`,col:T.orange,icon:"⚠"});}if(b.low<scbc&&pb.low>=scbc){sigs.push({barIdx:i,type:"context",setup:"Spring",detail:`Low below SC ${scbc.toFixed(2)}`,col:T.purple,icon:"🌱"});}}
        else{if(b.low<ar&&pb.low>=ar){sigs.push({barIdx:i,type:"context",setup:"B SOW",detail:`Low crossed AR ${ar.toFixed(2)}`,col:T.red,icon:"⬇"});}if(b.high>scbc&&pb.high<=scbc){sigs.push({barIdx:i,type:"context",setup:"Upthrust (UT)",detail:`High crossed BC ${scbc.toFixed(2)}`,col:T.orange,icon:"⚠"});}}
        if(b.vol>avgVol*1.5){const range=b.high-b.low;const nearLevel=Math.abs(b.close-ar)<atrV.wt2||Math.abs(b.close-scbc)<atrV.wt2;if(range<avgRange*0.6&&nearLevel){sigs.push({barIdx:i,type:"context",setup:"Possible Absorption",detail:"Compressed + vol near level",col:T.accent,icon:"🧲"});}}
        if(b.vol>avgVol*2){const range=b.high-b.low;const closeNearOpen=Math.abs(b.close-b.open)<range*0.3;if(range>avgRange*1.5&&closeNearOpen){sigs.push({barIdx:i,type:"context",setup:"Possible Exhaustion",detail:"Wide spread, high vol, close≈open",col:T.yellow,icon:"⚡"});}}
      }
      return sigs;
    };
    setContextSignals(computeSignals());
  },[bars.length,anchorSCBC,anchorAR,cycleType,atr,pcgData,ltfPhases.b,ltfPhases.d]);


  useEffect(()=>{
    if(!bars.length)return;const a=calcATR(atr),newT=[];
    const PCG_ANCHOR=bars[0]?.close||7264.75,PCG_STEP=a.so;const keyLevels=[];
    for(let p=PCG_ANCHOR;p>=PCG_ANCHOR-a.so*15;p=Math.round((p-PCG_STEP)*4)/4)keyLevels.push(p);
    for(let p=Math.round((PCG_ANCHOR+PCG_STEP)*4)/4;p<=PCG_ANCHOR+a.so*15;p=Math.round((p+PCG_STEP)*4)/4)keyLevels.push(p);
    // Add dynamic levels around current price so signals work at any price level
    const currentPrice=bars[bars.length-1]?.close||PCG_ANCHOR;
    for(let p=Math.round((currentPrice-a.so*8)*4)/4;p<=currentPrice+a.so*8;p=Math.round((p+PCG_STEP)*4)/4)keyLevels.push(p);
    if(anchorSCBC&&parseFloat(anchorSCBC))keyLevels.push(parseFloat(anchorSCBC));
    if(anchorAR&&parseFloat(anchorAR))keyLevels.push(parseFloat(anchorAR));
    const MIN_GAP=15;let lastSigBar=-MIN_GAP;
    const window20=(i)=>bars.slice(Math.max(0,i-20),i);
    // Simple trend filter: 20-bar EMA direction
    const ema20=(i)=>{const w=bars.slice(Math.max(0,i-20),i+1);const k=2/(w.length+1);return w.reduce((e,b,j)=>j===0?b.close:b.close*k+e*(1-k),bars[Math.max(0,i-20)].close);};
    for(let i=5;i<bars.length;i++){
      const b=bars[i],pb=bars[i-1],ppb=bars[i-2];if(!b||!pb||!ppb)continue;if(i-lastSigBar<MIN_GAP)continue;
      const w=window20(i),avgVol=w.reduce((s,x)=>s+(x.vol||0),0)/w.length||1,avgRange=w.reduce((s,x)=>s+(x.high-x.low),0)/w.length||1,range=b.high-b.low;
      if((b.vol||0)<avgVol*2.0)continue;
      // Trend direction: EMA slope
      const emaNow=ema20(i),emaPrev=ema20(i-3);
      const trendUp=emaNow>emaPrev+0.1,trendDown=emaNow<emaPrev-0.1;
      const nearLvl=keyLevels.reduce((best,lvl)=>{const d=Math.min(Math.abs(b.close-lvl),Math.abs(b.high-lvl),Math.abs(b.low-lvl));return d<best.d?{d,lvl}:best;},{d:Infinity,lvl:null});
      if(nearLvl.d>a.wt2)continue;const lvl=nearLvl.lvl,key=`${i}-${lvl.toFixed(2)}`;if(loggedRef.current.has(key))continue;if(loggedRef.current.has(`done-${i}`))continue;
      let sig=null;
      // Absorption: compressed range + ultra high vol + near level
      // Only trade WITH trend direction
      if(b.vol>avgVol*3.0&&range<avgRange*0.45){
        const isLong=b.close>lvl;
        if(isLong&&!trendDown) sig={type:"long",setup:`Absorption @ ${lvl.toFixed(2)}`,entry:Math.round((b.high+0.25)*4)/4,stop:Math.round((b.low-a.wt1)*4)/4,t1:Math.round((b.high+a.wt3)*4)/4,t2:Math.round((b.high+a.wt5)*4)/4};
        else if(!isLong&&!trendUp) sig={type:"short",setup:`Absorption @ ${lvl.toFixed(2)}`,entry:Math.round((b.low-0.25)*4)/4,stop:Math.round((b.high+a.wt1)*4)/4,t1:Math.round((b.low-a.wt3)*4)/4,t2:Math.round((b.low-a.wt5)*4)/4};
      }
      // Exhaustion: wide range + ultra high vol + close near open (doji/spinning top)
      // Fade the move: only short exhaustion in uptrend, only long in downtrend
      else if(b.vol>avgVol*3.5&&range>avgRange*2.2&&Math.abs(b.close-b.open)<range*0.15){
        const isUp=b.close>=b.open;
        if(isUp&&trendUp) sig={type:"short",setup:`Exhaustion @ ${lvl.toFixed(2)}`,entry:Math.round((b.high+0.25)*4)/4,stop:Math.round((b.high+a.wt2)*4)/4,t1:Math.round((b.high-a.wt3)*4)/4,t2:Math.round((b.high-a.wt5)*4)/4};
        else if(!isUp&&trendDown) sig={type:"long",setup:`Exhaustion @ ${lvl.toFixed(2)}`,entry:Math.round((b.low-0.25)*4)/4,stop:Math.round((b.low-a.wt2)*4)/4,t1:Math.round((b.low+a.wt3)*4)/4,t2:Math.round((b.low+a.wt5)*4)/4};
      }
      else if(transData){
        // Spring @ RD: only long when not in strong downtrend
        if(transData.rdLines){for(const l of transData.rdLines){if(!l.price)continue;if(pb.low<l.price&&b.close>l.price&&b.close>pb.open&&!trendDown){const springKey=`spring-${i}-${l.price.toFixed(2)}`;if(loggedRef.current.has(springKey))continue;sig={type:"long",setup:`Spring @ RD ${l.price.toFixed(2)}`,entry:Math.round((b.close+0.25)*4)/4,stop:Math.round((pb.low-a.wt1)*4)/4,t1:Math.round((b.close+a.wt3)*4)/4,t2:Math.round((b.close+a.wt5)*4)/4};loggedRef.current.add(springKey);break;}}}
        // Test @ FS: only short when not in strong uptrend
        if(transData.fsLines){for(const l of transData.fsLines){if(!l.price)continue;if(pb.high>l.price&&b.close<l.price&&b.close<pb.open&&!trendUp){const testKey=`fstest-${i}-${l.price.toFixed(2)}`;if(loggedRef.current.has(testKey))continue;sig={type:"short",setup:`Test @ FS ${l.price.toFixed(2)}`,entry:Math.round((b.close-0.25)*4)/4,stop:Math.round((pb.high+a.wt1)*4)/4,t1:Math.round((b.close-a.wt3)*4)/4,t2:Math.round((b.close-a.wt5)*4)/4};loggedRef.current.add(testKey);break;}}}
      }
      if(!sig&&vsaSwings&&vsaSwings.length>=2){
        const last2=vsaSwings.slice(-2),prevSw=last2[0],curr=last2[1];
        // WEXH S+→S-: long only when NOT in strong downtrend
        if(prevSw.vsaClass==="S+"&&curr.vsaClass==="S-"&&!curr.isUp){const sPlowPrice=prevSw.toPrice,sMlowPrice=curr.toPrice;if(b.low<=sPlowPrice+a.wt1&&b.close>sPlowPrice&&b.close>b.open&&!trendDown){const wexhKey=`wexh-${i}-${sPlowPrice.toFixed(2)}`;if(!loggedRef.current.has(wexhKey)){const sPlusVol=prevSw.highVol?avgVol*2:avgVol;if((b.vol||0)<sPlusVol*0.8){sig={type:"long",setup:`WEXH S+→S- @ ${sPlowPrice.toFixed(2)}`,entry:Math.round((b.close+0.25)*4)/4,stop:Math.round((Math.min(b.low,sMlowPrice)-a.wt1)*4)/4,t1:Math.round((b.close+a.wt3)*4)/4,t2:Math.round((b.close+a.wt5)*4)/4};loggedRef.current.add(wexhKey);}}}}
        // WEXH D+→D-: short only when NOT in strong uptrend
        if(prevSw.vsaClass==="D+"&&curr.vsaClass==="D-"&&curr.isUp){const dPlusHighPrice=prevSw.toPrice;if(b.high>=dPlusHighPrice-a.wt1&&b.close<dPlusHighPrice&&b.close<b.open&&!trendUp){const wexhKey=`wexhd-${i}-${dPlusHighPrice.toFixed(2)}`;if(!loggedRef.current.has(wexhKey)){sig={type:"short",setup:`WEXH D+→D- @ ${dPlusHighPrice.toFixed(2)}`,entry:Math.round((b.close-0.25)*4)/4,stop:Math.round((Math.max(b.high,dPlusHighPrice)+a.wt1)*4)/4,t1:Math.round((b.close-a.wt3)*4)/4,t2:Math.round((b.close-a.wt5)*4)/4};loggedRef.current.add(wexhKey);}}}
      }
      if(!sig)continue;
      let exitPrice=null,exitReason="";const isLong=sig.type==="long";
      for(let j=i+1;j<Math.min(i+30,bars.length);j++){const fb=bars[j];if(!fb)break;if(isLong){if(sig.t1&&fb.high>=sig.t1){exitPrice=sig.t1;exitReason="T1";break;}if(sig.stop&&fb.low<=sig.stop){exitPrice=sig.stop;exitReason="Stop";break;}}else{if(sig.t1&&fb.low<=sig.t1){exitPrice=sig.t1;exitReason="T1";break;}if(sig.stop&&fb.high>=sig.stop){exitPrice=sig.stop;exitReason="Stop";break;}}}
      if(!exitPrice)continue;
      const pts=isLong?(exitPrice-sig.entry):(sig.entry-exitPrice);
      // Vaste size voor visuele PnL-display op chart markers
      // (echte trade-sizing gebeurt in agent.py)
      const tradeSize=1;
      const pnl=Math.round(pts*tradeSize*MES_PER_PT*100)/100;
      const tm=new Date(b.time),time=tm.getHours().toString().padStart(2,"0")+":"+tm.getMinutes().toString().padStart(2,"0");
      loggedRef.current.add(key);loggedRef.current.add(`done-${i}`);lastSigBar=i;
      newT.push({type:sig.type,entry:sig.entry,exit:exitPrice,pts,pnl,size:tradeSize,setup:sig.setup,exitReason,time,barIdx:i,stop:sig.stop,t1:sig.t1});
    }
    if(newT.length)setTrades(prev=>{const keys=new Set(prev.map(t=>`${t.barIdx}-${t.entry}-${t.setup}-${t.time}`));return[...prev,...newT.filter(t=>!keys.has(`${t.barIdx}-${t.entry}-${t.setup}-${t.time}`))];});
  },[bars.length,liveBar?.close,atr,transData,anchorSCBC,anchorAR,cycleType,bias,vsaSwings]);

  const diff=price-prev,diffCol=diff>=0?T.green:T.red;
  const phase=pcgData?pcgData.phaseLabel:"IDLE";
  const frontComplete=pcgData&&pcgData.frontside&&pcgData.frontside.complete;
  const swingLeg=useMemo(()=>{if(!pcgData||pcgData.st<2)return"IDLE";if(pcgData.st===2){if(!pcgData.b1V)return"ST→B1 LONG";if(!pcgData.b2V)return"B1→B2 SHORT";if(!pcgData.b3V)return"B2→B3 LONG";return pcgData.isAccum?"B3→C LONG (Frontside)":"B3→C SHORT (Backside)";}return pcgData.st===3?"C→DSOW":pcgData.st===4?"DSOW→LPS":pcgData.st===5?"Phase E":"";},[pcgData]);
  const allTrackerTrades=[...trades,...botTrades];
  const closedTrackerTrades=allTrackerTrades.filter(t=>t.exit!=null);
  const wins=closedTrackerTrades.filter(t=>t.pnl>0),losses=closedTrackerTrades.filter(t=>t.pnl<=0);
  const totalPnl=Math.round(closedTrackerTrades.reduce((s,t)=>s+t.pnl,0)*100)/100;
  const winRate=closedTrackerTrades.length?Math.round(wins.length/closedTrackerTrades.length*100):0;
  const avgWin=wins.length?wins.reduce((s,t)=>s+t.pnl,0)/wins.length:0;
  const avgLoss=losses.length?losses.reduce((s,t)=>s+t.pnl,0)/losses.length:0;
  const pf=avgLoss!==0?Math.abs(avgWin/avgLoss):null;

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100vh",background:T.bg,color:T.white,fontFamily:"'Courier New',monospace",fontSize:12,overflow:"hidden"}}>
      <style>{"*{box-sizing:border-box} ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-thumb{background:#1a2535;border-radius:2px} @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}"}</style>

      {/* TOPBAR */}
      <div style={{display:"flex",alignItems:"center",padding:"0 12px",height:42,background:T.panel,borderBottom:`1px solid ${T.border}`,flexShrink:0,gap:10}}>
        <span style={{fontWeight:900,fontSize:14,color:T.accent,letterSpacing:"0.05em"}}>PCG</span>
        <span style={{color:"#8aabb8",fontSize:11}}>SP500</span>
        <span style={{color:"#8aabb8",fontSize:10}}>xyz perp</span>
        <span style={{fontSize:20,fontWeight:900,color:T.white,letterSpacing:"-0.02em"}}>{price.toFixed(2)}</span>
        <span style={{fontSize:11,color:diffCol,fontWeight:700}}>{diff>=0?"+":""}{diff.toFixed(2)}</span>
        <div style={{width:1,height:20,background:T.border,margin:"0 4px"}}/>
        <div style={{display:"flex",alignItems:"center",gap:4}}>
          <span style={{fontSize:9,color:T.dim}}>ATR</span>
          <input value={atrInput} onChange={e=>setAtrInput(e.target.value)} style={{width:52,background:T.card,border:`1px solid ${T.borderLt}`,color:T.yellow,padding:"2px 5px",borderRadius:2,fontFamily:"monospace",fontSize:10,outline:"none"}}/>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4}}>
          <span style={{fontSize:9,color:T.dim}}>KL</span>
          <input value={klInput} onChange={e=>setKlInput(e.target.value)} placeholder="0.00" style={{width:60,background:T.card,border:`1px solid ${T.borderLt}`,color:T.accent,padding:"2px 5px",borderRadius:2,fontFamily:"monospace",fontSize:10,outline:"none"}}/>
          {keyLevel&&<span style={{fontSize:9,color:pcgData&&pcgData.cType===1?T.purple:"#ff40ff"}}>{pcgData&&pcgData.cType===1?"C1":"SC"} {atrL.wt3.toFixed(2)}</span>}
        </div>
        <div style={{width:1,height:20,background:T.border,margin:"0 4px"}}/>
        <div style={{padding:"2px 8px",borderRadius:2,fontSize:9,fontWeight:700,background:"rgba(245,166,35,0.1)",border:"1px solid rgba(245,166,35,0.3)",color:T.gold}}>HTF {htfBias==="front"?"▲F":"▼B"}{htfMbzMomentum?` · MBZ ${htfMbzMomentum.toFixed(0)}`:""}</div>
        {pcgData&&pcgData.st>0&&<div style={{padding:"2px 10px",borderRadius:2,fontWeight:700,fontSize:10,background:phase==="Phase B"?"rgba(245,166,35,0.15)":phase.includes("Phase C")?"rgba(255,64,255,0.15)":phase.includes("Phase D")?"rgba(0,229,160,0.15)":"rgba(128,255,64,0.15)",border:`1px solid ${phase==="Phase B"?T.gold:phase.includes("Phase C")?T.purple:phase.includes("Phase D")?T.green:T.lime}`,color:phase==="Phase B"?T.gold:phase.includes("Phase C")?T.purple:phase.includes("Phase D")?T.green:T.lime}}>{phase}</div>}

        {/* Cycle store indicator */}
        {(cycleStore.cycles.length>0||pcgData)&&(
          <div style={{padding:"2px 7px",borderRadius:2,fontSize:9,fontWeight:700,
            background:manualCycleOverride?"rgba(0,229,160,0.12)":"rgba(245,166,35,0.08)",
            border:`1px solid ${manualCycleOverride?"rgba(0,229,160,0.5)":"rgba(245,166,35,0.4)"}`,
            color:manualCycleOverride?T.green:T.gold,display:"flex",alignItems:"center",gap:4}}>
            <span style={{fontSize:8,fontWeight:900}}>{manualCycleOverride?"MANUAL":"AUTO"}</span>
            {cycleStore.cycles.map(c=>{const lvl=LEVEL_MAP[c.level];return <span key={c.id} style={{width:6,height:6,borderRadius:"50%",background:c.color,display:"inline-block"}} title={`${lvl?.label} ${c.label}`}/>;  })}
            {cycleStore.cycles.length} cycle{cycleStore.cycles.length!==1?"s":""}
          </div>
        )}

        {(()=>{const n=bars.length;const recentTrade=[...trades].reverse().find(t=>t.barIdx>=n-5);const recentVsa=[...vsaSignals].reverse().find(s=>s.barIdx>=n-5);const candidates=[recentTrade&&{type:recentTrade.type,setup:recentTrade.setup,entry:recentTrade.entry,barIdx:recentTrade.barIdx},recentVsa&&{type:recentVsa.vsaClass==="D+→S-"?"long":"short",setup:recentVsa.setup,barIdx:recentVsa.barIdx}].filter(Boolean);if(!candidates.length)return null;const sig=candidates.reduce((a,b)=>((a.barIdx||0)>(b.barIdx||0)?a:b));const isLong=sig.type==="long";const col=isLong?"#00e5a0":"#ff3f50";const label=sig.setup.startsWith("WEXH")?"WEXH":sig.setup.startsWith("Abs")?"ABS":sig.setup.startsWith("Exh")?"EXH":sig.setup.startsWith("Spring")?"SPR":sig.setup.startsWith("Test")?"TEST":sig.setup.includes("D+→S-")?"D+→S-":sig.setup.includes("S+→D-")?"S+→D-":sig.setup.slice(0,6).toUpperCase();return(<div style={{padding:"3px 8px",borderRadius:2,fontSize:10,fontWeight:900,background:isLong?"rgba(0,229,160,0.2)":"rgba(255,63,80,0.2)",border:`2px solid ${col}`,color:col,letterSpacing:"0.05em",display:"flex",alignItems:"center",gap:4,animation:"pulse 1s infinite"}}>{isLong?"▲":"▼"} {label}{sig.entry&&<span style={{fontSize:8,opacity:0.7,fontFamily:"monospace"}}>{sig.entry.toFixed(2)}</span>}</div>);})()}
        {frontComplete&&<div style={{padding:"2px 10px",borderRadius:2,fontWeight:800,fontSize:10,background:"rgba(128,255,64,0.2)",border:`1px solid ${T.lime}`,color:T.lime}}>🧲 MAGNET ACTIVE</div>}

        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
          <div style={{padding:"2px 8px",borderRadius:2,fontSize:9,background:"rgba(0,212,255,0.06)",border:"1px solid rgba(0,212,255,0.2)",color:"#8aabb8"}}>{user.username}</div>
          <button onClick={onLogout} style={{padding:"2px 8px",background:"transparent",border:`1px solid ${T.border}`,color:T.dim,cursor:"pointer",fontFamily:"monospace",fontSize:10,borderRadius:2}}>Logout</button>
          <button onClick={()=>setStreaming(v=>!v)} style={{background:"transparent",border:"none",color:streaming?T.green:T.dim,cursor:"pointer",fontSize:14,padding:0}}>{streaming?"⏸":"▶"}</button>
          <select value={streamSpeed} onChange={e=>setSpeed(Number(e.target.value))} style={{background:T.card,border:`1px solid ${T.border}`,color:"#c8dae8",fontFamily:"monospace",fontSize:10,outline:"none",cursor:"pointer",padding:"2px 4px",borderRadius:2}}>
            <option value={5000}>very slow</option><option value={2000}>slow</option><option value={800}>normal</option><option value={300}>fast</option><option value={100}>faster</option><option value={16}>max</option>
          </select>
          <div style={{display:"flex",gap:2,background:T.card,borderRadius:3,padding:2,border:`1px solid ${T.border}`}}>
            {["1m","5m","15m","1h"].map(t=>(
              <button key={t} onClick={()=>{
                if(t===tf)return;
                setStreaming(false);
                // NOTE: cycles persist — no reset here
                setAnchorSCBC("");setAnchorAR("");setEqHigh("");setEqLow("");setKlInput("");
                setTrades([]);loggedRef.current=new Set();
                setTf(t);setFeedSrc("loading");setBars(BASE);setLiveBar({...BASE[BASE.length-1]});
                fetchYahoo(t).then(data=>{setFullData(data);setBars(data);setLiveBar({...data[data.length-1]});setPrice(data[data.length-1].close);setPrev(data[data.length-1].close);streamIdxRef.current=data.length;setFeedSrc("hl");setStreaming(false);}).catch(()=>{setFeedSrc("error");setFullData(BASE);setBars(BASE);streamIdxRef.current=BASE.length;setStreaming(false);});
              }} style={{padding:"2px 8px",borderRadius:2,cursor:"pointer",fontFamily:"monospace",fontSize:10,fontWeight:700,border:"none",background:tf===t?"rgba(0,212,255,0.2)":"transparent",color:tf===t?T.accent:T.dim}}>{t}</button>
            ))}
          </div>
          <button onClick={()=>setShowVWAP(v=>!v)} style={{padding:"2px 8px",background:showVWAP?"rgba(0,212,255,0.1)":"transparent",border:`1px solid ${showVWAP?T.accent:T.border}`,color:showVWAP?T.accent:T.dim,cursor:"pointer",fontFamily:"monospace",fontSize:10,borderRadius:2}}>VWAP</button>
          <button onClick={()=>setShowNano(v=>!v)} style={{padding:"2px 8px",background:showNano?"rgba(0,212,255,0.1)":"transparent",border:`1px solid ${showNano?"rgba(0,212,255,0.4)":T.border}`,color:showNano?"rgba(0,212,255,0.8)":T.dim,cursor:"pointer",fontFamily:"monospace",fontSize:10,borderRadius:2}}>15s</button>
          <button onClick={()=>{setFeedSrc("loading");fetchYahoo(tf).then(d=>{setFullData(d);setBars(d);setLiveBar({...d[d.length-1]});setPrice(d[d.length-1].close);streamIdxRef.current=d.length;setFeedSrc("hl");setStreaming(false);}).catch(()=>{setFeedSrc("error");});}} style={{padding:"2px 8px",background:"transparent",border:`1px solid ${T.border}`,color:"#8aabb8",cursor:"pointer",fontFamily:"monospace",fontSize:10,borderRadius:2}}>⟳ Refresh</button>
          <div style={{padding:"2px 8px",borderRadius:2,fontSize:9,fontWeight:700,background:T.card,border:"1px solid "+(feedSrc==="hl"?T.green:feedSrc==="loading"?T.yellow:T.red),color:feedSrc==="hl"?T.green:feedSrc==="loading"?T.yellow:T.red}}>{feedSrc==="loading"?"LOADING":feedSrc==="hl"?"LIVE":"SYNTH"}</div>
          <span style={{fontSize:10,color:T.dim}}>{clock}</span>
        </div>
      </div>

      {/* MAIN AREA */}
      <div style={{flex:1,display:"flex",overflow:"hidden"}}>

        {/* HTF SIDEBAR */}
        <div style={{width:195,background:"#080e14",borderRight:`1px solid ${T.border}`,overflowY:"auto",flexShrink:0}}>
          <div style={{padding:"6px 10px",background:"rgba(245,166,35,0.08)",borderBottom:`1px solid ${T.border}`}}>
            <div style={{fontSize:9,fontWeight:900,color:T.gold,letterSpacing:"0.14em"}}>HTF CONTEXT</div>
          </div>
          <Sec title="HTF Bias" col={htfBias==="front"?T.lime:T.red}>
            <div style={{display:"flex",gap:4}}>
              {[["front","▲ FRONT",T.lime],["back","▼ BACK",T.red]].map(([v,l,c])=>(<button key={v} onClick={()=>setHtfBias(v)} style={{flex:1,padding:"4px 0",cursor:"pointer",borderRadius:2,fontFamily:"monospace",fontSize:10,fontWeight:700,background:htfBias===v?`${c}22`:"transparent",border:`1px solid ${htfBias===v?c:T.border}`,color:htfBias===v?c:T.dim}}>{l}</button>))}
            </div>
          </Sec>

          {/* HTF MBZ CALCULATOR */}
          <Sec title="HTF MBZ" col={T.gold}>
            {/* Instrucite */}
            <div style={{fontSize:8,color:T.dim,marginBottom:6,lineHeight:1.5,padding:"3px 0",borderBottom:`1px solid ${T.border}`}}>
              <span style={{color:"#00e5a0"}}>●</span> Swing High: midden high<br/>
              <span style={{color:"#00e5a0"}}>●</span> Anchor Low: links low → 50% groen<br/>
              <span style={{color:"#ff8c42"}}>●</span> Anchor High: midden high → 50% rood<br/>
              <span style={{color:"#ff8c42"}}>●</span> Swing Low: rechts low
            </div>

            {/* Swing High */}
            {(()=>{
              const active=clickMode==="htfSH";
              return(<div style={{display:"flex",alignItems:"center",gap:3,marginBottom:4}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:8,color:"#00e5a0",marginBottom:1,fontWeight:700}}>SWING HIGH</div>
                  <input value={htfSwingHigh} onChange={e=>setHtfSwingHigh(e.target.value)} placeholder="0.00"
                    style={{width:"100%",background:T.card,border:`1px solid ${active?"#00e5a0":T.borderLt}`,color:"#00e5a0",padding:"2px 5px",borderRadius:2,fontFamily:"monospace",fontSize:10,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <button onClick={()=>setClickMode(active?null:"htfSH")}
                  style={{width:22,height:22,borderRadius:2,border:`1px solid ${active?"#00e5a0":T.border}`,cursor:"pointer",fontSize:11,background:active?"rgba(0,229,160,0.25)":"rgba(255,255,255,0.05)",color:active?"#00e5a0":T.dim,flexShrink:0,marginTop:10,display:"flex",alignItems:"center",justifyContent:"center"}}>{active?"●":"⊕"}</button>
              </div>);
            })()}

            {/* Anchor Low */}
            {(()=>{
              const active=clickMode==="htfAL";
              return(<div style={{display:"flex",alignItems:"center",gap:3,marginBottom:4}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:8,color:"#00e5a0",marginBottom:1,fontWeight:700}}>ANCHOR LOW <span style={{color:T.dim,fontWeight:400}}>(links)</span></div>
                  <input value={htfAnchorLow} onChange={e=>setHtfAnchorLow(e.target.value)} placeholder="0.00"
                    style={{width:"100%",background:T.card,border:`1px solid ${active?"#00e5a0":T.borderLt}`,color:"#00e5a0",padding:"2px 5px",borderRadius:2,fontFamily:"monospace",fontSize:10,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <button onClick={()=>setClickMode(active?null:"htfAL")}
                  style={{width:22,height:22,borderRadius:2,border:`1px solid ${active?"#00e5a0":T.border}`,cursor:"pointer",fontSize:11,background:active?"rgba(0,229,160,0.25)":"rgba(255,255,255,0.05)",color:active?"#00e5a0":T.dim,flexShrink:0,marginTop:10,display:"flex",alignItems:"center",justifyContent:"center"}}>{active?"●":"⊕"}</button>
              </div>);
            })()}

            {/* Groene 50% output */}
            {htfMbzGreen!=null&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"3px 6px",borderRadius:2,marginBottom:6,background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.25)"}}>
              <span style={{fontSize:8,color:"#00e5a0",fontWeight:700}}>50% GROEN</span>
              <span style={{fontSize:11,color:"#00e5a0",fontFamily:"monospace",fontWeight:900}}>{htfMbzGreen.toFixed(2)}</span>
            </div>}

            <div style={{height:1,background:T.border,margin:"4px 0"}}/>

            {/* Anchor High */}
            {(()=>{
              const active=clickMode==="htfAH";
              return(<div style={{display:"flex",alignItems:"center",gap:3,marginBottom:4}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:8,color:"#ff8c42",marginBottom:1,fontWeight:700}}>ANCHOR HIGH <span style={{color:T.dim,fontWeight:400}}>(midden)</span></div>
                  <input value={htfAnchorHigh} onChange={e=>setHtfAnchorHigh(e.target.value)} placeholder="0.00"
                    style={{width:"100%",background:T.card,border:`1px solid ${active?"#ff8c42":T.borderLt}`,color:"#ff8c42",padding:"2px 5px",borderRadius:2,fontFamily:"monospace",fontSize:10,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <button onClick={()=>setClickMode(active?null:"htfAH")}
                  style={{width:22,height:22,borderRadius:2,border:`1px solid ${active?"#ff8c42":T.border}`,cursor:"pointer",fontSize:11,background:active?"rgba(255,140,66,0.25)":"rgba(255,255,255,0.05)",color:active?"#ff8c42":T.dim,flexShrink:0,marginTop:10,display:"flex",alignItems:"center",justifyContent:"center"}}>{active?"●":"⊕"}</button>
              </div>);
            })()}

            {/* Swing Low */}
            {(()=>{
              const active=clickMode==="htfSL";
              return(<div style={{display:"flex",alignItems:"center",gap:3,marginBottom:4}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:8,color:"#ff8c42",marginBottom:1,fontWeight:700}}>SWING LOW <span style={{color:T.dim,fontWeight:400}}>(rechts)</span></div>
                  <input value={htfSwingLow} onChange={e=>setHtfSwingLow(e.target.value)} placeholder="0.00"
                    style={{width:"100%",background:T.card,border:`1px solid ${active?"#ff8c42":T.borderLt}`,color:"#ff8c42",padding:"2px 5px",borderRadius:2,fontFamily:"monospace",fontSize:10,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <button onClick={()=>setClickMode(active?null:"htfSL")}
                  style={{width:22,height:22,borderRadius:2,border:`1px solid ${active?"#ff8c42":T.border}`,cursor:"pointer",fontSize:11,background:active?"rgba(255,140,66,0.25)":"rgba(255,255,255,0.05)",color:active?"#ff8c42":T.dim,flexShrink:0,marginTop:10,display:"flex",alignItems:"center",justifyContent:"center"}}>{active?"●":"⊕"}</button>
              </div>);
            })()}

            {/* Rode 50% output */}
            {htfMbzRed!=null&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"3px 6px",borderRadius:2,marginBottom:6,background:"rgba(255,140,66,0.08)",border:"1px solid rgba(255,140,66,0.25)"}}>
              <span style={{fontSize:8,color:"#ff8c42",fontWeight:700}}>50% ROOD</span>
              <span style={{fontSize:11,color:"#ff8c42",fontFamily:"monospace",fontWeight:900}}>{htfMbzRed.toFixed(2)}</span>
            </div>}

            {/* MBZ Momentum output — beide lijnen aanwezig */}
            {htfMbzMomentum!=null&&<div style={{padding:"6px 8px",borderRadius:3,background:"rgba(245,166,35,0.12)",border:"1px solid rgba(245,166,35,0.4)",marginTop:2}}>
              <div style={{fontSize:8,color:T.gold,fontWeight:900,letterSpacing:"0.1em",marginBottom:4}}>◆ MBZ OUTPUT</div>
              <Row label="50% Groen" val={htfMbzGreen.toFixed(2)} col="#00e5a0" bold/>
              <Row label="Momentum" val={htfMbzMomentum.toFixed(2)} col={T.yellow} bold/>
              <Row label="50% Rood"  val={htfMbzRed.toFixed(2)}   col="#ff8c42" bold/>
              <div style={{marginTop:4,fontSize:8,color:T.dim,textAlign:"center"}}>
                {price>Math.max(htfMbzGreen,htfMbzRed)
                  ?<span style={{color:T.lime,fontWeight:700}}>▲ BOVEN MBZ — Bullish</span>
                  :price<Math.min(htfMbzGreen,htfMbzRed)
                  ?<span style={{color:T.red,fontWeight:700}}>▼ ONDER MBZ — Bearish</span>
                  :<span style={{color:T.yellow,fontWeight:700}}>◆ IN MBZ — Geen bias</span>}
              </div>
            </div>}

            {/* Actieve click hint */}
            {["htfSH","htfAL","htfAH","htfSL"].includes(clickMode)&&
              <div style={{marginTop:6,padding:"3px 6px",borderRadius:2,fontSize:8,background:"rgba(0,212,255,0.1)",border:"1px solid rgba(0,212,255,0.4)",color:T.accent,textAlign:"center"}}>
                ⊕ Klik een bar op de chart<br/>
                <span style={{color:T.dim}}>→ {clickMode==="htfSH"?"Swing High":clickMode==="htfAL"?"Anchor Low":clickMode==="htfAH"?"Anchor High":"Swing Low"}</span>
              </div>}

            {/* Clear knop */}
            <button onClick={()=>{setHtfSwingHigh("");setHtfAnchorLow("");setHtfAnchorHigh("");setHtfSwingLow("");setClickMode(null);}}
              style={{width:"100%",marginTop:8,padding:"3px",cursor:"pointer",borderRadius:2,fontFamily:"monospace",fontSize:9,background:"transparent",border:`1px solid ${T.border}`,color:T.dim}}>Clear MBZ</button>
          </Sec>

          <Sec title="HTF ATR Grid" col={T.gold}>
            <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:6}}>
              <span style={{fontSize:9,color:T.dim}}>ATR</span>
              <input value={htfAtr} onChange={e=>setHtfAtr(e.target.value)} style={{width:52,background:T.card,border:`1px solid ${T.borderLt}`,color:T.yellow,padding:"2px 5px",borderRadius:2,fontFamily:"monospace",fontSize:10,outline:"none"}}/>
            </div>
            {htfEqL?(<>{<div style={{fontSize:9,color:T.dim,marginBottom:4}}>From EQL {htfEqL} upward</div>}{htfAtrGrid.map(({label,price,mult,col})=>(<div key={mult} style={{display:"flex",justifyContent:"space-between",padding:"2px 0",fontSize:10,borderBottom:`1px solid ${T.border}`}}><span style={{color:col,fontWeight:mult>=3.5?900:700,width:36}}>{label}</span><span style={{color:col,fontFamily:"monospace",fontWeight:mult>=3.5?900:400}}>{price.toFixed(2)}</span></div>))}<div style={{marginTop:4,fontSize:9,color:T.dim}}>EQL {htfEqL} · ATR {htfAtrVal}</div></>):(<div style={{fontSize:10,color:T.dim}}>Set Swing Low to activate</div>)}
          </Sec>
          <Sec title="HTF Notes" col={T.dim}>
            <textarea value={htfNotes} onChange={e=>setHtfNotes(e.target.value)} placeholder="HTF context, key observations..." style={{width:"100%",background:T.card,border:`1px solid ${T.borderLt}`,color:T.white,padding:6,borderRadius:3,fontFamily:"monospace",fontSize:10,resize:"vertical",minHeight:50,lineHeight:1.6,outline:"none"}}/>
          </Sec>
        </div>

        {/* CHART AREA */}
        <div ref={containerRef} style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:T.bg}}>
          <div style={{flex:1,overflow:"hidden",position:"relative"}}>
            <CandleChart staticBars={bars} liveBar={bars[bars.length-1]||liveBar}
              showVWAP={showVWAP} height={chartH} atr={atr} keyLevel={keyLevel}
              onVwapUpdate={setVwapValue}
              transData={transData} pcgData={pcgData} staticMBZ={staticMBZ}
              htfMBZ={htfMBZ} htfSCBC={htfSCBCVal} htfAR={htfARVal} htfCycle={htfCycle}
              htfAtrGrid={htfAtrGrid}
              htfSR={{s1:parseFloat(chk.s1)||null,s2:parseFloat(chk.s2)||null,r1:parseFloat(chk.r1)||null,r2:parseFloat(chk.r2)||null}}
              fibData={fibData}
              pcgSignals={pcgSignals} pcgContextSignals={contextSignals}
              pcgVsaSwings={vsaSwings} pcgVsaSignals={vsaSignals}
              pcgVpaSignals={vpaSwingSignals}
              cycles={cycleStore.cycles}
              chartTrades={trades}
              clickMode={clickMode||!!manualStep}
              onBarClick={(bar)=>{
                const t=bar.time instanceof Date?bar.time:new Date(bar.time);
                const hh=String(t.getHours()).padStart(2,"0"),mm=String(t.getMinutes()).padStart(2,"0");
                const timeStr=`${hh}:${mm}`;
                const mode=bar._clickMode||clickMode;
                if(mode==="scbc"){setAnchorSCBC(bar.close.toFixed(2));setAnchorSCBCTime(timeStr);setClickMode(null);}
                else if(mode==="fibP1"){const v=bar.close.toFixed(2);setFibP1(v);localStorage.setItem("pcg_fib_p1",v);setClickMode("fibP2");}
                else if(mode==="fibP2"){const v=bar.close.toFixed(2);setFibP2(v);localStorage.setItem("pcg_fib_p2",v);setClickMode(null);}
                else if(mode==="ar"){setAnchorAR(bar.close.toFixed(2));setAnchorARTime(timeStr);setClickMode(null);}
                else if(mode==="htfSH"){setHtfSwingHigh(bar.close.toFixed(2));setClickMode(null);}
                else if(mode==="htfAL"){setHtfAnchorLow(bar.close.toFixed(2));setClickMode(null);}
                else if(mode==="htfAH"){setHtfAnchorHigh(bar.close.toFixed(2));setClickMode(null);}
                else if(mode==="htfSL"){setHtfSwingLow(bar.close.toFixed(2));setClickMode(null);}
                else if(manualStep&&activeCycleId){
                  // ── Persistent cycle store click ──
                  cycleStore.setCyclePoint(activeCycleId,manualStep,bar.close,bar._barIdx??(bars.length-1),timeStr,tf);
                  const idx=CYCLE_STEPS.indexOf(manualStep);
                  setManualStep(CYCLE_STEPS[idx+1]||null);
                }
              }}
              onZoom={xS=>{xScaleRef.current=xS;if(volDrawRef.current)volDrawRef.current(xS);}}
              pandasZones={pandasZones}
              showPandasZones={showPandasZones}
              agentLevels={agentLevels}
              showAgentLevels={showAgentLevels}
              marketState={marketState} showMarketState={showMarketState}/>          </div>
          <div style={{height:55,borderTop:`1px solid ${T.border}`,flexShrink:0}}>
            <VolumeChart bars={bars} liveBar={bars[bars.length-1]||liveBar} xScaleRef={xScaleRef} volDrawRef={volDrawRef}/>
          </div>
          {showNano&&<div style={{height:120,borderTop:"1px solid rgba(0,212,255,0.2)",flexShrink:0,background:"rgba(0,212,255,0.02)",display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{color:"#8aabb8",fontSize:11,fontFamily:"monospace"}}>NANO 15s · {swingLeg} · {bars.length*4} synthetic bars</span></div>}
        </div>

        {/* RIGHT PANEL */}
        <div style={{display:"flex",borderLeft:`1px solid ${T.border}`,flexShrink:0}}>

        {/* AGENT DATA SIDEBAR */}
        <div style={{width:195,background:T.panel,borderRight:`1px solid ${T.border}`,overflowY:"auto",flexShrink:0}}>
          <div style={{padding:"6px 10px",background:"rgba(192,132,252,0.06)",borderBottom:`1px solid ${T.border}`}}>
            <div style={{fontSize:9,fontWeight:900,color:"#c084fc",letterSpacing:"0.14em"}}>AGENT DATA</div>
          </div>

          {/* ── PANDAS ZONES PANEL ──────────────────────────────── */}
          <Sec title="Pandas Zones" col="#c084fc">
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <button onClick={()=>setShowPandasZones(v=>!v)}
                  style={{padding:"2px 8px",borderRadius:2,cursor:"pointer",fontFamily:"monospace",
                    fontSize:8,fontWeight:700,border:`1px solid ${showPandasZones?"#c084fc":"#1a2535"}`,
                    background:showPandasZones?"rgba(192,132,252,0.15)":"transparent",
                    color:showPandasZones?"#c084fc":T.dim}}>
                  {showPandasZones?"ON":"OFF"}
                </button>
                {zonesLoading&&<span style={{fontSize:8,color:T.dim}}>↻</span>}
                {zonesError&&<span style={{fontSize:7,color:T.red}} title={zonesError}>⚠</span>}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:4}}>
                {zonesLastUpdate&&<span style={{fontSize:7,color:T.dim}}>{zonesLastUpdate.toLocaleTimeString("nl-NL",{hour:"2-digit",minute:"2-digit"})}</span>}
                <button onClick={zonesRefresh}
                  style={{padding:"1px 5px",borderRadius:2,cursor:"pointer",fontFamily:"monospace",
                    fontSize:7,border:`1px solid ${T.border}`,background:"transparent",color:T.dim}}>↺</button>
              </div>
            </div>
            {!showPandasZones&&<div style={{fontSize:9,color:T.dim,textAlign:"center",padding:"4px 0"}}>Zones verborgen</div>}
            {showPandasZones&&!pandasZones&&!zonesLoading&&(
              <div style={{fontSize:8,color:T.dim,padding:"6px 0",lineHeight:1.6}}>
                <span style={{color:T.red}}>⚠ API offline</span><br/>
                Start: <span style={{color:T.accent,fontFamily:"monospace"}}>uvicorn zones_api:app --port 8001</span>
              </div>
            )}
            {showPandasZones&&pandasZones&&(<>
              <div style={{marginBottom:6,padding:"4px 6px",borderRadius:2,background:"rgba(192,132,252,0.06)",border:"1px solid rgba(192,132,252,0.2)"}}>
                <div style={{fontSize:8,color:"#c084fc",fontWeight:900,letterSpacing:"0.1em",marginBottom:4}}>MICRO 1m — {pandasZones.micro.phase}</div>
                <Row label="SC" val={pandasZones.micro.sc>0?pandasZones.micro.sc.toFixed(2):"—"} col="#ff40ff"/>
                <Row label="AR" val={pandasZones.micro.ar>0?pandasZones.micro.ar.toFixed(2):"—"} col="#00bcd4"/>
                <Row label="pMCP" val={pandasZones.micro.mcp>0?pandasZones.micro.mcp.toFixed(2):"—"} col="#c084fc" bold/>
                <Row label="Bias" val={pandasZones.micro.bias} col={pandasZones.micro.bias==="front"?T.green:pandasZones.micro.bias==="back"?T.red:T.dim}/>
                <div style={{fontSize:7,color:T.dim,marginTop:3,lineHeight:1.4}}>{pandasZones.micro.phase_detail.slice(0,70)}</div>
              </div>
              {pandasZones.supply.length>0&&(<>
                <div style={{fontSize:8,color:T.red,fontWeight:700,marginBottom:3}}>▼ Supply zones</div>
                {pandasZones.supply.map((z,i)=>(<div key={i} style={{display:"flex",justifyContent:"space-between",padding:"2px 0",borderBottom:`1px solid ${T.border}`,fontSize:8}}><span style={{color:T.dim,width:20}}>{z.timeframe}</span><span style={{fontFamily:"monospace",color:T.red}}>{z.bottom.toFixed(2)}–{z.top.toFixed(2)}</span><span style={{color:T.dim,fontSize:7}}>{z.vol_ratio.toFixed(1)}x</span><span style={{color:T.dim,fontSize:7}}>+{z.dist_pts.toFixed(1)}pt</span></div>))}
              </>)}
              {pandasZones.demand.length>0&&(<>
                <div style={{fontSize:8,color:T.green,fontWeight:700,margin:"5px 0 3px"}}>▲ Demand zones</div>
                {pandasZones.demand.map((z,i)=>(<div key={i} style={{display:"flex",justifyContent:"space-between",padding:"2px 0",borderBottom:`1px solid ${T.border}`,fontSize:8}}><span style={{color:T.dim,width:20}}>{z.timeframe}</span><span style={{fontFamily:"monospace",color:T.green}}>{z.bottom.toFixed(2)}–{z.top.toFixed(2)}</span><span style={{color:T.dim,fontSize:7}}>{z.vol_ratio.toFixed(1)}x</span><span style={{color:T.dim,fontSize:7}}>-{z.dist_pts.toFixed(1)}pt</span></div>))}
              </>)}
              {pandasZones.hvn.length>0&&(<>
                <div style={{fontSize:8,color:T.yellow,fontWeight:700,margin:"5px 0 3px"}}>◈ HVN (1H magneten)</div>
                {pandasZones.hvn.map((n,i)=>(<div key={i} style={{display:"flex",justifyContent:"space-between",padding:"2px 0",borderBottom:`1px solid ${T.border}`,fontSize:8}}><span style={{fontFamily:"monospace",color:T.yellow}}>{n.price.toFixed(2)}</span><span style={{color:T.dim,fontSize:7}}>{n.dist_pts>0?"+":""}{n.dist_pts.toFixed(1)}pt</span></div>))}
              </>)}
              <div style={{marginTop:5,fontSize:7,color:"#4a6070",textAlign:"right"}}>ATR 1m: {pandasZones.atr_1m.toFixed(2)} · {pandasZones.calc_ms}ms</div>
            </>)}
          </Sec>

          {/* ── MARKET STATE AGENT PANEL ─────────────────────── */}
          <Sec title="Market State Agent" col="#00d4ff">
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <button onClick={()=>setShowMarketState(v=>!v)}
                  style={{padding:"2px 8px",borderRadius:2,cursor:"pointer",fontFamily:"monospace",
                    fontSize:8,fontWeight:700,border:`1px solid ${showMarketState?"#00d4ff":"#1a2535"}`,
                    background:showMarketState?"rgba(0,212,255,0.15)":"transparent",
                    color:showMarketState?"#00d4ff":"#8aabb8"}}>
                  {showMarketState?"ON":"OFF"}
                </button>
                {msError&&<span style={{fontSize:7,color:"#ff3f50"}} title={msError}>⚠</span>}
              </div>
              {msLastUpdate&&<span style={{fontSize:7,color:"#8aabb8"}}>{msLastUpdate.toLocaleTimeString("nl-NL",{hour:"2-digit",minute:"2-digit"})}</span>}
            </div>
            {showMarketState&&<MarketStatePanel marketState={marketState} msError={msError} msLastUpdate={msLastUpdate} refreshMs={refreshMs}/>}
            {!showMarketState&&<div style={{fontSize:9,color:"#8aabb8",textAlign:"center",padding:"4px 0"}}>Agent verborgen</div>}
          </Sec>

          {/* ── PCG D+/S- BIAS TABLE — port van PCG_DS_Bias_v4.pine ─ */}
          <Sec title="PCG D+/S- Bias" col="#00d4ff">
            <PCGBiasTable
              vsaSwings={vsaSwings}
              vpaSwingSignals={vpaSwingSignals}
              pandasZones={pandasZones}
              pcgData={pcgData}
              transData={transData}
              bars={bars}
            />
          </Sec>
        </div>

        {/* SIGNALS SIDEBAR */}
        <div style={{width:190,background:T.panel,borderLeft:`1px solid ${T.border}`,overflowY:"auto",flexShrink:0}}>
          <Sec title="PCG Signals" col={T.green}>
            {cycleTradeSignals.length>0&&<div style={{padding:"4px 6px",marginBottom:4,borderRadius:2,background:"rgba(192,132,252,0.1)",border:"1px solid rgba(192,132,252,0.4)"}}>
              <div style={{fontSize:8,color:"#c084fc",fontWeight:900,letterSpacing:"0.1em",marginBottom:4}}>◆ CYCLE SETUPS</div>
              {cycleTradeSignals.map((s,i)=>(
                <div key={i} style={{padding:"4px 0",borderBottom:`1px solid ${T.border}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:2}}>
                    <span style={{fontSize:10,fontWeight:900,color:s.col}}>{s.icon} {s.type==="long"?"LONG":"SHORT"}</span>
                    <span style={{fontSize:8,color:"#c084fc"}}>{s.setup.split("—")[0].trim()}</span>
                  </div>
                  <div style={{fontSize:8,color:T.dim,marginBottom:2}}>{s.detail}</div>
                  {s.entry&&<div style={{fontSize:9,color:s.col}}>Entry {s.entry.toFixed(2)}</div>}
                  {s.stop&&<div style={{fontSize:9,color:T.red}}>Stop {s.stop.toFixed(2)}</div>}
                  {s.t1&&<div style={{fontSize:9,color:T.gold}}>T1 {s.t1.toFixed(2)}</div>}
                  {s.t2&&<div style={{fontSize:9,color:T.orange}}>T2 {s.t2.toFixed(2)}</div>}
                </div>
              ))}
            </div>}
            {contextSignals.slice(-5).reverse().map((s,i)=>(<div key={i} style={{padding:"4px 0",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"flex-start",gap:5}}><span style={{fontSize:12,flexShrink:0}}>{s.icon}</span><div><div style={{fontSize:10,fontWeight:800,color:s.col}}>{s.setup}</div><div style={{fontSize:8,color:T.dim,marginTop:1}}>{s.detail}</div></div></div>))}
            {vsaSignals.slice(-3).map((s,i)=>(<div key={`vsa${i}`} style={{padding:"4px 0",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"flex-start",gap:5}}><span style={{fontSize:12,flexShrink:0,color:s.col}}>{s.icon}</span><div><div style={{fontSize:10,fontWeight:800,color:s.col}}>{s.setup}</div><div style={{fontSize:8,color:T.dim,marginTop:1}}>{s.detail}</div></div></div>))}
            {vpaSwingSignals.slice(-4).reverse().map((s,i)=>(
              <div key={`vpa${i}`} style={{padding:"4px 0",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"flex-start",gap:5}}>
                <span style={{fontSize:10,flexShrink:0,color:s.col,fontWeight:900}}>{s.isLong?"▲":"▼"}</span>
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:4}}>
                    <span style={{fontSize:9,fontWeight:900,padding:"1px 4px",borderRadius:2,background:`${s.col}20`,color:s.col,fontFamily:"monospace"}}>{s.label}</span>
                    <span style={{fontSize:8,color:T.dim}}>{s.isLong?"LONG":"SHORT"}</span>
                  </div>
                  <div style={{fontSize:8,color:T.dim,marginTop:1}}>{s.detail}</div>
                </div>
              </div>
            ))}
            {pcgSignals.length===0&&contextSignals.length===0&&vsaSignals.length===0&&vpaSwingSignals.length===0?(<div style={{fontSize:11,color:T.dim}}>Waiting for setup...</div>):pcgSignals.slice(-3).map((s,i)=>{const isFixed=s.setup.startsWith("C —")||s.setup.startsWith("C2"),isFront=s.setup.startsWith("BT")||s.setup.startsWith("LOL"),ruleLabel=isFixed?"Exit: +4pt fixed":isFront?"Exit: ATR 3/8→5/8":"Exit: ATR exhaust";return(<div key={i} style={{padding:"6px 0",borderBottom:`1px solid ${T.border}`}}><div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}><span style={{fontSize:12,fontWeight:800,color:s.type==="long"?T.green:T.red}}>{s.type==="long"?"▲ LONG":"▼ SHORT"}</span><span style={{fontSize:9,color:T.dim}}>{s.setup}</span></div>{s.entry&&<div style={{fontSize:10,color:s.type==="long"?T.green:T.red}}>Entry  {s.entry.toFixed(2)}</div>}{s.stop&&<div style={{fontSize:10,color:T.red}}>Stop   {s.stop.toFixed(2)}</div>}{s.target&&<div style={{fontSize:10,color:T.gold}}>{isFixed?"Target":"T1 (WT5)"} {s.target.toFixed(2)}</div>}{s.target2&&<div style={{fontSize:10,color:T.orange}}>T2 (SO) {s.target2.toFixed(2)}</div>}<div style={{fontSize:9,color:isFixed?T.yellow:isFront?T.accent:T.green,marginTop:2,fontStyle:"italic"}}>{ruleLabel}</div></div>);})}
          </Sec>

          <Sec title="ATR Weak Tests" col={T.yellow}>
            {keyLevel?(<><div style={{display:"grid",gridTemplateColumns:"40px 1fr 1fr",padding:"3px 0",borderBottom:`1px solid ${T.border}`,fontSize:9,color:"#8aabb8",fontWeight:700}}><span>Level</span><span>Above</span><span>Below</span></div>{[["1/8","wt1","rgba(180,180,255,1)"],["2/8","wt2","rgba(140,200,255,1)"],["3/8","wt3",T.accent],["4/8","wt4",T.yellow],["5/8","wt5",T.yellow],["6/8","wt6",T.orange],["7/8","wt7",T.orange],["FULL","full",T.red]].map(([l,k,col])=>(<div key={l} style={{display:"grid",gridTemplateColumns:"40px 1fr 1fr",padding:"3px 0",borderBottom:`1px solid ${T.border}`,fontSize:10}}><span style={{color:col,fontWeight:700}}>{l}</span><span style={{color:col,fontFamily:"monospace"}}>{(keyLevel+atrL[k]).toFixed(2)}</span><span style={{color:col,fontFamily:"monospace"}}>{(keyLevel-atrL[k]).toFixed(2)}</span></div>))}</>):(<div style={{fontSize:11,color:T.dim}}>Set KL in topbar</div>)}
          </Sec>

          <Sec title="Control" col={T.dim}>
            <div style={{padding:"8px",borderRadius:3,marginBottom:6,textAlign:"center",fontWeight:800,fontSize:12,letterSpacing:"0.08em",background:transData.ctrl==="DEMAND > SUPPLY"?"rgba(0,229,160,0.12)":"rgba(255,63,80,0.12)",border:`1px solid ${transData.ctrl==="DEMAND > SUPPLY"?"rgba(0,229,160,0.3)":"rgba(255,63,80,0.3)"}`,color:transData.ctrl==="DEMAND > SUPPLY"?T.green:T.red}}>{transData.ctrl}</div>
            {transData.mom&&<><Row label="MCP" val={transData.mom.toFixed(2)} col={T.purple} bold/><Row label={price>transData.mom?"Price above MCP":"Price below MCP"} val="" col={price>transData.mom?T.green:T.red}/></>}
          </Sec>

          <Sec title="S/R Pivots" col={T.dim}>
            <div style={{fontSize:9,color:T.red,fontWeight:700,marginBottom:3}}>↓ Supply highs</div>
            {transData.pivots.highs.slice(-4).reverse().map((p,i)=>(<Row key={i} label={`H${i+1}`} val={p.price.toFixed(2)} col={T.red}/>))}
            <div style={{fontSize:9,color:T.green,fontWeight:700,margin:"6px 0 3px"}}>↑ Demand lows</div>
            {transData.pivots.lows.slice(-4).reverse().map((p,i)=>(<Row key={i} label={`L${i+1}`} val={p.price.toFixed(2)} col={T.green}/>))}
          </Sec>

        </div>
        </div>
      </div>

      {/* STATUS BAR */}
      <div style={{height:24,background:T.panel,borderTop:`1px solid ${T.border}`,display:"flex",alignItems:"center",padding:"0 14px",gap:14,fontSize:10,color:"#c8dae8",flexShrink:0}}>
        <span>ATR <span style={{color:T.yellow,fontWeight:700}}>{atr.toFixed(2)}</span></span>
        <span>WT3 <span style={{color:T.accent,fontWeight:700}}>{atrL.wt3.toFixed(2)}</span></span>
        <span>SO <span style={{color:T.red,fontWeight:700}}>{atrL.so.toFixed(2)}</span></span>
        {transData.mom&&<span>MCP <span style={{color:T.purple,fontWeight:700}}>{transData.mom.toFixed(2)}</span></span>}
        {pcgData&&pcgData.st>0&&<span style={{color:T.gold}}>{phase}</span>}
        {frontComplete&&<span style={{color:T.lime,fontWeight:800}}>🧲 MAGNET</span>}
        {cycleStore.cycles.length>0&&<span style={{color:T.accent}}>💾 {cycleStore.cycles.length} cycles opgeslagen</span>}
        {showPandasZones&&pandasZones&&<span style={{color:"#c084fc"}}>📊 Zones {(pandasZones.supply.length+pandasZones.demand.length)}×</span>}
        {showPandasZones&&zonesError&&<span style={{color:T.red,fontSize:9}}>⚠ Zones offline</span>}
        <span style={{color:T.dim}}>{bars.length} bars · {tf} · {feedSrc==="hl"?"@SP500":"synthetic"} · "live ✓"</span>
        <span style={{marginLeft:"auto",color:T.dim}}>{clock}</span>
        <span style={{color:transData.ctrl==="DEMAND > SUPPLY"?T.green:T.red,fontWeight:700}}>{transData.ctrl}</span>
      </div>
      <LiveSessionPanel cycleStore={cycleStore} />
    </div>
  );
}

// ── ROOT APP ───────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props){super(props);this.state={hasError:false,error:null};}
  static getDerivedStateFromError(e){return{hasError:true,error:e};}
  componentDidCatch(e,info){console.error("App crash:",e,info);}
  render(){
    if(this.state.hasError)return(<div style={{background:"#0a0f14",color:"#ff3f50",padding:40,fontFamily:"monospace",minHeight:"100vh"}}><h2>⚠ App Error</h2><pre style={{color:"#ffd060",fontSize:12,whiteSpace:"pre-wrap"}}>{this.state.error?.toString()}</pre><button onClick={()=>window.location.reload()} style={{marginTop:20,padding:"8px 16px",background:"transparent",border:"1px solid #ff3f50",color:"#ff3f50",cursor:"pointer",fontFamily:"monospace"}}>Reload</button></div>);
    return this.props.children;
  }
}

export default function App() {
  // Altijd eerst de landingspagina, ook als je al ingelogd bent. Alleen bij
  // terugkomst van Stripe (?checkout=...) of Discord (?discord=...) gaan we
  // direct door naar het platform.
  const [view, setView] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return q.has("checkout") || q.has("discord") ? "platform" : "landing";
  });   // landing → login → platform
  const auth = useAuth();
  const user = auth.user;

  // Na een geslaagde betaling/Discord-koppeling de ?checkout=/?discord= uit
  // de adresbalk halen, zodat een latere reload niet weer "Processing" toont.
  useEffect(() => {
    if (auth.accessStatus !== "approved") return;
    const q = new URLSearchParams(window.location.search);
    if (q.has("checkout") || q.has("discord")) {
      window.history.replaceState(null, "", window.location.pathname + window.location.hash);
    }
  }, [auth.accessStatus]);

  if (view === "landing") {
    return <Landing onEnter={() => setView(user ? "platform" : "login")} />;
  }
  if (!user) {
    return <AuthPage auth={auth} />;
  }

  // Betaald lidmaatschap: zonder actieve membership → AccessGate met
  // "Become a Pro member" (Stripe Checkout). Coach/gratis toegang → server
  // geeft altijd "approved".
  if (user.role !== "coach" && auth.accessStatus !== "approved") {
    return <AccessGate auth={auth} onHome={() => setView("landing")} />;
  }

  // De oude Hyperliquid/D3 dashboard (Platform) is vervangen door de
  // Coach Terminal. Platform blijft in dit bestand staan (niet verwijderd,
  // alleen onbereikbaar) voor het geval we iets daaruit later nog nodig
  // hebben.
  return (
    <ErrorBoundary>
      <CoachTerminal
        user={user}
        onLogout={() => { auth.logout(); setView("landing"); }}
        accountMenu={<AccountMenu auth={auth} />}
      />
    </ErrorBoundary>
  );
}
